import React, { useState, useEffect, useRef, useCallback, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import ProfileMenu from './ProfileMenu';
import EmojiPicker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import NotificationPanel from './NotificationPanel';

const ChatInput = memo(forwardRef(({ onSend, sending }, ref) => {
  const [text, setText] = useState('');
  useImperativeHandle(ref, () => ({
    clear: () => setText(''),
    getValue: () => text,
    append: (s) => setText(p => p + s),
  }));
  return (
    <div className="chat-input-pill">
      <input className="ci-input" placeholder="Escribe un mensaje..." value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (text.trim()) { onSend(text); setText(''); } } }} />
      <button className="ci-send" disabled={sending} onClick={() => { if (text.trim()) { onSend(text); setText(''); } }}>
        {sending ? <div className="spinner" style={{ width: 14, height: 14, borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>}
      </button>
    </div>
  );
}));

const NAV_ITEMS = [
  { id: 'Dashboard', label: 'Dashboard', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="14" y="3" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="3" y="14" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="14" y="14" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/></svg> },
  { id: 'Conversaciones', label: 'Conversaciones', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/></svg> },
  { id: 'Pedidos', label: 'Pedidos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/><line x1="3" y1="6" x2="21" y2="6" stroke={a?'#16a34a':'#aaa'} strokeWidth="1.6"/><path d="M16 10a4 4 0 0 1-8 0" stroke={a?'#16a34a':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Contactos', label: 'Contactos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Flujos', label: 'Flujos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="15" y="9" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="3" y="16" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><path d="M9 5.5h3a3 3 0 0 1 3 3v3" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/><path d="M9 18.5h3a3 3 0 0 0 3-3v-3" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Marketing', label: 'Marketing', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
];

const WaIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#22c55e"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;
const IgIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig)" /><circle cx="12" cy="12" r="4.5" stroke="#fff" strokeWidth="1.5" fill="none"/><circle cx="17.5" cy="6.5" r="1" fill="#fff"/><defs><linearGradient id="ig" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse"><stop stopColor="#f9ce34"/><stop offset="0.3" stopColor="#ee2a7b"/><stop offset="1" stopColor="#6228d7"/></linearGradient></defs></svg>;
const FbIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#1877f2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;

const DEPTO_POR_CIUDAD = {
  'bogotá':'Cundinamarca','bogota':'Cundinamarca','medellín':'Antioquia','medellin':'Antioquia',
  'cali':'Valle del Cauca','barranquilla':'Atlántico','cúcuta':'Norte de Santander','cucuta':'Norte de Santander',
  'bucaramanga':'Santander','pereira':'Risaralda','santa marta':'Magdalena','cartagena':'Bolívar',
  'ibagué':'Tolima','ibague':'Tolima','pasto':'Nariño','manizales':'Caldas','neiva':'Huila',
  'villavicencio':'Meta','armenia':'Quindío','valledupar':'Cesar','montería':'Córdoba','monteria':'Córdoba',
  'sincelejo':'Sucre','popayán':'Cauca','popayan':'Cauca','tunja':'Boyacá','florencia':'Caquetá',
  'riohacha':'La Guajira','quibdó':'Chocó','quibdo':'Chocó','mocoa':'Putumayo','yopal':'Casanare',
  'arauca':'Arauca','leticia':'Amazonas','soacha':'Cundinamarca','palmira':'Valle del Cauca',
  'bello':'Antioquia','envigado':'Antioquia','itagüí':'Antioquia','itagui':'Antioquia',
  'floridablanca':'Santander','girón':'Santander','giron':'Santander','dosquebradas':'Risaralda',
  'cartago':'Valle del Cauca','buenaventura':'Valle del Cauca','barrancabermeja':'Santander',
  'duitama':'Boyacá','sogamoso':'Boyacá','ocaña':'Norte de Santander','pamplona':'Norte de Santander',
  'maicao':'La Guajira','uribia':'La Guajira','manaure':'La Guajira','fonseca':'La Guajira',
  'san juan del cesar':'La Guajira','villanueva':'La Guajira','albania':'La Guajira',
  'soledad':'Atlántico','sabanalarga':'Atlántico','malambo':'Atlántico',
  'zipaquirá':'Cundinamarca','zipaquira':'Cundinamarca','fusagasugá':'Cundinamarca',
  'fusagasuga':'Cundinamarca','facatativá':'Cundinamarca','facatativa':'Cundinamarca',
  'chía':'Cundinamarca','chia':'Cundinamarca','cajicá':'Cundinamarca','cajica':'Cundinamarca',
  'madrid':'Cundinamarca','funza':'Cundinamarca','mosquera':'Cundinamarca','girardot':'Cundinamarca',
  'sabaneta':'Antioquia','rionegro':'Antioquia','apartadó':'Antioquia','turbo':'Antioquia',
  'tulua':'Valle del Cauca','tuluá':'Valle del Cauca','buga':'Valle del Cauca',
  'piedecuesta':'Santander','lorica':'Córdoba','cereté':'Córdoba','sahagún':'Córdoba',
  'la virginia':'Risaralda','chiquinquirá':'Boyacá',
};
const inferDepto = (city) => city ? (DEPTO_POR_CIUDAD[(city||'').toLowerCase().trim()] || '') : '';

const initials = (name) => {
  if (!name) return '#';
  const clean = name.trim();
  if (!clean) return '#';
  // Si es solo dígitos/símbolos de teléfono, no mostrar números
  if (/^\+?[\d\s()-]+$/.test(clean)) return '?';
  return clean.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
};
const fmtPhone = (phone) => {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 12) return `+${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`;
  if (d.length === 11) return `+${d.slice(0,1)} ${d.slice(1,4)} ${d.slice(4,7)} ${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
  return phone;
};
const AVATAR_COLORS = ['#6ee7b7','#93c5fd','#fca5a5','#fcd34d','#c4b5fd','#86efac','#fdba74','#67e8f9'];
const CARRIERS_MAP = {
  coordinadora:    { label:'Coordinadora',     color:'#e30613', img:'/coordinadora-logo.webp' },
  servientrega:    { label:'Servientrega',     color:'#00529b', img:'/servientrega-logo.jpg' },
  interrapidisimo: { label:'Inter Rapidísimo', color:'#00a651', img:'/interrapidisimo-logo.jpg' },
  tcc:             { label:'TCC',              color:'#e4002b', img:'/tcc-logo.jpg' },
  envia:           { label:'Envía',            color:'#ff6600', img:'/envia-logo.jpg' },
};
const PAYMENT_MAP = {
  cod:            { label:'Contra entrega', img:null },
  cash_on_delivery: { label:'Contra entrega', img:null },
  addi:           { label:'Addi',           img:'/payment-logos/addi.png' },
  sistecredito:   { label:'Sistecrédito',   img:'/payment-logos/sistecredito.png' },
};

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    const s = String(ts).replace(' ', 'T');
    // DB stores Colombia time (UTC-5) as naive timestamp — append -05:00, not Z
    const d = new Date(s.includes('+') || s.endsWith('Z') ? s : s + '-05:00');
    return d.toLocaleTimeString('es-CO', {hour:'2-digit', minute:'2-digit', timeZone:'America/Bogota'});
  } catch(e) { return ''; }
};

const fmtCOP = (v) => v > 0 ? new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(v) : '—';

const API = 'https://talku.online';
const authH = (extra = {}) => ({ 'Authorization': `Bearer ${localStorage.getItem('talku_token') || ''}`, ...extra });
const AGENTES = ['Daniel','Tatiana Rojas','Paola Escobar','Oficina Trend','Soporte'];


const TemplateModal = memo(function TemplateModal({ tpl, onSend, onClose, sending, err }) {
  const [vars, setVars] = useState(() => {
    const bodyComp = tpl.components?.find(c=>c.type==='BODY');
    const matches = bodyComp ? [...new Set((bodyComp.text||'').match(/\{\{(\d+)\}\}/g)||[])].sort() : [];
    return matches.map(()=>'');
  });
  const [headerUrl, setHeaderUrl] = useState('');
  const hasImageHeader = !!(tpl.components?.find(c=>c.type==='HEADER' && c.format==='IMAGE'));
  const allFilled = vars.every(v=>v.trim()) && (!hasImageHeader || headerUrl.trim());
  const bodyText = (tpl.components?.find(c=>c.type==='BODY')?.text||'');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:440}} onClick={e=>e.stopPropagation()}>
        <div className="modal-hdr">
          <div className="modal-title">Completar variables</div>
          <div className="modal-close" onClick={onClose}>{"×"}</div>
        </div>
        <div style={{padding:'4px 0 14px',fontSize:12,color:'#888',fontStyle:'italic',flexShrink:0}}>
          Plantilla: <strong style={{color:'#0a0a0a',fontFamily:'monospace'}}>{tpl.name}</strong>
        </div>
        <div style={{background:'#f8f8f6',borderRadius:10,padding:'12px 14px',fontSize:13,lineHeight:1.6,color:'#444',marginBottom:14,flexShrink:0,borderLeft:'3px solid #e5e5e3'}}>
          {bodyText.replace(/\{\{(\d+)\}\}/g,(m,n)=>{
            const v = vars[parseInt(n)-1];
            return v ? `[${v}]` : m;
          })}
        </div>
        {hasImageHeader && (
          <div style={{marginBottom:12,flexShrink:0}}>
            <div style={{fontSize:12,color:'#888',marginBottom:6,fontWeight:500}}>Imagen del encabezado</div>
            <input
              style={{width:'100%',height:36,padding:'0 10px',border:'1.5px solid #e5e5e3',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
              placeholder="URL de la imagen (https://...)"
              value={headerUrl}
              onChange={e=>setHeaderUrl(e.target.value)}
            />
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:10,flexShrink:0}}>
          {vars.map((v,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:12,color:'#888',whiteSpace:'nowrap',minWidth:50,fontFamily:'monospace'}}>{'{{'+(i+1)+'}}'}</span>
              <input
                style={{flex:1,height:36,padding:'0 10px',border:'1.5px solid #e5e5e3',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit'}}
                placeholder={i===0?'Ej. Juan Pérez':i===1?'Ej. 98765':'Valor...'}
                value={v}
                onChange={e=>{ const a=[...vars]; a[i]=e.target.value; setVars(a); }}
              />
            </div>
          ))}
        </div>
        {err && <div style={{marginTop:12,fontSize:12,color:'#ef4444',padding:'8px 12px',background:'#fef2f2',borderRadius:8,flexShrink:0}}>{err}</div>}
        <div style={{display:'flex',gap:10,marginTop:18,flexShrink:0}}>
          <button onClick={onClose} style={{flex:1,height:38,borderRadius:9,border:'1.5px solid #e5e5e3',background:'#fff',color:'#555',fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Cancelar</button>
          <button
            disabled={sending || !allFilled}
            onClick={()=>onSend(tpl, vars, headerUrl)}
            style={{flex:2,height:38,borderRadius:9,border:'none',background:allFilled?'#22c55e':'#ccc',color:'#fff',fontSize:13,fontWeight:700,cursor:allFilled?'pointer':'default',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            {sending ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/>Enviando...</> : '📤 Enviar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
});

const FLOWS_ACTIVOS = [
  { id:1,  tipo:'webhook', nombre:'TCF CONFIRMACION',          tienda:'Tiendas Colombia', ejecuciones:1842, estado:'activo',   mensaje:'Hola {nombre}, tu pedido ha sido confirmado. En breve recibirás más detalles. 📦' },
  { id:3,  tipo:'http',    nombre:'Novedad CM: NO SE LOCALIZÓ', tienda:'Tiendas Colombia', ejecuciones:324,  estado:'activo',   mensaje:'Hola {nombre}, tuvimos un inconveniente localizando tu dirección. ¿Puedes confirmarnos tu dirección completa?' },
  { id:4,  tipo:'http',    nombre:'Novedad CM: NO SE LOCALIZÓ 2',tienda:'Coordinadora',   ejecuciones:210,  estado:'activo',   mensaje:'Hola {nombre}, tuvimos un inconveniente localizando tu dirección. ¿Puedes confirmarnos tu dirección completa con barrio y ciudad?' },
  { id:5,  tipo:'http',    nombre:'Novedad CM: REPARTO',        tienda:'Coordinadora',     ejecuciones:890,  estado:'activo',   mensaje:'Hola {nombre}, tu pedido ya está en reparto. Lo recibirás hoy o mañana. 🚚' },
  { id:2,  tipo:'http',    nombre:'Novedad CM: ENTREGADO',      tienda:'Tiendas Colombia', ejecuciones:0,    estado:'inactivo', mensaje:'Hola {nombre}, tu pedido ha sido entregado. ¡Esperamos que estés feliz con tu compra! 😊' },
  { id:8,  tipo:'instagram',nombre:'Instagram: Tiendas Col...',  tienda:'Tiendas Colombia', ejecuciones:312,  estado:'activo',   mensaje:'¡Hola! Gracias por contactarnos. ¿En qué podemos ayudarte hoy?' },
  { id:10, tipo:'messenger',nombre:'Messenger: Tiendas C...',   tienda:'Tiendas Colombia', ejecuciones:428,  estado:'activo',   mensaje:'Hola, gracias por escribirnos. Un agente te atenderá en breve.' },
  { id:11, tipo:'webhook', nombre:'Preliminares T-CO 2',        tienda:'Tiendas Colombia', ejecuciones:654,  estado:'activo',   mensaje:'Hola {nombre}, notamos que iniciaste un pedido. ¿Te ayudamos a completarlo?' },
  { id:6,  tipo:'whatsapp',nombre:'Pedido preliminar COD',      tienda:'Tiendas Colombia', ejecuciones:0,    estado:'inactivo', mensaje:'Hola {nombre}, vimos que intentaste hacer un pedido. ¿Te ayudamos a completarlo? Es fácil y rápido 🛍️' },
  { id:12, tipo:'webhook', nombre:'Carrito abandonado',         tienda:'Tiendas Colombia', ejecuciones:0,    estado:'inactivo', mensaje:'Hola {nombre}, dejaste productos en tu carrito. ¿Quieres que te ayudemos a completar tu pedido?' },
];

const FLOW_TIPO_COLOR = { webhook:'#8b5cf6', http:'#3b82f6', whatsapp:'#22c55e', instagram:'#ee2a7b', messenger:'#1877f2', shopify:'#5a8a00' };

const PLANTILLAS_INTERNAS = [
  { id:'p1', nombre:'Seguimiento de pedido', categoria:'servicio', texto:'Hola {nombre}, te informamos que tu pedido #{orden} está {estado}. Para más información escríbenos.' },
  { id:'p2', nombre:'Solicitud de dirección', categoria:'logística', texto:'Hola {nombre}, necesitamos confirmar tu dirección de entrega. ¿Podrías enviárnosla completa con barrio y ciudad?' },
  { id:'p3', nombre:'Cambio de talla', categoria:'garantías', texto:'Hola {nombre}, para procesar tu cambio de talla necesitamos que nos indiques: 1️⃣ Talla actual recibida 2️⃣ Talla que deseas 3️⃣ Foto del producto' },
  { id:'p4', nombre:'Pedido entregado', categoria:'post-venta', texto:'Hola {nombre}, ¿recibiste tu pedido? Esperamos que estés feliz con tu compra. 😊 Si tienes alguna novedad, con gusto te ayudamos.' },
  { id:'p5', nombre:'Producto agotado alternativa', categoria:'ventas', texto:'Hola {nombre}, lamentamos informarte que el producto que buscas está siendo reabastecido. Te ofrecemos estas alternativas: [agregar productos]' },
  { id:'p6', nombre:'Confirmación devolución', categoria:'garantías', texto:'Hola {nombre}, recibimos tu solicitud de devolución. En 24-48 horas hábiles procesaremos tu caso. Gracias por tu paciencia.' },
];

const NOVEDAD_COLORS = {
  'En reparto': '#3b82f6', 'Entregado': '#16a34a', 'En tránsito': '#f59e0b',
  'Llegó a ciudad destino': '#16a34a', 'Dirección incorrecta': '#ef4444',
};

let _activeAudio = null;

const AudioPlayer = memo(function AudioPlayer({ src, isOut, time, onError }) {
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const bars = useMemo(() => {
    let seed = 0;
    for (let i = 0; i < src.length; i++) seed = ((seed << 5) - seed + src.charCodeAt(i)) | 0;
    return Array.from({ length: 32 }, (_, i) => {
      seed = (seed * 16807 + 7) & 0x7fffffff;
      return 0.15 + (seed % 1000) / 1000 * 0.85;
    });
  }, [src]);

  const fmtDur = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); }
    else {
      if (_activeAudio && _activeAudio !== a) {
        _activeAudio.pause();
        _activeAudio.currentTime = 0;
        _activeAudio.dispatchEvent(new Event('ended'));
      }
      _activeAudio = a;
      a.play().catch(() => {});
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    const w = waveRef.current;
    if (!a || !w || !duration) return;
    const rect = w.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    a.currentTime = (x / rect.width) * duration;
    setCurrentTime(a.currentTime);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onMeta = () => setDuration(a.duration);
    const onTime = () => setCurrentTime(a.currentTime);
    const onEnd = () => { setPlaying(false); setCurrentTime(0); _activeAudio = null; };
    const onErr = () => { setPlaying(false); if (onError) onError(); };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onErr);
      if (_activeAudio === a) _activeAudio = null;
    };
  }, [onError]);

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className={`audio-player ${isOut ? 'out' : 'in'}`}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button className="ap-play" onClick={toggle} aria-label={playing ? 'Pausar' : 'Reproducir'}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>
      <div className="ap-body">
        <div className="ap-wave" ref={waveRef} onClick={seek}>
          {bars.map((h, i) => (
            <div key={i} className="ap-bar" style={{
              height: `${h * 100}%`,
              background: i / bars.length <= progress
                ? (isOut ? '#075e54' : '#25d366')
                : (isOut ? 'rgba(7,94,84,0.3)' : 'rgba(0,0,0,0.18)')
            }} />
          ))}
        </div>
        <div className="ap-info">
          <span className="ap-dur">{fmtDur(playing || currentTime > 0 ? currentTime : duration)}</span>
          {time && <span className="ap-time">{time}</span>}
        </div>
      </div>
    </div>
  );
});

const MediaExpired = ({ type }) => (
  <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'rgba(255,255,255,0.08)',borderRadius:8,fontSize:12,color:'#aaa'}}>
    <span>{type==='audio'?'🎵':type==='video'?'🎬':type==='document'?'📄':'🖼️'}</span>
    <span>{type==='audio'?'[Audio]':type==='video'?'[Video]':type==='document'?'[Documento]':'[Imagen]'} — no disponible</span>
  </div>
);

export default function Conversations({ user, onNav, onLogout, initialPhone }) {
  useEffect(() => { document.title = 'Talku — Conversaciones'; }, []);
  const uKey = (key) => `${key}_${user?.id || 'default'}`;
  const [activeNav, setActiveNav] = useState('Conversaciones');
  const [showNotifs, setShowNotifs] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const chatInputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMoreMsgs, setHasMoreMsgs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [activeTab, setActiveTab] = useState('cliente');
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showCallMenu, setShowCallMenu] = useState(false);
  const [convStatuses, setConvStatuses] = useState({});
  const [shopifyContact, setShopifyContact] = useState(null);
  const [shopifyNames, setShopifyNames] = useState({});
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFlows, setShowFlows] = useState(false);
  const [flowStep, setFlowStep] = useState('list'); // 'list' | 'preview'
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [flowSending, setFlowSending] = useState(false);
  const [flowResult, setFlowResult] = useState(null); // null | 'ok' | 'error'
  const [flowSearch, setFlowSearch] = useState('');
  const [flowFilterEstado, setFlowFilterEstado] = useState('activo');
  const [showOrderSelect, setShowOrderSelect] = useState(false);
  const [showOrderActions, setShowOrderActions] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showCallIA, setShowCallIA] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTab, setTemplateTab] = useState('meta');
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [shopifyNoteInput, setShopifyNoteInput] = useState('');
  const [shopifyTagInput, setShopifyTagInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [assignedAgent, setAssignedAgent] = useState({});
  const [botStatus, setBotStatus] = useState({});
  const [imgModal, setImgModal] = useState(null);
  const [mediaErrors, setMediaErrors] = useState({});
  const [pendingMedia, setPendingMedia] = useState(null);
  const [sendError, setSendError] = useState('');
  const [showNexusModal, setShowNexusModal] = useState(false);
  const [nexusOrderInfo, setNexusOrderInfo] = useState(null);
  const [nexusForm, setNexusForm] = useState({});
  const [nexusLoading, setNexusLoading] = useState(false);
  const [nexusMsg, setNexusMsg] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [citySearching, setCitySearching] = useState(false);
  const cityTimeoutRef = useRef(null);
  const [shippingQuotes, setShippingQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState('coordinadora');
  const [nexusProductImg, setNexusProductImg] = useState(null);
  const [nexusProductTitle, setNexusProductTitle] = useState('');
  const [shipCarrier, setShipCarrier] = useState('coordinadora');
  const [shipGuia, setShipGuia] = useState('');
  const [shipLoading, setShipLoading] = useState(false);
  const [shipMsg, setShipMsg] = useState('');
  const [orderImages, setOrderImages] = useState({});
  const [trackingTimeline, setTrackingTimeline] = useState(null);
  const [trackingTimelineLoading, setTrackingTimelineLoading] = useState(false);
  const [trackingTimelineOpen, setTrackingTimelineOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [exitingNotifs, setExitingNotifs] = useState(new Set());

  // Reply con cita
  const [replyTo, setReplyTo] = useState(null); // { id, content, sender, wamid }

  // Emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Búsqueda global de mensajes
  const [globalResults, setGlobalResults] = useState([]); // [{ conv_id, conv_name, conv_phone, message_id, content, sent_at, role }]
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const globalSearchTimerRef = useRef(null);
  const [storeDomain, setStoreDomain] = useState('');
  useEffect(() => { fetch(`${API}/stores/`, { headers: authH() }).then(r=>r.json()).then(d=>{ if (Array.isArray(d) && d.length) setStoreDomain(d[0].domain); }).catch(()=>{}); }, []); // eslint-disable-line

  // Búsqueda interna del chat (Ctrl+F)
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [chatSearchIdx, setChatSearchIdx] = useState(0);
  const chatSearchRef = useRef(null);
  const msgRefs = useRef({});

  // Panel izquierdo
  const [listTab, setListTab] = useState('todos'); // 'todos' | 'abiertos' | 'asignados' | 'resueltos'
  const [favConvs, setFavConvs] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(uKey('talku_favs')) || '[]')); } catch { return new Set(); } });
  const [convLabels, setConvLabels] = useState(() => { try { return JSON.parse(localStorage.getItem(uKey('talku_labels')) || '{}'); } catch { return {}; } });
  const [labelFilter, setLabelFilter] = useState(null); // null | 'pedido' | 'novedad' | 'garantia'
  const [showLabelFilter, setShowLabelFilter] = useState(false);
  const [orderFilter, setOrderFilter] = useState(null);
  const [showOrderFilter, setShowOrderFilter] = useState(false);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [showSourceFilter, setShowSourceFilter] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, convId }

  const toggleFav = (convId) => {
    setFavConvs(prev => {
      const next = new Set(prev);
      next.has(convId) ? next.delete(convId) : next.add(convId);
      localStorage.setItem(uKey('talku_favs'), JSON.stringify([...next]));
      return next;
    });
  };

  const patchConv = async (convId, updates) => {
    try {
      await fetch(`${API}/whatsapp/conversations/${convId}`, {
        method: 'PATCH',
        headers: authH({'Content-Type':'application/json'}),
        body: JSON.stringify(updates)
      });
    } catch {}
  };

  const setLabel = (convId, label) => {
    setConvLabels(prev => {
      const next = { ...prev, [convId]: label };
      localStorage.setItem(uKey('talku_labels'), JSON.stringify(next));
      return next;
    });
    patchConv(convId, { label: label || null });
    setContextMenu(null);
  };

  const toggleBot = async (convId) => {
    const current = botStatus[convId] !== false;
    setBotStatus(prev => ({ ...prev, [convId]: !current }));
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${convId}/toggle-bot`, { method: 'POST', headers: authH() });
      const data = await res.json();
      if (data.ok) setBotStatus(prev => ({ ...prev, [convId]: data.bot_enabled }));
    } catch {}
  };

  // Nueva conversación modal
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvTab, setNewConvTab] = useState('nuevo'); // 'nuevo' | 'contacto'
  const [newConvPhone, setNewConvPhone] = useState('');
  const [newConvName, setNewConvName] = useState('');
  const [newConvContactSearch, setNewConvContactSearch] = useState('');
  const [newConvSelectedContact, setNewConvSelectedContact] = useState(null);
  const [newConvTemplates, setNewConvTemplates] = useState([]);
  const [newConvTemplateSearch, setNewConvTemplateSearch] = useState('');
  const [newConvSelectedTemplate, setNewConvSelectedTemplate] = useState(null);
  const [newConvVariables, setNewConvVariables] = useState([]);
  const [newConvSending, setNewConvSending] = useState(false);
  const [newConvError, setNewConvError] = useState('');

  // Panel de plantilla Meta para ventana 24h
  const [metaTplToSend, setMetaTplToSend] = useState(null);   // plantilla seleccionada para enviar
  const [metaTplVars, setMetaTplVars]     = useState([]);      // valores de variables {{1}},{{2}}...
  const [metaTplSending, setMetaTplSending] = useState(false);
  const [metaTplErr, setMetaTplErr]       = useState('');
  const [metaTplHeaderUrl, setMetaTplHeaderUrl] = useState('');

  const messagesAreaRef = useRef(null);
  const hasLoadedMoreRef = useRef(false);
  const pollRef = useRef(null);
  const optionsRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingCancelledRef = useRef(false);
  const activeConvRef = useRef(null);
  const activePhoneRef = useRef(null);
  const conversationsRef = useRef([]);
  const prevConvsRef = useRef({});
  const initialPhoneAppliedRef = useRef(null);
  const notifTimersRef = useRef({});
  const swRegRef = useRef(null);

  const STATUS_OPTIONS = [
    { id:'open', label:'Abierto', color:'#16a34a', bg:'#f0fdf4' },
    { id:'waiting', label:'En espera', color:'#d97706', bg:'#fffbeb' },
    { id:'urgent', label:'Urgente', color:'#ef4444', bg:'#fef2f2' },
    { id:'resolved', label:'Resuelto', color:'#888', bg:'#f4f4f2' },
  ];

  const OPTIONS_MENU = [
    { id:'assign', label:'Asignar conversación', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg> },
    { id:'history', label:'Ver historial completo', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/></svg> },
    { id:'archive', label:'Archivar', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg> },
    { id:'spam', label:'Marcar como spam', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#ef4444"/></svg>, danger: true },
  ];

  // ── Detecta si la conversación activa es WA fuera de ventana 24h ──
  const isWa24hExpired = useMemo(() => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv) return false;
    const ch = (conv.channel || '').toLowerCase();
    if (['instagram','ig','messenger','fb','tiktok'].includes(ch)) return false;
    if (messages.length === 0) return false; // todavía cargando
    const lastIn = [...messages].reverse().find(m => m.role === 'user');
    if (!lastIn) return true; // cliente nunca respondió = siempre fuera de ventana
    const s = String(lastIn.sent_at || '').replace(' ','T');
    const d = new Date(s.includes('+')||s.endsWith('Z') ? s : s+'-05:00');
    return (Date.now() - d.getTime()) > 24 * 60 * 60 * 1000;
  }, [activeConv, messages, conversations]);

  const sendMetaTemplate = async (tpl, vars, headerUrl) => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv || !tpl) return;
    setMetaTplSending(true); setMetaTplErr('');
    try {
      const res = await fetch(`${API}/whatsapp/send-template`, {
        method: 'POST', headers: authH({'Content-Type':'application/json'}),
        body: JSON.stringify({
          phone: conv.contact_phone,
          contact_name: conv.contact_name || '',
          template_name: tpl.name,
          language_code: tpl.language || 'es_CO',
          variables: vars || [],
          header_image_url: headerUrl || metaTplHeaderUrl || '',
        }),
      });
      const data = await res.json();
      if (data.ok) {
        const bodyText = tpl.components?.find(c=>c.type==='BODY')?.text || `[Plantilla: ${tpl.name}]`;
        const preview = (vars||[]).reduce((t,v,i)=>t.replace(`{{${i+1}}}`,v||`{{${i+1}}}`), bodyText);
        setMessages(prev => [...prev, { id:'temp_'+Date.now(), role:'outbound', content:preview, sent_at:new Date().toISOString(), status:'sent', source:'agent' }]);
        setShowTemplates(false); setMetaTplToSend(null); setMetaTplVars([]); setMetaTplErr(''); setMetaTplHeaderUrl('');
      } else {
        setMetaTplErr(data.error?.error_user_msg || data.error?.message || JSON.stringify(data.error||data).slice(0,120));
      }
    } catch { setMetaTplErr('Error de conexión'); }
    setMetaTplSending(false);
  };

  const handleMetaTplClick = (tpl) => {
    if (tpl.status !== 'APPROVED') return;
    const bodyComp = tpl.components?.find(c=>c.type==='BODY');
    const headerComp = tpl.components?.find(c=>c.type==='HEADER');
    const vars = bodyComp ? [...new Set((bodyComp.text||'').match(/\{\{(\d+)\}\}/g)||[])].sort() : [];
    const hasImageHeader = headerComp?.format === 'IMAGE';
    if (vars.length > 0 || hasImageHeader) {
      setMetaTplToSend(tpl);
      setMetaTplVars(vars.map(()=>''));
      setMetaTplHeaderUrl('');
      setMetaTplErr('');
    } else {
      sendMetaTemplate(tpl, []);
    }
  };

  const handleNav = (id) => { setActiveNav(id); if (onNav) onNav(id); };

  useEffect(() => {
    const close = () => { setContextMenu(null); setShowLabelFilter(false); setShowEmojiPicker(false); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Ctrl+F abre búsqueda interna
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowChatSearch(v => { if (!v) setChatSearchTerm(''); return !v; });
        setTimeout(() => chatSearchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') { setShowChatSearch(false); setChatSearchTerm(''); setShowEmojiPicker(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Derived: matching messages for search
  const searchMatches = chatSearchTerm.length >= 2
    ? messages.filter(m => (m.content || '').toLowerCase().includes(chatSearchTerm.toLowerCase()))
    : [];

  const scrollToMatch = useCallback((idx) => {
    const m = searchMatches[idx];
    if (!m) return;
    const el = msgRefs.current[m.id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchMatches]);

  useEffect(() => {
    setChatSearchIdx(0);
    if (searchMatches.length > 0) scrollToMatch(0);
  }, [chatSearchTerm, searchMatches.length]);

  const goSearchPrev = () => {
    const next = (chatSearchIdx - 1 + searchMatches.length) % searchMatches.length;
    setChatSearchIdx(next);
    scrollToMatch(next);
  };
  const goSearchNext = () => {
    const next = (chatSearchIdx + 1) % searchMatches.length;
    setChatSearchIdx(next);
    scrollToMatch(next);
  };

  // Búsqueda global debounced
  useEffect(() => {
    clearTimeout(globalSearchTimerRef.current);
    if (searchQuery.length < 3) { setGlobalResults([]); return; }
    globalSearchTimerRef.current = setTimeout(async () => {
      setGlobalSearchLoading(true);
      try {
        const res = await fetch(`${API}/whatsapp/search?q=${encodeURIComponent(searchQuery)}`, { headers: authH() });
        const data = await res.json();
        setGlobalResults(data.results || []);
      } catch(e) { setGlobalResults([]); }
      setGlobalSearchLoading(false);
    }, 350);
    return () => clearTimeout(globalSearchTimerRef.current);
  }, [searchQuery]);

  const highlightText = (text, term) => {
    if (!term || term.length < 2 || !text) return text;
    const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} style={{background:'#fef08a',borderRadius:3,padding:'0 1px',color:'#0a0a0a'}}>{part}</mark>
        : part
    );
  };

  const openNewConvModal = async () => {
    setShowNewConv(true);
    setNewConvTab('nuevo');
    setNewConvPhone('');
    setNewConvName('');
    setNewConvContactSearch('');
    setNewConvSelectedContact(null);
    setNewConvSelectedTemplate(null);
    setNewConvVariables([]);
    setNewConvError('');
    if (newConvTemplates.length === 0) {
      try {
        const res = await fetch(`${API}/whatsapp/templates`, { headers: authH() });
        const data = await res.json();
        const approved = (data.data || []).filter(t => t.status === 'APPROVED');
        setNewConvTemplates(approved);
      } catch (e) {}
    }
  };

  const selectNewConvTemplate = (tpl) => {
    setNewConvSelectedTemplate(tpl);
    const bodyComp = (tpl.components || []).find(c => c.type === 'BODY');
    const text = bodyComp?.text || '';
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
    const maxIdx = matches.length > 0 ? Math.max(...matches.map(m => parseInt(m[1]))) : 0;
    setNewConvVariables(Array(maxIdx).fill(''));
  };

  const sendNewConversation = async () => {
    const phone = newConvTab === 'contacto'
      ? (newConvSelectedContact?.phone || '')
      : newConvPhone.trim();
    const name = newConvTab === 'contacto'
      ? (newConvSelectedContact?.name || '')
      : newConvName.trim();

    if (!phone) { setNewConvError('Ingresa un número de teléfono.'); return; }
    if (!newConvSelectedTemplate) { setNewConvError('Selecciona una plantilla.'); return; }

    setNewConvSending(true);
    setNewConvError('');
    try {
      const res = await fetch(`${API}/whatsapp/send-template`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          phone,
          contact_name: name,
          template_name: newConvSelectedTemplate.name,
          language_code: newConvSelectedTemplate.language,
          variables: newConvVariables,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setShowNewConv(false);
        await loadConversations();
        if (data.conv_id) setActiveConv(data.conv_id);
      } else {
        setNewConvError(data.error ? JSON.stringify(data.error).slice(0, 120) : 'Error al enviar.');
      }
    } catch (e) {
      setNewConvError('Error de red. Intenta de nuevo.');
    }
    setNewConvSending(false);
  };
  useEffect(() => { activeConvRef.current = activeConv; setReplyTo(null); }, [activeConv]);
  useEffect(() => {
    if (!initialPhone || conversations.length === 0) return;
    // Auto-seleccionar solo una vez por cada valor de initialPhone (apertura desde otra
    // pantalla). El polling de conversations no debe re-seleccionar: una selección manual
    // posterior del usuario siempre prevalece.
    if (initialPhoneAppliedRef.current === initialPhone) return;
    initialPhoneAppliedRef.current = initialPhone;
    const digits = initialPhone.replace(/\D/g, '');
    const match = conversations.find(c => c.contact_phone && c.contact_phone.replace(/\D/g, '').endsWith(digits.slice(-10)));
    if (match) setActiveConv(match.id);
  }, [initialPhone, conversations]);


  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => { swRegRef.current = reg; }).catch(() => {});
      const handler = (e) => { if (e.data?.type === 'OPEN_CONV') setActiveConv(e.data.convId); };
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []);

  const playNotifSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[880, 0, 0.15], [1108, 0.12, 0.28]].forEach(([freq, start, end]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + end);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + end);
      });
    } catch(e) {}
  };

  const SOCIAL_CHANNELS = ['instagram', 'messenger', 'tiktok'];

  const loadConversations = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/conversations`, { headers: authH() });
      const data = await res.json();
      if (data.conversations) {
        const crmConvs = data.conversations.filter(c => !SOCIAL_CHANNELS.includes(c.channel || ''));
        const isFirstLoad = Object.keys(prevConvsRef.current).length === 0;
        if (!isFirstLoad) {
          crmConvs.forEach(conv => {
            const prevCount = prevConvsRef.current[conv.id] || 0;
            if ((conv.unread_count || 0) > prevCount && conv.id !== activeConvRef.current) {
              const notifId = `notif_${conv.id}`;
              const rawPhone = (conv.contact_phone || '').replace(/\D/g, '');
              const displayName = shopifyNames[rawPhone] || conv.contact_name || fmtPhone(conv.contact_phone);
              const msgPreview = conv.last_message || '';
              setNotifications(prev => {
                const filtered = prev.filter(n => n.convId !== conv.id);
                return [...filtered, { id: notifId, convId: conv.id, contactName: displayName, message: msgPreview, channel: conv.channel || 'wa', timestamp: conv.last_message_at }];
              });
              playNotifSound();
              if ('Notification' in window && Notification.permission === 'granted' && (document.hidden || !document.hasFocus())) {
                const notifOpts = { body: msgPreview || 'Nuevo mensaje de WhatsApp', icon: '/favicon-talku.png', tag: `conv-${conv.id}`, data: { convId: conv.id }, actions: [{ action: 'dismiss', title: 'Descartar' }] };
                if (swRegRef.current) {
                  swRegRef.current.showNotification(displayName, notifOpts);
                } else {
                  const osNotif = new Notification(displayName, notifOpts);
                  osNotif.onclick = () => { window.focus(); setActiveConv(conv.id); osNotif.close(); };
                }
              }
              clearTimeout(notifTimersRef.current[conv.id]);
              notifTimersRef.current[conv.id] = setTimeout(() => dismissNotification(notifId), 6000);
            }
          });
        }
        crmConvs.forEach(c => { prevConvsRef.current[c.id] = c.unread_count || 0; });
        setBotStatus(prev => {
          const next = { ...prev };
          crmConvs.forEach(c => { if (next[c.id] === undefined) next[c.id] = c.bot_enabled !== false; });
          return next;
        });
        setConversations(crmConvs);
        if (!activeConvRef.current && crmConvs.length > 0) setActiveConv(crmConvs[0].id);
      }
    } catch (e) {}
    setLoading(false);
  };

  const loadMessages = async (convId, silent = false) => {
    if (!convId) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${convId}/messages?limit=50`, { headers: authH() });
      const data = await res.json();
      if (data.messages) {
        if (!hasLoadedMoreRef.current) setHasMoreMsgs(!!data.has_more);
        setMessages(prev => {
          const temps = prev.filter(m => {
            if (!String(m.id).startsWith('temp_')) return false;
            if (m.media_type) return !data.messages.some(db => db.role === 'outbound' && db.media_type === m.media_type);
            return !data.messages.some(db => db.role === 'outbound' && db.content === m.content);
          });
          // Preservar mensajes anteriores cargados con "Cargar mensajes anteriores"
          let baseMsgs = data.messages;
          if (hasLoadedMoreRef.current && data.messages.length > 0) {
            const minNewId = Math.min(...data.messages.map(m => Number(m.id)));
            const olderMsgs = prev.filter(m => !String(m.id).startsWith('temp_') && Number(m.id) < minNewId);
            baseMsgs = [...olderMsgs, ...data.messages];
          }
          const prevStr = JSON.stringify(prev.filter(m => !String(m.id).startsWith('temp_')).map(m => m.id + m.status));
          const newStr = JSON.stringify(baseMsgs.map(m => m.id + m.status));
          if (prevStr === newStr && temps.length === 0) return prev;
          return [...baseMsgs, ...temps].sort((a, b) => {
            const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
            const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
            return idA - idB;
          });
        });
      }
    } catch (e) {}
    if (!silent) setLoadingMsgs(false);
  };

  const loadMoreMessages = async () => {
    if (!activeConv || loadingMore) return;
    const firstId = messages.find(m => !String(m.id).startsWith('temp_'))?.id;
    if (!firstId) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${activeConv}/messages?limit=50&before_id=${firstId}`, { headers: authH() });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        hasLoadedMoreRef.current = true;
        setHasMoreMsgs(!!data.has_more);
        const area = messagesAreaRef.current;
        const prevScrollHeight = area?.scrollHeight || 0;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
          return [...newMsgs, ...prev].sort((a, b) => {
            const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
            const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
            return idA - idB;
          });
        });
        // Mantener posición de scroll después de insertar mensajes arriba
        setTimeout(() => {
          if (area) area.scrollTop = area.scrollHeight - prevScrollHeight;
        }, 0);
      } else {
        setHasMoreMsgs(false);
      }
    } catch(e) {}
    setLoadingMore(false);
  };

  const loadShopifyData = async (phone) => {
    if (!phone) return;
    const rawPhone = phone.replace(/\D/g, '');
    try {
      const res = await fetch(`${API}/contacts/by-phone/${rawPhone}`, { headers: authH() });
      const d = await res.json();
      if (d.found) {
        setShopifyContact(d);
        if (d.nombre) setShopifyNames(prev => ({ ...prev, [rawPhone]: d.nombre }));
        setLoadingOrders(true);
        const ores = await fetch(`${API}/orders/?customer_id=${d.id}&phone=${rawPhone}&limit=10`, { headers: authH() });
        const odata = await ores.json();
        const getEstado = (o) => {
          if (o.estado && o.estado !== 'sin_despachar') return o.estado;
          if (o.estado_envio === 'fulfilled') return 'despachado';
          if (o.estado_pago === 'paid') return 'pagado';
          if (o.estado_pago === 'voided') return 'cancelado';
          return 'pendiente';
        };
        const ESTADO_LABEL = { despachado:'Despachado', pagado:'Pagado', pendiente:'Pendiente', cancelado:'Cancelado', entregado:'Entregado', preparando:'Preparando', sin_despachar:'Sin despachar' };
        const orders = (odata.orders || []).slice(0, 8).map(o => ({
          shopify_id: o.shopify_id, id: o.id,
          estado: getEstado(o),
          estado_label: ESTADO_LABEL[getEstado(o)] || getEstado(o),
          estado_pago: o.estado_pago, estado_envio: o.estado_envio,
          fulfillment_status: o.estado_envio, financial_status: o.estado_pago,
          product: o.producto || 'Producto', variant: o.variante || '',
          total: o.valor || 0, metodo: o.metodo || 'COD',
          items_detalle: o.items_detalle || [],
          guia: o.guia_nexus || null,
          guia_nexus: o.guia_nexus || null,
          transportadora: o.transportadora || null,
          tienda: o.tienda || '',
          tags: o.tags || '', note: o.notas || '',
        }));
        setShopifyOrders(orders);
        setLoadingOrders(false);
        const activeO = orders.find(o => o.estado !== 'entregado' && o.estado !== 'cancelado') || orders[0];
        if (activeO?.guia_nexus) {
          setNexusOrderInfo({ has_nexus: true, tracking_number: activeO.guia_nexus, carrier: activeO.transportadora, shopify_order_id: activeO.shopify_id });
        } else if (activeO?.shopify_id) {
          try {
            const nres = await fetch(`${API}/nexus/by-shopify-order/${activeO.shopify_id}`, { headers: authH() });
            if (nres.ok) setNexusOrderInfo({ ...(await nres.json()), shopify_order_id: activeO.shopify_id });
            else setNexusOrderInfo(null);
          } catch (e) { setNexusOrderInfo(null); }
        } else {
          setNexusOrderInfo(null);
        }
        // Auto-assign label based on Shopify order status (never overwrite manual labels)
        const MANUAL_LABELS = new Set(['pedido','novedad','garantia']);
        const existingLabel = convLabels[activeConv] || conversations.find(c => c.id === activeConv)?.label;
        if (!MANUAL_LABELS.has(existingLabel)) {
          const autoLabel = ['despachado','pendiente','pagado'].includes(activeO?.estado) ? activeO.estado : null;
          if (autoLabel !== existingLabel && activeConv) {
            setConvLabels(prev => { const next={...prev,[activeConv]:autoLabel}; localStorage.setItem(uKey('talku_labels'),JSON.stringify(next)); return next; });
            patchConv(activeConv, { label: autoLabel });
          }
        }
        const allItems = activeO?.items_detalle || [];
        const inlineImgs = {};
        const needFetch = [];
        allItems.forEach((it, i) => {
          if (it.image_url) { inlineImgs[i] = it.image_url; }
          else if (it.variant_id) { needFetch.push({ idx: i, vid: it.variant_id }); }
        });
        if (needFetch.length > 0) {
          Promise.all(needFetch.map(({ vid }) =>
            fetch(`${API}/orders/variant-image/${vid}`, { headers: authH() })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          )).then(results => {
            const imgs = { ...inlineImgs };
            results.forEach((r, i) => { if (r?.image_url) imgs[needFetch[i].idx] = r.image_url; });
            setOrderImages(imgs);
          });
        } else if (Object.keys(inlineImgs).length > 0) { setOrderImages(inlineImgs); }
        else if (allItems.length > 0 && activeO.shopify_id) {
          fetch(`${API}/nexus/orders/${activeO.shopify_id}/detail`, { headers: authH() })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
              if (!d) return;
              const m = {};
              const nxItems = d.items || d.data?.items || [];
              if (nxItems.length > 0) {
                allItems.forEach((_, i) => { const ni = nxItems[i]; const ig = ni?.product?.image_url || ni?.variant_image_url || ni?.image_url; if (ig) m[i] = ig; });
              }
              if (Object.keys(m).length === 0) {
                const ig = d.variant_image_url || d.product_image || d.data?.variant_image_url || d.data?.product_image;
                if (ig) allItems.forEach((_, i) => { m[i] = ig; });
              }
              if (Object.keys(m).length > 0) setOrderImages(m);
            }).catch(() => {});
        } else { setOrderImages({}); }
      } else {
        setShopifyContact(null);
        setShopifyOrders([]);
        setNexusOrderInfo(null);
        setOrderImages({});
        setShipGuia(''); setShipMsg('');
      }
    } catch (e) { setLoadingOrders(false); }
  };

  const loadMetaTemplates = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/templates`, { headers: authH() });
      const data = await res.json();
      setMetaTemplates(data.data || data.templates || []);
    } catch (e) {}
  };

  const dismissNotification = (id) => {
    setExitingNotifs(prev => new Set([...prev, id]));
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
      setExitingNotifs(prev => { const s = new Set([...prev]); s.delete(id); return s; });
    }, 380);
  };

  const replyNotification = (notif) => {
    setActiveConv(notif.convId);
    markRead(notif.convId);
    dismissNotification(notif.id);
  };

  const markRead = async (convId) => {
    try {
      await fetch(`${API}/whatsapp/conversations/${convId}/mark-read`, { method: 'POST', headers: authH() });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
      dismissNotification(`notif_${convId}`);
    } catch (e) {}
  };

  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, 5000);
    const msgPoll = setInterval(() => {
      if (activeConvRef.current) {
        loadMessages(activeConvRef.current, true);
        const conv = conversationsRef.current.find(c => c.id === activeConvRef.current);
        if (conv?.unread_count > 0) markRead(activeConvRef.current);
      }
    }, 2000);
    return () => { clearInterval(pollRef.current); clearInterval(msgPoll); };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (activeConv) {
      hasLoadedMoreRef.current = false;
      setMessages([]);
      loadMessages(activeConv);
      setMediaErrors({});
      activePhoneRef.current = null;
      setPendingMedia(null);
      setSendError('');
      setShopifyContact(null);
      setShopifyOrders([]);
      setNexusOrderInfo(null);
      setShipGuia(''); setShipMsg('');
    }
  }, [activeConv]);

  useEffect(() => {
    if (activeConv && conversations.length > 0) {
      const c = conversations.find(x => x.id === activeConv);
      if (c?.contact_phone && c.contact_phone !== activePhoneRef.current) {
        activePhoneRef.current = c.contact_phone;
        loadShopifyData(c.contact_phone);
      }
    }
  }, [activeConv, conversations]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    const handleClick = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) setShowOptionsMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const sendMessage = async (text) => {
    const msg = text;
    if (!msg || !msg.trim() || !activeConv) return;
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv) return;
    const currentReplyTo = replyTo;
    setReplyTo(null);
    const tempMsg = { id: 'temp_' + Date.now(), role: 'outbound', content: msg, sent_at: new Date().toISOString(), status: 'sent', source: 'agent', quoted_content: currentReplyTo?.content || null, quoted_sender: currentReplyTo?.sender || null };
    setMessages(prev => {
      const all = [...prev, tempMsg];
      return all.sort((a, b) => {
        const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
        const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
        return idA - idB;
      });
    });
    setSending(true);
    const tempId = tempMsg.id;
    try {
      await fetch(`${API}/whatsapp/send`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          phone: conv.contact_phone,
          message: msg,
          source: 'agent',
          conv_id: activeConv,
          ...(currentReplyTo ? {
            context_message_id: currentReplyTo.wamid || '',
            quoted_content: currentReplyTo.content || '',
            quoted_sender: currentReplyTo.sender || '',
          } : {})
        })
      });
      // Remover temp por ID exacto y recargar desde DB para evitar duplicado
      setMessages(prev => prev.filter(m => m.id !== tempId));
      loadMessages(activeConv, true);
    } catch (e) {}
    setSending(false);
  };

  const sendMedia = async (file) => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv || !file) return;
    const MB5 = 5 * 1024 * 1024;
    const MB100 = 100 * 1024 * 1024;
    const isImage = file.type.startsWith('image/');
    const maxSize = isImage ? MB5 : MB100;
    if (file.size > maxSize) {
      setSendError(`Archivo muy grande (${(file.size/1024/1024).toFixed(1)}MB). Máximo: ${isImage?'5':'100'}MB`);
      return;
    }
    setSending(true);
    setSendError('');
    const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document';
    const contentLabel = type === 'image' ? '[Imagen]' : type === 'video' ? '[Video]' : type === 'audio' ? '[Audio]' : `[Documento: ${file.name}]`;
    const tempId = 'temp_' + Date.now();
    const tempMedia = { id: tempId, role: 'outbound', content: contentLabel, sent_at: new Date().toISOString(), status: 'sent', source: 'agent', media_type: type, media_url: null };
    setMessages(prev => [...prev, tempMedia].sort((a, b) => {
      const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
      const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
      return idA - idB;
    }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${API}/whatsapp/upload-media`, { method: 'POST', headers: authH(), body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadData.id) {
        const errDetail = uploadData.error?.message || uploadData.message || uploadData.error_data?.details || JSON.stringify(uploadData).slice(0,120);
        setSendError(`Error al subir: ${errDetail}`);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setSending(false);
        return;
      }
      if (uploadData.local_url) {
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, media_url: uploadData.local_url } : m));
      }
      const sendRes = await fetch(`${API}/whatsapp/send-media`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ phone: conv.contact_phone, type, media_id: uploadData.id, filename: file.name, local_url: uploadData.local_url || '' })
      });
      const sendData = await sendRes.json();
      if (!sendRes.ok || sendData.error) {
        const errMsg = sendData.error?.message || sendData.detail || JSON.stringify(sendData.error || sendData).slice(0, 120);
        setSendError(`Error al enviar: ${errMsg}`);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setSending(false);
        return;
      }
      setMessages(prev => prev.filter(m => m.id !== tempId));
      loadMessages(activeConv, true);
    } catch (e) {
      setSendError(`Error: ${e.message}`);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
    setSending(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
                     : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
                     : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingCancelledRef.current = false;
      mediaRecorder.ondataavailable = e => audioChunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordingCancelledRef.current) return;
        const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const baseMime = mimeType.split(';')[0];
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const file = new File([blob], `nota_voz.${ext}`, { type: baseMime });
        await sendMedia(file);
      };
      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (e) { alert('No se pudo acceder al micrófono'); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(recordingTimerRef.current);
    setRecordingTime(0);
  };

  const openNexusModal = async (shopifyOrderId) => {
    setNexusMsg(''); setNexusLoading(true); setShowNexusModal(true);
    try {
      const res = await fetch(`${API}/nexus/by-shopify-order/${shopifyOrderId}`, { headers: authH() });
      const d = await res.json();
      setNexusOrderInfo({ ...d, shopify_order_id: shopifyOrderId });

      // Intentar cargar datos reales desde Nexus para pre-llenar el form
      let nexusDetail = null;
      if (d.has_nexus) {
        try {
          const dr = await fetch(`${API}/nexus/orders/${shopifyOrderId}/detail`, { headers: authH() });
          if (dr.ok) nexusDetail = await dr.json();
        } catch(_) {}
      }

      const conv = conversations.find(x => x.id === activeConv);
      const phone = shopifyContact?.telefono || (conv?.contact_phone || '').replace(/\D/g, '');
      const ord = shopifyOrders.find(o => o.fulfillment_status !== 'fulfilled') || shopifyOrders[0];
      const _city = nexusDetail?.city || nexusDetail?.ciudad || shopifyContact?.ciudad || '';
      const _state = nexusDetail?.state || nexusDetail?.department || nexusDetail?.departamento || shopifyContact?.provincia || inferDepto(_city);
      setNexusForm({
        customer_name:       nexusDetail?.customer_name || shopifyContact?.nombre || conv?.contact_name || '',
        customer_phone:      nexusDetail?.customer_phone || phone || '',
        shipping_address:    nexusDetail?.shipping_address || nexusDetail?.address || shopifyContact?.direccion || '',
        city:                _city,
        city_code:           '',
        state:               _state,
        content_description: nexusDetail?.content_description || ord?.variant || '',
        total_amount:        nexusDetail?.total_amount || nexusDetail?.total || String(ord?.total || '') || '',
        customer_email:      nexusDetail?.customer_email || nexusDetail?.email || shopifyContact?.email || '',
      });
      setCityQuery(_city ? `${_city}${_state ? ', ' + _state : ''}` : '');
      setCitySuggestions([]);
      const pName = nexusDetail?.product_name || ord?.product || '';
      const vName = nexusDetail?.variant_selected || ord?.variant || '';
      setNexusProductTitle(vName && pName.endsWith(vName) ? pName.slice(0, -vName.length).replace(/\s*-\s*$/, '').trim() : pName);
      const nexusImg = nexusDetail?.variant_image_url || nexusDetail?.product_image || null;
      setNexusProductImg(nexusImg);
      if (!nexusImg) {
        const variantId = ord?.items_detalle?.[0]?.variant_id;
        if (variantId) {
          try {
            const imgRes = await fetch(`${API}/orders/variant-image/${variantId}`, { headers: authH() });
            if (imgRes.ok) { const imgD = await imgRes.json(); if (imgD.image_url) setNexusProductImg(imgD.image_url); }
          } catch(_) {}
        }
      }
      setShippingQuotes([]); setSelectedCarrier('coordinadora');
      if (d.has_nexus) {
        try {
          const qr = await fetch(`${API}/nexus/orders/${shopifyOrderId}/quote`, { method: 'POST', headers: authH() });
          if (qr.ok) { const qd = await qr.json(); setShippingQuotes(qd.quotes || qd.data || []); }
        } catch(_) {}
      }
    } catch(e) {
      setNexusMsg(`Error: ${e?.message || String(e)}`);
    } finally { setNexusLoading(false); }
  };

  const nexusGenerateGuide = async () => {
    if (!nexusOrderInfo?.shopify_order_id) return;
    setNexusLoading(true); setNexusMsg('');
    try {
      // 1. Actualizar datos en Nexus antes de generar
      const updateFields = Object.fromEntries(Object.entries(nexusForm).filter(([,v]) => String(v).trim() !== '' && v !== null && v !== undefined));
      // content_description con TODOS los productos de todos los pedidos a despachar
      const _gOrders = (() => {
        const p = shopifyOrders.filter(o => o.fulfillment_status !== 'fulfilled');
        return p.length ? p : (shopifyOrders.length ? [shopifyOrders[0]] : []);
      })();
      const _validName = (s) => { const t = (s == null ? '' : String(s)); const tt = t.trim(); return (tt !== '' && tt !== '—' && tt !== '–') ? t : ''; };
      const _fullDesc = _gOrders
        .flatMap(o => (o.items_detalle && o.items_detalle.length > 0
          ? o.items_detalle
          : [{ producto: o.product, variante: o.variant, cantidad: 1 }]).map(it => ({ ...it, _oproduct: o.product })))
        .map(it => {
          const p = _validName(it.producto) || _validName(it.title) || _validName(it._oproduct) || 'Producto';
          const v = it.variante || it.variant_title || '';
          const q = it.cantidad || it.quantity || 1;
          return `${p}${v && v !== '—' ? ' - ' + v : ''}${q > 1 ? ' x' + q : ''}`;
        })
        .filter(Boolean).join(' + ');
      if (_fullDesc) updateFields.content_description = _fullDesc;
      if (Object.keys(updateFields).length > 0) {
        const patchRes = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}`, {
          method: 'PATCH',
          headers: authH({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(updateFields),
        });
        if (!patchRes.ok) {
          const patchErr = await patchRes.json().catch(() => ({}));
          setNexusMsg('❌ Error al actualizar datos en Nexus: ' + (patchErr.detail || patchRes.status) + '. Corrija los datos e intente de nuevo.');
          setNexusLoading(false);
          return;
        }
      }
      // 2. Generar guía
      const res = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/generate-guide`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ carrier: selectedCarrier }) });
      const d = await res.json();
      if (d.guide_number) {
        // El backend devuelve 200 con already_existed=true cuando Nexus responde 409
        // (guía ya activa). No es una guía nueva: se sincroniza la existente.
        const label = d.already_existed ? '✅ Guía sincronizada (ya existía)' : '✅ Guía generada';
        setNexusMsg(`${label}: ${d.guide_number}`);
        setNexusOrderInfo(prev => ({ ...prev, tracking_number: d.guide_number, carrier_status: 'shipped' }));
        const conv = conversations.find(x => x.id === activeConv);
        if (conv?.contact_phone) loadShopifyData(conv.contact_phone);
      } else {
        setNexusMsg(`❌ ${d.message || d.detail || 'Error generando guía'}`);
      }
    } catch(e) {
      setNexusMsg('❌ Error de conexión');
    } finally { setNexusLoading(false); }
  };

  const shopifyAction = async (action, orderId, extra = {}) => {
    setActionLoading(true); setActionMsg('');
    try {
      let url, body = {};
      if (action === 'cancel') { url = `${API}/orders/${orderId}/cancel`; }
      else if (action === 'note') { url = `${API}/orders/${orderId}/note`; body = { note: extra.note }; }
      else if (action === 'tags') { url = `${API}/orders/${orderId}/tags`; body = { tags: extra.tags }; }
      else if (action === 'fulfill') { url = `${API}/orders/${orderId}/fulfill`; }
      else if (action === 'mark-paid') { url = `${API}/orders/${orderId}/mark-paid`; }
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) { setActionMsg('✅ Acción realizada correctamente'); const c = conversations.find(x => x.id === activeConv); if (c?.contact_phone) loadShopifyData(c.contact_phone); }
      else { setActionMsg('❌ Error al realizar la acción'); }
    } catch (e) { setActionMsg('❌ Error de conexión'); }
    setActionLoading(false);
  };

  const conv = conversations.find(c => c.id === activeConv);
  const currentStatus = convStatuses[activeConv] || conv?.status || 'open';
  const statusObj = STATUS_OPTIONS.find(s => s.id === currentStatus) || STATUS_OPTIONS[0];
  const filtered = conversations.filter(c => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q ||
      (c.contact_name || '').toLowerCase().includes(q) ||
      (c.contact_phone || '').includes(q) ||
      (c.last_message || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (['fb', 'ig', 'messenger', 'instagram'].includes(c.channel)) return false;
    if (filter === 'Nuevos') return (c.unread_count || 0) > 0;
    if (filter === 'Abiertos') return c.status === 'open';
    if (filter === 'Urgentes') return c.status === 'urgent';
    if (filter === 'Resueltos') return c.status === 'resolved';
    return true;
  });

  const activeOrder = shopifyOrders.find(o => o.fulfillment_status !== 'fulfilled' && o.financial_status !== 'voided' && o.financial_status !== 'refunded') || shopifyOrders[0];
  const pastOrders = shopifyOrders.filter(o => o.shopify_id !== activeOrder?.shopify_id);
  const guiaNexus = nexusOrderInfo?.tracking_number || null;
  const guiaShopify = activeOrder?.guia || null;
  const trackingUrl = guiaNexus
    ? `https://www.coordinadora.com/portafolio-de-servicios/servicios-en-linea/rastrear-guias/?guia=${guiaNexus}`
    : activeOrder?.tracking_url || null;
  const transportadora = nexusOrderInfo?.carrier || activeOrder?.transportadora || null;
  const carrierStatus = nexusOrderInfo?.carrier_status || null;
  const carrierLastUpdate = nexusOrderInfo?.carrier_last_update || null;

  useEffect(() => {
    if (!nexusOrderInfo?.shopify_order_id) { setTrackingTimeline(null); setTrackingTimelineOpen(false); return; }
    let cancelled = false;
    const fetchTimeline = async () => {
      setTrackingTimelineLoading(true);
      try {
        const res = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/timeline`, { headers: authH() });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setTrackingTimeline(data);
          if (data.events?.length > 0) setTrackingTimelineOpen(true);
        }
      } catch (e) {}
      if (!cancelled) setTrackingTimelineLoading(false);
    };
    fetchTimeline();
    return () => { cancelled = true; };
  }, [nexusOrderInfo?.shopify_order_id]);

  const ChannelIcon = ({ ch }) => ch === 'ig' ? <IgIcon /> : ch === 'fb' ? <FbIcon /> : <WaIcon />;
  const fmtRecording = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const isOrderEditable = (order) => order && order.fulfillment_status !== 'fulfilled';

  const SourceBadge = ({ source, isOut }) => {
    if (!isOut) return null;
    if (source === 'bot') return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#3b82f6',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 11V7"/><circle cx="12" cy="5" r="2"/></svg>IA</span>;
    if (source === 'flow') return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#fdf4ff',border:'1px solid #e9d5ff',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#9333ea',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="2" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>Flujo</span>;
    return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#16a34a',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>Agente</span>;
  };

  const StatusIcon = ({ status, isOut }) => {
    if (!isOut) return null;
    if (status === 'failed') return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
    if (status === 'read') return <span style={{display:'inline-flex'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" style={{marginLeft:-6}}><polyline points="20 6 9 17 4 12"/></svg></span>;
    if (status === 'delivered') return <span style={{display:'inline-flex'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" style={{marginLeft:-6}}><polyline points="20 6 9 17 4 12"/></svg></span>;
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
  };

  return (
    <>
      
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        body{overflow:hidden;}
        .app{display:flex;flex-direction:column;flex:1;min-height:0;background:#f0f0ee;}
        .app-body{display:flex;flex:1;overflow:hidden;}
        .sidebar{width:80px;background:transparent;display:flex;flex-direction:column;justify-content:space-between;align-items:center;padding:20px 0;flex-shrink:0;}
        .sb-island{background:rgba(255,255,255,0.9);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.4);border-radius:9999px;box-shadow:0 4px 16px rgba(0,0,0,0.07);display:flex;flex-direction:column;align-items:center;gap:2px;padding:10px 10px;}
        .sb-icon-btn{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;border:none;background:transparent;transition:all 0.18s;color:#9CA3AF;flex-shrink:0;}
        .sb-icon-btn:hover{background:rgba(0,0,0,0.05);color:#374151;}
        .sb-icon-btn.active{color:#1f2937;background:rgba(0,0,0,0.07);}
        .sb-dark-circle{width:42px;height:42px;border-radius:50%;background:radial-gradient(circle at 50% 30%,#4B5563,#111827);box-shadow:inset 0 2px 4px rgba(255,255,255,0.12),0 4px 10px rgba(0,0,0,0.32);display:flex;align-items:center;justify-content:center;cursor:pointer;border:none;transition:all 0.18s;flex-shrink:0;}
        .sb-dark-circle:hover{box-shadow:inset 0 2px 4px rgba(255,255,255,0.12),0 6px 16px rgba(0,0,0,0.42);transform:translateY(-1px);}
        .sb-sep{width:22px;height:1px;background:rgba(0,0,0,0.08);margin:3px 0;}
        .topbar{height:68px;background:transparent;display:flex;align-items:center;padding:0 24px;gap:16px;flex-shrink:0;z-index:10;}
        .tb-brand{display:flex;align-items:center;gap:10px;min-width:150px;}
        .tb-logo{height:32px;background:transparent;display:flex;align-items:center;flex-shrink:0;}
        .tb-name{font-size:19px;font-weight:700;color:#0a0a0a;letter-spacing:-0.4px;}
        .tb-name span{color:#6ee7b7;}
        .tb-nav{flex:1;display:flex;align-items:center;justify-content:center;}
        .tb-nav-inner{display:flex;align-items:center;gap:2px;background:rgba(255,255,255,0.78);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-radius:999px;border:1px solid rgba(255,255,255,0.65);box-shadow:0 4px 24px rgba(0,0,0,0.08),0 1px 3px rgba(0,0,0,0.05);padding:3px;}
        .tb-nav-item{font-size:13.5px;font-weight:500;color:#555;padding:7px 20px;border-radius:999px;cursor:pointer;transition:all 0.18s;white-space:nowrap;border:none;background:transparent;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .tb-nav-item:hover{color:#0a0a0a;background:rgba(0,0,0,0.05);}
        .tb-nav-item.active{background:linear-gradient(145deg,#1f1f1f 0%,#0a0a0a 100%);color:#fff;font-weight:700;box-shadow:inset 0 1px 0 rgba(255,255,255,0.12),inset 0 -1px 0 rgba(0,0,0,0.2),0 2px 8px rgba(0,0,0,0.22);}
        .tb-right{display:flex;align-items:center;gap:10px;min-width:150px;justify-content:flex-end;}
        .tb-icon-btn{width:42px;height:42px;border-radius:50%;border:1px solid rgba(255,255,255,0.6);background:rgba(255,255,255,0.7);display:flex;align-items:center;justify-content:center;cursor:pointer;position:relative;transition:background 0.15s;backdrop-filter:blur(8px);}
        .tb-icon-btn:hover{background:rgba(255,255,255,0.9);}
        .tb-badge{position:absolute;top:8px;right:8px;width:7px;height:7px;border-radius:50%;background:#ef4444;border:1.5px solid #fff;}
        .tb-profile{display:flex;align-items:center;gap:9px;background:rgba(255,255,255,0.78);border:1px solid rgba(255,255,255,0.65);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-radius:999px;padding:5px 14px 5px 5px;cursor:pointer;}
        .tb-profile-img{width:34px;height:34px;border-radius:50%;background:#0a0a0a;overflow:hidden;}
        .tb-profile-name{font-size:13px;font-weight:700;color:#0a0a0a;}
        .tb-profile-role{font-size:11px;color:#999;}
        .conv-layout{flex:1;display:flex;overflow:hidden;padding:0;gap:0;min-height:0;}

        /* ── Left panel ── */
        .conv-list-panel{width:340px;background:#fff;border-right:1px solid #ebebeb;display:flex;flex-direction:column;flex-shrink:0;}
        .clp-order-filter{position:relative;margin:0 12px 8px;}
        .clp-order-btn{width:100%;padding:8px 12px;border-radius:10px;border:1.5px solid #e8e8e6;background:#f9f9f7;font-size:13px;font-weight:500;color:#555;cursor:pointer;display:flex;align-items:center;justify-content:space-between;font-family:inherit;}
        .clp-order-btn.active{border-color:#0a0a0a;background:#0a0a0a;color:#fff;}
        .clp-order-dropdown{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1px solid #e8e8e6;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.1);z-index:100;overflow:hidden;}
        .clp-od-item{padding:9px 14px;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:8px;transition:background 0.1s;}
        .clp-od-item:hover{background:#f5f5f3;}
        .clp-od-item.active{font-weight:700;color:#0a0a0a;}
        .src-dot{display:inline-block;width:8px;height:8px;border-radius:50%;flex-shrink:0;}
        .src-dot.src-organic{background:#a0a0a0;} .src-dot.src-meta_ad{background:#1877f2;} .src-dot.src-nexus_landing{background:#8b5cf6;}
        .src-dot.src-nexus_recovery{background:#f97316;} .src-dot.src-social_ad{background:#ec4899;} .src-dot.src-nexus_catalog{background:#14b8a6;}
        .clp-tabs{display:flex;padding:0 12px 8px;gap:4px;overflow-x:auto;}
        .clp-tabs::-webkit-scrollbar{height:3px;}
        .clp-tabs::-webkit-scrollbar-thumb{background:#ddd;border-radius:3px;}
        .clp-tab{padding:5px 12px;border-radius:999px;font-size:12px;font-weight:500;color:#888;cursor:pointer;border:1.5px solid transparent;background:transparent;white-space:nowrap;font-family:inherit;flex-shrink:0;}
        .clp-tab.active{border-color:#0a0a0a;background:#0a0a0a;color:#fff;font-weight:700;}
        .clp-header{padding:20px 16px 0;}
        .clp-top-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
        .clp-title{font-size:22px;font-weight:700;color:#0a0a0a;letter-spacing:-0.4px;}
        .clp-top-actions{display:flex;align-items:center;gap:6px;}
        .clp-new-btn{display:flex;align-items:center;gap:5px;font-size:13px;font-weight:500;color:#0a0a0a;background:#f5f5f5;border:none;border-radius:999px;padding:6px 12px;cursor:pointer;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;transition:background 0.15s;}
        .clp-new-btn:hover{background:#ebebeb;}
        .clp-filter-btn{width:30px;height:30px;border-radius:50%;background:transparent;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.15s;}
        .clp-filter-btn:hover{background:#f0f0f0;}
        .clp-search{display:flex;align-items:center;gap:8px;background:#f5f5f5;border-radius:10px;padding:0 12px;height:36px;margin:0 0 14px;}
        .clp-search input{background:none;border:none;outline:none;font-size:13px;color:#0a0a0a;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;width:100%;}
        .clp-search input::placeholder{color:#c0c0c0;}
        .clp-toggle{display:flex;background:#f5f5f5;border-radius:10px;padding:3px;margin:0 0 16px;}
        .clp-toggle-btn{flex:1;text-align:center;padding:7px 0;border-radius:8px;font-size:13px;font-weight:500;cursor:pointer;border:none;background:transparent;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#888;transition:all 0.15s;}
        .clp-toggle-btn.active{background:#fff;color:#0a0a0a;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.09);}
        .clp-section-label{font-size:11px;font-weight:700;color:#b0b0b0;letter-spacing:0.04em;padding:10px 16px 6px;text-transform:none;}
        .clp-list{flex:1;overflow-y:auto;}
        .clp-list::-webkit-scrollbar{width:0;}
        .gs-item{padding:10px 16px;cursor:pointer;transition:background 0.1s;border-bottom:1px solid #f4f4f2;}
        .gs-item:hover{background:#f8f8f6;}
        .gs-name{font-size:13px;font-weight:700;color:#0a0a0a;margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .gs-snippet{font-size:12px;color:#888;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .gs-time{font-size:10px;color:#c0c0c0;flex-shrink:0;margin-left:6px;}

        /* Conv items */
        .conv-item{display:flex;align-items:center;gap:12px;padding:11px 16px;cursor:pointer;transition:background 0.1s;position:relative;}
        .conv-item:hover{background:#f8f8f8;}
        .conv-item.active{background:#f0f0f0;}
        .ci-av-wrap{position:relative;flex-shrink:0;}
        .ci-av{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#555;}
        .ci-online{position:absolute;bottom:1px;left:1px;width:11px;height:11px;border-radius:50%;background:#22c55e;border:2px solid #fff;}
        .ci-body{flex:1;min-width:0;}
        .ci-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:2px;}
        .ci-name{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.1px;}
        .ci-time{font-size:11px;color:#b8b8b8;flex-shrink:0;}
        .ci-preview{font-size:12.5px;color:#9a9a9a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ci-badge{min-width:19px;height:19px;border-radius:999px;background:#2196F3;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 5px;flex-shrink:0;margin-left:4px;}
        .ci-label-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;margin-left:4px;}
        .ctx-menu{position:fixed;background:#fff;border:1px solid #ebebeb;border-radius:12px;padding:5px;min-width:180px;box-shadow:0 8px 30px rgba(0,0,0,0.12);z-index:900;}
        .ctx-item{padding:9px 12px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;color:#0a0a0a;display:flex;align-items:center;gap:9px;transition:background 0.1s;}
        .ctx-item:hover{background:#f5f5f5;}
        .ctx-item.danger{color:#ef4444;}
        .ctx-item.danger:hover{background:#fef2f2;}
        .ctx-sep{height:1px;background:#f0f0f0;margin:4px 0;}
        .chat-panel{flex:1;display:flex;flex-direction:column;background:rgba(255,255,255,0.75);backdrop-filter:blur(12px) saturate(180%);-webkit-backdrop-filter:blur(12px) saturate(180%);border:1px solid rgba(255,255,255,0.3);border-radius:16px;box-shadow:0 8px 32px 0 rgba(31,38,135,0.07);overflow:hidden;min-height:0;}
        .chat-header{background:transparent;border-bottom:1px solid rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:space-between;padding:16px 24px;gap:12px;flex-shrink:0;}
        .ch-av{width:46px;height:46px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#555;position:relative;flex-shrink:0;}
        .ch-channel{position:absolute;bottom:-1px;right:-1px;width:18px;height:18px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;}
        .ch-info{flex:1;min-width:0;}
        .ch-name-row{display:flex;align-items:center;gap:8px;margin-bottom:2px;}
        .ch-name{font-size:15px;font-weight:700;color:#111827;}
        .ch-note-badge{background:#E3F2FD;color:#1976D2;border-radius:9999px;padding:2px 10px;font-size:12px;font-weight:700;}
        .ch-sub{font-size:12px;color:#9CA3AF;}
        .ch-actions{display:flex;gap:16px;align-items:center;}
        .ch-btn-icon{width:34px;height:34px;border-radius:50%;border:none;background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background 0.15s;flex-shrink:0;color:#6B7280;}
        .ch-btn-icon:hover{background:rgba(0,0,0,0.05);}
        .messages-area{flex:1;overflow-y:auto;padding:16px 18px;display:flex;flex-direction:column;gap:6px;background:#efeae2;}
        .date-sep{display:flex;align-items:center;justify-content:center;margin:8px 0;}
        .date-sep-line{display:none;}
        .date-sep-label{font-size:11px;font-weight:600;color:#54656f;background:#fff;padding:4px 12px;border-radius:6px;box-shadow:0 1px 1px rgba(0,0,0,0.08);}
        .msg-row{display:flex;flex-direction:column;max-width:68%;}
        .msg-row.out{align-self:flex-end;align-items:flex-end;}
        .msg-row.in{align-self:flex-start;align-items:flex-start;}
        .msg-meta{display:flex;align-items:center;gap:5px;margin-bottom:3px;}
        .msg-av-sm{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;flex-shrink:0;}
        .msg-sender{font-size:11px;font-weight:600;color:#666;}
        .msg-ts{font-size:10px;color:#bbb;}
        .msg-bubble{padding:8px 12px;font-size:13.5px;line-height:1.5;word-break:break-word;white-space:pre-wrap;box-shadow:0 1px 1px rgba(0,0,0,0.06);}
        .msg-bubble.in{background:#ffffff;color:#111;border-radius:0 8px 8px 8px;border:1px solid #e8e8e6;}
        .msg-bubble.out{background:#d9fdd3;color:#111;border-radius:8px 0 8px 8px;border:none;}
        .quoted-msg{display:flex;gap:8px;margin-bottom:6px;padding:6px 10px;border-radius:6px;background:rgba(0,0,0,0.05);cursor:default;overflow:hidden;}
        .quoted-bar{width:3px;border-radius:2px;flex-shrink:0;background:#25d366;}
        .quoted-body{flex:1;min-width:0;}
        .quoted-sender{font-size:11px;font-weight:700;color:#075e54;margin-bottom:2px;}
        .quoted-text{font-size:12px;color:#555;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .msg-status-row{display:flex;align-items:center;gap:4px;margin-top:4px;}
        .msg-actions{display:none;gap:4px;margin-top:3px;}
        .msg-row:hover .msg-actions{display:flex;}
        .msg-action-btn{padding:3px 9px;border-radius:6px;border:1px solid #e4e4e2;background:#fff;cursor:pointer;font-size:11px;color:#555;display:flex;align-items:center;gap:4px;transition:background 0.12s;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .msg-action-btn:hover{background:#f0f0ee;border-color:#d0d0ce;}
        .reply-preview{display:flex;align-items:center;gap:10px;padding:9px 18px;background:#f8f8f6;border-top:1px solid #eeeeec;border-bottom:none;}
        .reply-bar{width:3px;height:36px;background:#25d366;border-radius:2px;flex-shrink:0;}
        .reply-preview-body{flex:1;min-width:0;}
        .reply-preview-sender{font-size:11px;font-weight:700;color:#075e54;margin-bottom:2px;}
        .reply-preview-text{font-size:12px;color:#666;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .reply-close{background:none;border:none;cursor:pointer;padding:4px;color:#aaa;font-size:18px;line-height:1;flex-shrink:0;}
        .reply-close:hover{color:#555;}
        .chat-input-wrapper{background:#f0f0f0;border-top:1px solid rgba(0,0,0,0.05);flex-shrink:0;position:relative;padding:8px 14px 10px;}
        .chat-input-pill{position:relative;border:1px solid #E5E7EB;border-radius:24px;background:#fff;padding:12px 52px 12px 16px;transition:border-color 0.2s;box-shadow:0 1px 4px rgba(0,0,0,0.04);}
        .chat-input-pill:focus-within{border-color:#9CA3AF;box-shadow:0 1px 4px rgba(0,0,0,0.08);}
        .ci-input{display:block;width:100%;background:transparent;border:none;outline:none;font-size:14px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111827;resize:none;height:auto;max-height:120px;overflow-y:auto;line-height:1.5;padding:0;}
        .ci-input::placeholder{color:#9CA3AF;}
        .ci-send{position:absolute;right:10px;top:50%;transform:translateY(-50%);width:36px;height:36px;border-radius:50%;background:#25d366;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background 0.15s;}
        .ci-send:hover{background:#1da855;} .ci-send:disabled{background:#ccc;cursor:not-allowed;}
        .chat-toolbar{background:#fff;border-radius:10px;padding:4px 6px;margin-top:8px;display:flex;align-items:center;gap:2px;border:1px solid #e8e8e6;box-shadow:0 1px 3px rgba(0,0,0,.04);flex-wrap:wrap;}
        .toolbar-sep{width:1px;height:16px;background:#e8e8e6;margin:0 3px;flex-shrink:0;}
        .toolbar-btn{display:flex;align-items:center;gap:5px;padding:5px 10px;border-radius:7px;border:none;background:transparent;cursor:pointer;font-size:11px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-weight:500;color:#555;transition:all .2s cubic-bezier(.4,0,.2,1);position:relative;white-space:nowrap;}
        .toolbar-btn:hover{background:#f5f5f3;color:#111;transform:translateY(-1px);}
        .toolbar-btn:active{transform:translateY(0);background:#ebebeb;}
        .toolbar-btn svg{transition:transform .2s;}
        .toolbar-btn:hover svg{transform:scale(1.1);}
        .shopify-btn{color:#16a34a;}
        .shopify-btn:hover{background:#f0fdf4;color:#15803d;}
        .ci-attach{display:none;}
        .ci-mic{display:none;}
        @keyframes pulse-red{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.3)}50%{box-shadow:0 0 0 6px rgba(239,68,68,0)}}
        .audio-player{display:flex;align-items:center;gap:10px;min-width:220px;max-width:320px;padding:2px 0;}
        .ap-play{width:36px;height:36px;border-radius:50%;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background 0.15s;}
        .audio-player.out .ap-play{background:#075e54;color:#fff;}
        .audio-player.in .ap-play{background:#25d366;color:#fff;}
        .ap-play:hover{opacity:0.85;}
        .ap-body{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;}
        .ap-wave{height:28px;display:flex;align-items:center;gap:1.5px;cursor:pointer;padding:2px 0;}
        .ap-bar{width:3px;min-height:3px;border-radius:1.5px;transition:background 0.1s;}
        .ap-info{display:flex;justify-content:space-between;align-items:center;}
        .ap-dur{font-size:11px;color:#667781;font-variant-numeric:tabular-nums;}
        .ap-time{font-size:10.5px;color:#667781;}
        @media(max-width:600px){.audio-player{min-width:180px;max-width:260px;}.ap-wave{height:24px;}}
        .cl-header{display:flex;align-items:center;gap:10px;margin-bottom:10px;}
        .cl-avatar{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;flex-shrink:0;background:#075e54;}
        .cl-name-row{flex:1;min-width:0;}
        .cl-name{font-size:15px;font-weight:700;color:#0a0a0a;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .cl-detail{display:flex;flex-direction:column;gap:3px;margin-bottom:8px;}
        .cl-detail-row{display:flex;align-items:center;gap:6px;font-size:12px;color:#555;line-height:1.3;}
        .cl-detail-row svg{flex-shrink:0;opacity:0.4;}
        .cl-detail-val{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .cl-stats{display:flex;gap:0;border-top:1px solid #f0efed;padding-top:8px;}
        .cl-stat{flex:1;text-align:center;}
        .cl-stat:first-child{border-right:1px solid #f0efed;}
        .cl-stat-num{font-size:15px;font-weight:700;color:#0a0a0a;line-height:1.2;}
        .cl-stat-label{font-size:10px;color:#999;margin-top:1px;}
        .op-card{background:#f8f8f6;border-radius:12px;padding:12px;border:1px solid #f0efed;}
        .op-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;}
        .op-id{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .op-item{display:flex;gap:10px;align-items:flex-start;padding:6px 0;}
        .op-item:not(:last-child){border-bottom:1px solid rgba(0,0,0,0.04);padding-bottom:8px;margin-bottom:2px;}
        .op-img{width:48px;height:48px;border-radius:8px;object-fit:cover;background:#eee;flex-shrink:0;}
        .op-img-placeholder{width:48px;height:48px;border-radius:8px;background:#f0efed;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .op-item-body{flex:1;min-width:0;}
        .op-item-name{font-size:12.5px;font-weight:600;color:#1a1a1a;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .op-item-variant{font-size:11px;color:#888;margin-top:1px;}
        .op-item-qty{font-size:10px;color:#aaa;margin-top:1px;}
        .op-footer{display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.04);}
        .op-price{font-size:15px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .op-payment{font-size:11px;font-weight:600;padding:3px 8px;border-radius:999px;}
        .tr-grid{display:flex;flex-direction:column;gap:4px;}
        .tr-row{display:flex;justify-content:space-between;align-items:center;padding:3px 0;}
        .tr-label{font-size:11px;color:#888;}
        .tr-value{font-size:11px;font-weight:600;color:#0a0a0a;text-align:right;max-width:55%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .tr-section{}
        .tr-section-label{font-size:9px;font-weight:700;color:#bbb;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:5px;}
        .tr-pills{display:flex;flex-wrap:wrap;gap:4px;margin-top:2px;}
        .tr-pill{font-size:10px;font-weight:600;padding:2px 8px;border-radius:999px;white-space:nowrap;text-transform:capitalize;}
        .info-panel{width:320px;background:#fff;border-left:1px solid #ebebeb;display:flex;flex-direction:column;flex-shrink:0;overflow:hidden;}
        .ip-tabs{display:flex;gap:0;flex-shrink:0;padding:0 16px;border-bottom:1px solid #f0f0ee;}
        .ip-tab{flex:1;padding:13px 8px;font-size:13px;font-weight:500;color:#bbb;text-align:center;cursor:pointer;border-bottom:2.5px solid transparent;transition:all 0.2s;letter-spacing:-0.01em;}
        .ip-tab:hover{color:#888;}
        .ip-tab.active{color:#0a0a0a;font-weight:700;border-bottom-color:#0a0a0a;}
        .ip-body{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:16px;}
        .ip-section-title{font-size:11px;font-weight:700;color:#0a0a0a;letter-spacing:0.02em;margin-bottom:2px;display:flex;align-items:center;gap:6px;}
        .ip-section-title svg{opacity:0.45;}
        .ip-card{background:#f8f8f6;border-radius:14px;padding:14px;border:1px solid #f0efed;}
        .ip-row{display:flex;justify-content:space-between;align-items:flex-start;padding:7px 0;border-bottom:1px solid rgba(0,0,0,0.04);}
        .ip-row:last-child{border-bottom:none;padding-bottom:0;}
        .ip-row:first-child{padding-top:0;}
        .ip-label{font-size:12px;color:#999;white-space:nowrap;}
        .ip-value{font-size:12px;font-weight:500;color:#1a1a1a;text-align:right;max-width:58%;word-break:break-word;}
        .order-card{background:linear-gradient(135deg,#fafaf8 0%,#f5f5f3 100%);border-radius:14px;padding:14px;margin-bottom:0;border:1px solid #eeeeed;transition:box-shadow 0.2s;}
        .order-card:hover{box-shadow:0 2px 12px rgba(0,0,0,0.04);}
        .order-card-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
        .order-num{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .order-status{font-size:10px;font-weight:700;padding:3px 10px;border-radius:999px;letter-spacing:0.02em;}
        .status-entregado{background:#f0fdf4;color:#16a34a;}
        .status-despachado{background:#eff6ff;color:#3b82f6;}
        .status-pendiente{background:#fef9c3;color:#854d0e;}
        .order-product{font-size:12px;color:#666;margin-bottom:2px;line-height:1.4;}
        .order-price{font-size:14px;font-weight:700;color:#0a0a0a;margin-top:6px;letter-spacing:-0.02em;}
        .order-meta{display:flex;justify-content:space-between;align-items:center;margin-top:6px;padding-top:6px;border-top:1px solid #f0f0ee;}
        .order-guia{font-size:11px;color:#3b82f6;font-weight:500;}
        .novedad-item{display:flex;gap:10px;margin-bottom:10px;position:relative;}
        .novedad-item:not(:last-child)::after{content:'';position:absolute;left:5px;top:14px;bottom:-4px;width:1px;background:#e8e8e6;}
        .novedad-dot{width:11px;height:11px;border-radius:50%;flex-shrink:0;margin-top:3px;}
        .novedad-title{font-size:12px;font-weight:700;margin-bottom:1px;}
        .novedad-desc{font-size:11px;color:#888;}
        .novedad-time{font-size:10px;color:#bbb;margin-top:1px;}
        .hist-item{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;background:#f8f8f6;border-radius:12px;margin-bottom:6px;border:1px solid #f0efed;transition:background 0.15s;}
        .hist-item:hover{background:#f0f0ee;}
        .note-input-wrap{display:flex;flex-direction:column;gap:7px;}
        .note-input{background:#f6f6f4;border:1px solid #e8e8e6;border-radius:10px;padding:11px 13px;font-size:13px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;outline:none;color:#0a0a0a;resize:none;width:100%;height:72px;}
        .note-input::placeholder{color:#bbb;}
        .note-btn{background:#0a0a0a;color:#fff;border:none;border-radius:9px;padding:8px 16px;font-size:12px;font-weight:700;cursor:pointer;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;align-self:flex-end;}
        .empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:#aaa;}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinner{width:18px;height:18px;border:2px solid #e8e8e6;border-top-color:#0a0a0a;border-radius:50%;animation:spin 0.7s linear infinite;}
        .live-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;animation:pulse 2s infinite;}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        .seg-badge{display:inline-block;padding:3px 10px;border-radius:999px;font-size:10px;font-weight:700;letter-spacing:0.03em;text-transform:capitalize;}
        .seg-vip{background:#fef9c3;color:#854d0e;} .seg-recurrente{background:#eff6ff;color:#1d4ed8;}
        .seg-nuevo{background:#f0fdf4;color:#166534;} .seg-inactivo{background:#f4f4f2;color:#888;}
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:500;display:flex;align-items:center;justify-content:center;}
        .modal{background:#fff;border-radius:20px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,0.2);max-height:90vh;display:flex;flex-direction:column;}
        .modal-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:18px;flex-shrink:0;}
        .modal-title{font-size:17px;font-weight:700;color:#0a0a0a;}
        .modal-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8e8e6;background:#f6f6f4;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;color:#888;}
        .modal-search{display:flex;align-items:center;gap:8px;background:#f6f6f4;border:1px solid #e8e8e6;border-radius:10px;padding:0 12px;height:38px;margin-bottom:14px;flex-shrink:0;}
        .modal-search input{background:none;border:none;outline:none;font-size:13px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0a0a0a;width:100%;}
        .modal-scroll{flex:1;overflow-y:auto;}
        .tpl-item{padding:12px 14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .tpl-item:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .tpl-name{font-size:13px;font-weight:700;color:#0a0a0a;margin-bottom:4px;}
        .tpl-text{font-size:12px;color:#888;line-height:1.5;}
        .tpl-badge{display:inline-block;font-size:10px;padding:2px 7px;border-radius:999px;font-weight:700;margin-bottom:6px;}
        .flow-item{display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .flow-item:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .options-menu{background:#fff;border:1px solid #e8e8e6;border-radius:12px;padding:6px;width:210px;box-shadow:0 4px 20px rgba(0,0,0,0.12);}
        .options-item{display:flex;align-items:center;gap:9px;padding:9px 12px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background 0.12s;}
        .options-item:hover{background:#f4f4f2;} .options-item.danger{color:#ef4444;} .options-item.danger:hover{background:#fef2f2;}
        .action-btn{width:100%;padding:10px;border-radius:10px;border:1px solid #e8e8e6;background:#fff;font-size:13px;font-weight:500;cursor:pointer;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0a0a0a;margin-bottom:8px;display:flex;align-items:center;gap:8px;transition:background 0.15s;}
        .action-btn:hover{background:#f4f4f2;} .action-btn.danger{color:#ef4444;border-color:#fee2e2;} .action-btn.danger:hover{background:#fef2f2;} .action-btn.disabled{opacity:0.4;cursor:not-allowed;}
        .call-option{display:flex;align-items:center;gap:12px;padding:14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .call-option:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .info-toggle-btn{display:none;}
        .info-panel-overlay{display:none;}

        @media(max-width:1080px){
          .conv-list-panel{width:280px;}
          .info-panel{position:fixed;right:0;top:0;bottom:0;z-index:600;width:340px;box-shadow:-8px 0 30px rgba(0,0,0,0.12);transform:translateX(100%);transition:transform 0.25s ease;}
          .info-panel.open{transform:translateX(0);}
          .info-panel-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:599;}
          .info-panel-overlay.open{display:block;}
          .info-toggle-btn{display:flex;}
          .chat-header{padding:12px 16px;}
          .chat-input-wrapper{padding:8px 12px 8px;}
          .chat-toolbar{flex-wrap:nowrap;overflow-x:auto;gap:0;padding:3px 4px;margin-top:6px;}
          .chat-toolbar::-webkit-scrollbar{height:0;}
          .toolbar-btn{padding:5px 7px;font-size:0;gap:0;}
          .toolbar-btn svg,.toolbar-btn img{font-size:initial;}
          .toolbar-sep{display:none;}
          .messages-area{padding:14px;}
        }
        @media(max-width:820px){
          .conv-list-panel{width:240px;}
        }
      `}</style>

      <input ref={fileInputRef} type="file" style={{display:'none'}}
        accept="image/*,video/mp4,video/3gpp,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
        onChange={e => {
          const file = e.target.files[0];
          if (file) {
            const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document';
            const previewUrl = (type === 'image' || type === 'video') ? URL.createObjectURL(file) : null;
            setPendingMedia({ file, previewUrl, type });
          }
          e.target.value='';
        }}
      />

      <div className="app">

        <div className="app-body">

          {/* Context menu global */}
          {contextMenu && (
            <div className="ctx-menu" style={{top:contextMenu.y, left:contextMenu.x}} onClick={e=>e.stopPropagation()}>
              <div className="ctx-item" onClick={()=>{ toggleFav(contextMenu.convId); setContextMenu(null); }}>
                {favConvs.has(contextMenu.convId) ? (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>Quitar de favoritos</>
                ) : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>Agregar a favoritos</>
                )}
              </div>
              <div className="ctx-sep"/>
              <div style={{padding:'4px 12px 6px',fontSize:11,fontWeight:700,color:'#b0b0b0',textTransform:'uppercase',letterSpacing:'0.04em'}}>Etiqueta</div>
              {[['pedido','#3b82f6','Pedido'],['novedad','#f59e0b','Novedad'],['garantia','#16a34a','Garantía'],['despachado','#6366f1','Despachado'],['pendiente','#f97316','Pendiente'],['pagado','#10b981','Pagado']].map(([id,color,label])=>{
                const ctxConvLabel = conversations.find(c=>c.id===contextMenu.convId)?.label || convLabels[contextMenu.convId];
                return (
                <div key={id} className="ctx-item" onClick={()=>setLabel(contextMenu.convId, ctxConvLabel===id ? null : id)}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:color,flexShrink:0}}/>
                  {label}
                  {ctxConvLabel===id && <svg style={{marginLeft:'auto'}} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                );
              })}
              <div className="ctx-sep"/>
              <div className="ctx-item danger" onClick={()=>setContextMenu(null)}>Cancelar</div>
            </div>
          )}

          <div className="conv-layout" onClick={()=>{ if(contextMenu) setContextMenu(null); if(showLabelFilter) setShowLabelFilter(false); if(showOrderFilter) setShowOrderFilter(false); if(showSourceFilter) setShowSourceFilter(false); }}>
            <div className="conv-list-panel">

              {/* ── Header ── */}
              <div className="clp-header">
                <div className="clp-top-row">
                  <div className="clp-title">Chats</div>
                  <div className="clp-top-actions">
                    <button className="clp-new-btn" onClick={openNewConvModal}>
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round"/></svg>
                      Nuevo Chat
                    </button>
                    <div style={{position:'relative'}}>
                      <button className="clp-filter-btn" onClick={e=>{e.stopPropagation();setShowLabelFilter(v=>!v);}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="5" r="1" fill="#555"/><circle cx="12" cy="12" r="1" fill="#555"/><circle cx="12" cy="19" r="1" fill="#555"/></svg>
                      </button>
                      {showLabelFilter && (
                        <div className="ctx-menu" style={{position:'absolute',top:'110%',right:0}} onClick={e=>e.stopPropagation()}>
                          <div style={{padding:'6px 12px 8px',fontSize:11,fontWeight:700,color:'#b0b0b0',textTransform:'uppercase',letterSpacing:'0.04em'}}>Filtrar por etiqueta</div>
                          <div className="ctx-item" onClick={()=>{setLabelFilter(null);setShowLabelFilter(false);}}>
                            Todos {!labelFilter && <svg style={{marginLeft:'auto'}} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                          {[['pedido','#3b82f6','Pedido'],['novedad','#f59e0b','Novedad'],['garantia','#16a34a','Garantía'],['despachado','#6366f1','Despachado'],['pendiente','#f97316','Pendiente'],['pagado','#10b981','Pagado']].map(([id,color,label])=>(
                            <div key={id} className="ctx-item" onClick={()=>{setLabelFilter(id);setShowLabelFilter(false);}}>
                              <div style={{width:8,height:8,borderRadius:'50%',background:color}}/>
                              {label}
                              {labelFilter===id && <svg style={{marginLeft:'auto'}} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Filtro pedido */}
                <div className="clp-order-filter" onClick={e=>e.stopPropagation()}>
                  <button className={`clp-order-btn${orderFilter?' active':''}`} onClick={()=>setShowOrderFilter(v=>!v)}>
                    <span>{orderFilter ? {sin_confirmar:'Sin confirmar',por_generar:'Por generar',despachado:'Despachado',en_camino:'En camino',en_destino:'En destino',en_reparto:'En reparto',entregado:'Entregado',novedad:'Novedad',devolucion:'Devolución',cancelado:'Cancelado'}[orderFilter] : 'Todos los chats'}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showOrderFilter && (
                    <div className="clp-order-dropdown">
                      {[[null,'Todos los chats'],['sin_confirmar','Sin confirmar'],['por_generar','Por generar'],['despachado','Despachado'],['en_camino','En camino'],['en_destino','En destino'],['en_reparto','En reparto'],['entregado','Entregado'],['novedad','Novedad'],['devolucion','Devolución'],['cancelado','Cancelado']].map(([val,lbl])=>(
                        <div key={String(val)} className={`clp-od-item${orderFilter===val?' active':''}`} onClick={()=>{setOrderFilter(val);setShowOrderFilter(false);}}>
                          {orderFilter===val && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          {lbl}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filtro fuente */}
                <div className="clp-order-filter" onClick={e=>e.stopPropagation()}>
                  <button className={`clp-order-btn${sourceFilter?' active':''}`} onClick={()=>setShowSourceFilter(v=>!v)}>
                    <span>{sourceFilter ? {organic:'Orgánico',meta_ad:'Meta Ads',nexus_landing:'Nexus Landing',nexus_recovery:'Nexus Recovery',social_ad:'Social Ads',nexus_catalog:'Catálogo Nexus'}[sourceFilter]||sourceFilter : 'Todas las fuentes'}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showSourceFilter && (
                    <div className="clp-order-dropdown">
                      {[[null,'Todas las fuentes'],['organic','Orgánico'],['meta_ad','Meta Ads'],['nexus_landing','Nexus Landing'],['nexus_recovery','Nexus Recovery'],['social_ad','Social Ads'],['nexus_catalog','Catálogo Nexus']].map(([val,lbl])=>(
                        <div key={String(val)} className={`clp-od-item${sourceFilter===val?' active':''}`} onClick={()=>{setSourceFilter(val);setShowSourceFilter(false);}}>
                          {sourceFilter===val && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          <span className={val?`src-dot src-${val}`:''}/> {lbl}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Search */}
                <div className="clp-search">
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="#c0c0c0" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="#c0c0c0" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  <input placeholder="Buscar contacto..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
                </div>

                {/* Tabs */}
                <div className="clp-tabs">
                  {[['todos','Todos'],['abiertos','Abiertos'],['asignados','Asignados'],['resueltos','Resueltos']].map(([id,lbl])=>{
                    const count = id==='todos' ? conversations.length
                      : id==='resueltos' ? conversations.filter(c=>(convStatuses[c.id]||c.status)==='resolved').length
                      : id==='asignados' ? conversations.filter(c=>c.assigned_to===user?.name).length
                      : conversations.filter(c=>{ const st=convStatuses[c.id]||c.status||'open'; return st!=='resolved'; }).length;
                    return <button key={id} className={`clp-tab${listTab===id?' active':''}`} onClick={()=>setListTab(id)}>{lbl} {count>0&&<span style={{opacity:0.7}}>({count})</span>}</button>;
                  })}
                </div>
              </div>

              {/* ── List ── */}
              <div className="clp-list">
                {loading ? (
                  <div style={{display:'flex',justifyContent:'center',padding:40}}><div className="spinner"/></div>
                ) : searchQuery.length >= 3 && (globalSearchLoading || globalResults.length > 0) ? (
                  <>
                    <div className="clp-section-label" style={{display:'flex',alignItems:'center',gap:6}}>
                      {globalSearchLoading
                        ? <><div className="spinner" style={{width:10,height:10,borderWidth:1.5}}/>Buscando...</>
                        : `${globalResults.length} mensaje${globalResults.length!==1?'s':''} encontrado${globalResults.length!==1?'s':''}`
                      }
                    </div>
                    {globalResults.map(r => {
                      const q = searchQuery.toLowerCase();
                      const content = r.content || '';
                      const idx = content.toLowerCase().indexOf(q);
                      const snippet = idx >= 0
                        ? content.slice(Math.max(0, idx - 20), idx + 60)
                        : content.slice(0, 80);
                      return (
                        <div key={`${r.conv_id}_${r.message_id}`} className="gs-item" onClick={()=>{ setActiveConv(r.conv_id); markRead(r.conv_id); setSearchQuery(''); }}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <div className="gs-name">{r.conv_name || r.conv_phone}</div>
                            <div className="gs-time">{fmtTime(r.sent_at)}</div>
                          </div>
                          <div className="gs-snippet">
                            {idx >= 0 ? (
                              <>
                                {idx > 20 ? '…' : ''}{content.slice(Math.max(0,idx-20), idx)}
                                <mark style={{background:'#fef08a',borderRadius:2,padding:'0 1px',color:'#0a0a0a'}}>{content.slice(idx, idx+q.length)}</mark>
                                {content.slice(idx+q.length, idx+60)}{content.length > idx+60 ? '…' : ''}
                              </>
                            ) : snippet}
                          </div>
                        </div>
                      );
                    })}
                    {!globalSearchLoading && globalResults.length === 0 && (
                      <div style={{padding:'24px 20px',textAlign:'center',color:'#c0c0c0',fontSize:13}}>Sin resultados para "{searchQuery}"</div>
                    )}
                  </>
                ) : (() => {
                  // Filter by tab — si hay búsqueda activa, mostrar todas sin filtrar por tab
                  const byTab = conversations.filter(c => {
                    if (searchQuery) return true;
                    const st = convStatuses[c.id] || c.status || 'open';
                    if (listTab === 'resueltos') return st === 'resolved';
                    if (listTab === 'abiertos') return st !== 'resolved';
                    if (listTab === 'asignados') return c.assigned_to === user?.name && st !== 'resolved';
                    return true; // 'todos'
                  });
                  // Filter by search
                  const bySearch = byTab.filter(c => {
                    if (!searchQuery) return true;
                    const q = searchQuery.toLowerCase().replace(/[^0-9a-záéíóúñ\s#]/gi, '');
                    const phone = (c.contact_phone||'').replace(/\D/g,'');
                    const qDigits = searchQuery.replace(/\D/g,'');
                    return (c.contact_name||'').toLowerCase().includes(q)
                      || (phone && qDigits.length >= 6 && phone.includes(qDigits))
                      || (c.order_number && String(c.order_number).includes(searchQuery.replace('#','')));
                  });
                  // Filter by label
                  const byLabel = labelFilter ? bySearch.filter(c => (c.label || convLabels[c.id]) === labelFilter) : bySearch;
                  // Filter by order_estado
                  const byOrder = orderFilter ? byLabel.filter(c => c.order_estado === orderFilter) : byLabel;
                  // Filter by source
                  const bySource = sourceFilter ? byOrder.filter(c => (c.source || 'organic') === sourceFilter) : byOrder;
                  // Split favorites / rest
                  const favs = bySource.filter(c => favConvs.has(c.id));
                  const rest = bySource.filter(c => !favConvs.has(c.id));

                  const renderItem = (c, idx) => {
                    const labelColors = {pedido:'#3b82f6',novedad:'#f59e0b',garantia:'#16a34a',despachado:'#6366f1',pendiente:'#f97316',pagado:'#10b981'};
                    const lbl = c.label || convLabels[c.id];
                    const hasUnread = (c.unread_count||0) > 0 && activeConv !== c.id;
                    return (
                      <div key={c.id}
                        className={`conv-item${activeConv===c.id?' active':''}`}
                        onClick={()=>{setActiveConv(c.id);markRead(c.id);setContextMenu(null);}}
                        onContextMenu={e=>{e.preventDefault();setContextMenu({x:e.clientX,y:e.clientY,convId:c.id});}}
                      >
                        <div className="ci-av-wrap">
                          <div className="ci-av" style={{background:AVATAR_COLORS[idx%AVATAR_COLORS.length]}}>
                            {initials(shopifyNames[c.contact_phone?.replace(/\D/g,'')] || c.contact_name || c.contact_phone)}
                          </div>
                          <div className="ci-online"/>
                        </div>
                        <div className="ci-body">
                          <div className="ci-top">
                            <span className="ci-name" style={hasUnread?{fontWeight:700}:{}}>{shopifyNames[c.contact_phone?.replace(/\D/g,'')] || c.contact_name || fmtPhone(c.contact_phone)}</span>
                            <span className="ci-time">{fmtTime(c.last_message_at)}</span>
                          </div>
                          {c.source && c.source !== 'organic' && (() => {
                            const srcCfg = {meta_ad:['Meta Ad','#1877f2'],nexus_landing:['Nexus','#8b5cf6'],nexus_recovery:['Recovery','#f97316'],social_ad:['Social Ad','#ec4899'],nexus_catalog:['Catálogo','#14b8a6']};
                            const [sl,sc] = srcCfg[c.source]||[c.source,'#888'];
                            return <span style={{display:'inline-block',fontSize:9,fontWeight:600,color:sc,background:sc+'18',border:`1px solid ${sc}40`,borderRadius:4,padding:'0 5px',lineHeight:'16px',marginTop:1}}>{sl}</span>;
                          })()}
                          <div className="ci-preview" style={hasUnread?{color:'#555',fontWeight:500}:{}}>{c.last_message||'Sin mensajes'}</div>
                        </div>
                        {hasUnread && <div className="ci-badge">{c.unread_count}</div>}
                        {lbl && <div className="ci-label-dot" style={{background:labelColors[lbl]}}/>}
                        {favConvs.has(c.id) && !hasUnread && <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="none" style={{flexShrink:0,marginLeft:2}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>}
                      </div>
                    );
                  };

                  if (bySource.length === 0) return <div style={{padding:'40px 20px',textAlign:'center',color:'#c0c0c0',fontSize:13}}>Sin conversaciones</div>;

                  return (
                    <>
                      {favs.length > 0 && (
                        <>
                          <div className="clp-section-label">Favoritos</div>
                          {favs.map((c,i)=>renderItem(c,i))}
                        </>
                      )}
                      {rest.length > 0 && (
                        <>
                          <div className="clp-section-label">{favs.length > 0 ? 'Mensajes' : 'Todos los chats'}</div>
                          {rest.map((c,i)=>renderItem(c,favs.length+i))}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="chat-panel">
              {!conv ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <div style={{fontSize:14,fontWeight:500}}>Selecciona una conversación</div>
                  <div style={{fontSize:12}}>Los mensajes aparecen aquí en tiempo real</div>
                </div>
              ) : (
                <>
                  <div className="chat-header">
                    <div className="ch-av" style={{background:AVATAR_COLORS[filtered.findIndex(c=>c.id===activeConv)%AVATAR_COLORS.length]}}>
                      {initials(shopifyContact?.nombre || conv.contact_name || conv.contact_phone)}
                      <div className="ch-channel"><ChannelIcon ch={conv.channel||'wa'}/></div>
                    </div>
                    <div className="ch-info">
                      <div className="ch-name-row">
                        <div className="ch-name">{shopifyContact?.nombre || conv.contact_name || fmtPhone(conv.contact_phone)}</div>
                        {notes.length > 0 && <span className="ch-note-badge">{notes.length} {notes.length===1?'Note':'Notes'}</span>}
                        {assignedAgent[activeConv] && <span style={{fontSize:11,color:'#3b82f6',fontWeight:700,background:'#eff6ff',padding:'2px 8px',borderRadius:999}}>{assignedAgent[activeConv]}</span>}
                      </div>
                      <div className="ch-sub">{fmtPhone(conv.contact_phone)}</div>
                    </div>
                    <div className="ch-actions">
                      {/* Status pill */}
                      <div style={{position:'relative'}}>
                        <button onClick={()=>{setShowStatusMenu(!showStatusMenu);setShowOptionsMenu(false);setShowCallMenu(false);}} style={{display:'flex',alignItems:'center',gap:5,fontSize:12,padding:'5px 12px',borderRadius:999,fontWeight:700,border:'none',cursor:'pointer',fontFamily:'inherit',background:statusObj.bg,color:statusObj.color}}>
                          {statusObj.label}
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={statusObj.color} strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        {showStatusMenu && (
                          <div style={{position:'absolute',top:'110%',right:0,background:'#fff',border:'1px solid #e8e8e6',borderRadius:12,padding:'6px',width:160,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:200}}>
                            {STATUS_OPTIONS.map(s=>(
                              <div key={s.id} onClick={()=>{setConvStatuses(p=>({...p,[activeConv]:s.id}));patchConv(activeConv,{status:s.id});setShowStatusMenu(false);}} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:8,cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background=s.bg} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:8,height:8,borderRadius:'50%',background:s.color}}/>
                                <span style={{fontSize:13,fontWeight:500,color:s.color}}>{s.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Call */}
                      <div style={{position:'relative'}}>
                        <div className="ch-btn-icon" onClick={()=>{setShowCallMenu(!showCallMenu);setShowStatusMenu(false);setShowOptionsMenu(false);}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6 6l.86-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16z"/></svg>
                        </div>
                        {showCallMenu && (
                          <div style={{position:'absolute',top:'110%',right:0,background:'#fff',border:'1px solid #e8e8e6',borderRadius:14,padding:8,width:240,boxShadow:'0 4px 24px rgba(0,0,0,0.12)',zIndex:300}}>
                            <div className="call-option" onClick={()=>{setShowCallMenu(false);window.open(`https://wa.me/${conv.contact_phone?.replace(/\D/g,'')}`);}}>
                              <div style={{width:36,height:36,borderRadius:10,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><WaIcon/></div>
                              <div><div style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>Llamar por WhatsApp</div><div style={{fontSize:11,color:'#aaa'}}>Abre WhatsApp con llamada activa</div></div>
                            </div>
                            <div className="call-option" onClick={()=>{setShowCallMenu(false);setShowCallIA(true);}}>
                              <div style={{width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#6ee7b7,#3b82f6)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 11V7"/><circle cx="12" cy="5" r="2"/></svg></div>
                              <div><div style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>Llamar con IA</div><div style={{fontSize:11,color:'#aaa'}}>Zadarma + IA, elige el tipo</div></div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Bot toggle */}
                      <div
                        className="ch-btn-icon"
                        title={botStatus[activeConv] !== false ? 'Bot IA activo — click para desactivar' : 'Bot IA desactivado — click para activar'}
                        onClick={() => toggleBot(activeConv)}
                        style={{position:'relative'}}
                      >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={botStatus[activeConv] !== false ? '#22c55e' : '#ccc'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="10" rx="2"/>
                          <path d="M12 11V7"/>
                          <circle cx="12" cy="5" r="2"/>
                          <circle cx="8" cy="16" r="1" fill={botStatus[activeConv] !== false ? '#22c55e' : '#ccc'}/>
                          <circle cx="16" cy="16" r="1" fill={botStatus[activeConv] !== false ? '#22c55e' : '#ccc'}/>
                          <path d="M9 19h6"/>
                        </svg>
                        {botStatus[activeConv] === false && (
                          <div style={{position:'absolute',top:-2,right:-2,width:8,height:8,borderRadius:'50%',background:'#ef4444',border:'2px solid #fff'}}/>
                        )}
                      </div>
                      {/* Search in chat */}
                      <div className="ch-btn-icon" title="Buscar en chat (Ctrl+F)" onClick={()=>{ setShowChatSearch(v=>!v); setChatSearchTerm(''); setTimeout(()=>chatSearchRef.current?.focus(),50); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showChatSearch?'#0a0a0a':'#888'} strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      </div>
                      {/* Three dots */}
                      <div style={{position:'relative'}} ref={optionsRef}>
                        <div className="ch-btn-icon" onClick={()=>{setShowOptionsMenu(!showOptionsMenu);setShowStatusMenu(false);setShowCallMenu(false);}}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="5" r="1" fill="#888"/><circle cx="12" cy="12" r="1" fill="#888"/><circle cx="12" cy="19" r="1" fill="#888"/></svg>
                        </div>
                        {showOptionsMenu && (
                          <div className="options-menu" style={{position:'absolute',top:'110%',right:0,zIndex:300}}>
                            {OPTIONS_MENU.map((opt) => (
                              <div key={opt.id} className={`options-item${opt.danger?' danger':''}`} onClick={()=>{
                                setShowOptionsMenu(false);
                                if (opt.id === 'assign') setShowAssign(true);
                                if (opt.id === 'history') setActiveTab('notas');
                                if (opt.id === 'archive') { setConvStatuses(p=>({...p,[activeConv]:'resolved'})); patchConv(activeConv,{status:'resolved'}); }
                                if (opt.id === 'spam') alert('Conversación marcada como spam');
                              }}>{opt.icon}<span>{opt.label}</span></div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="ch-btn-icon info-toggle-btn" title="Panel cliente" onClick={()=>setShowInfoPanel(p=>!p)}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={showInfoPanel?'#0a0a0a':'#888'} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M15 3v18"/></svg>
                      </div>
                    </div>
                  </div>

                  {/* ── In-chat search bar ── */}
                  {showChatSearch && (
                    <div style={{background:'#fff',borderBottom:'1px solid #eeeeec',padding:'10px 20px',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      <input
                        ref={chatSearchRef}
                        value={chatSearchTerm}
                        onChange={e=>setChatSearchTerm(e.target.value)}
                        onKeyDown={e=>{ if(e.key==='Enter'){ e.shiftKey ? goSearchPrev() : goSearchNext(); } if(e.key==='Escape'){setShowChatSearch(false);setChatSearchTerm('');} }}
                        placeholder="Buscar en esta conversación..."
                        style={{flex:1,border:'none',outline:'none',fontSize:13,fontFamily:'inherit',color:'#0a0a0a',background:'transparent'}}
                      />
                      {chatSearchTerm.length >= 2 && (
                        <span style={{fontSize:11,color:'#aaa',whiteSpace:'nowrap',flexShrink:0}}>
                          {searchMatches.length === 0 ? 'Sin resultados' : `${chatSearchIdx+1} / ${searchMatches.length}`}
                        </span>
                      )}
                      {searchMatches.length > 1 && (
                        <>
                          <button onClick={goSearchPrev} style={{width:26,height:26,borderRadius:6,border:'1px solid #e4e4e2',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>
                          </button>
                          <button onClick={goSearchNext} style={{width:26,height:26,borderRadius:6,border:'1px solid #e4e4e2',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </button>
                        </>
                      )}
                      <button onClick={()=>{setShowChatSearch(false);setChatSearchTerm('');}} style={{width:26,height:26,borderRadius:6,border:'none',background:'transparent',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#aaa',fontSize:16,flexShrink:0}}>×</button>
                    </div>
                  )}

                  <div className="messages-area" ref={messagesAreaRef}>
                    {/* Botón cargar anteriores */}
                    {hasMoreMsgs && !loadingMsgs && (
                      <div style={{display:'flex',justifyContent:'center',marginBottom:8,flexShrink:0}}>
                        <button onClick={loadMoreMessages} disabled={loadingMore} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 16px',borderRadius:999,border:'1px solid #e4e4e2',background:'#fff',fontSize:12,fontWeight:500,color:'#555',cursor:loadingMore?'not-allowed':'pointer',fontFamily:'inherit',transition:'background 0.15s'}}>
                          {loadingMore ? <><div className="spinner" style={{width:11,height:11}}/> Cargando...</> : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg> Cargar mensajes anteriores</>}
                        </button>
                      </div>
                    )}
                    {loadingMsgs ? (
                      <div style={{display:'flex',justifyContent:'center',padding:32}}><div className="spinner"/></div>
                    ) : messages.length === 0 ? (
                      <div style={{textAlign:'center',color:'#bbb',fontSize:13,marginTop:32}}>No hay mensajes aún</div>
                    ) : (() => {
                      let lastDateStr = null;
                      const convIdx = filtered.findIndex(c=>c.id===activeConv);
                      const avatarBg = AVATAR_COLORS[convIdx % AVATAR_COLORS.length];
                      return messages.map((m) => {
                        const isOut = m.role === 'outbound' || m.role === 'assistant' || m.direction === 'outbound';
                        const source = m.role === 'assistant' ? 'bot' : (m.source || 'agent');
                        const status = m.status || 'sent';
                        const rawMediaUrl = m.media_url || (m.content && m.content.startsWith('https://') ? m.content : null);
                        const isLocalMedia = rawMediaUrl && rawMediaUrl.includes('/whatsapp/media/');
                        const isDirectImg = rawMediaUrl && (rawMediaUrl.includes('cdn.shopify.com') || rawMediaUrl.includes('nexusdrops.online'));
                        const proxyUrl = rawMediaUrl ? (isLocalMedia || isDirectImg ? rawMediaUrl : `${API}/whatsapp/media-proxy?url=${encodeURIComponent(rawMediaUrl)}`) : null;
                        const rawMt = m.media_type || '';
                        const mediaType = rawMt.startsWith('audio/') ? 'audio'
                          : rawMt.startsWith('video/') ? 'video'
                          : rawMt.startsWith('image/') ? 'image'
                          : rawMt.startsWith('application/') ? 'document'
                          : rawMt || null;
                        const hasError = mediaErrors[m.id];
                        const isConfirmTemplate = isOut && (m.content || '').includes('¿Los datos están correctos?');

                        // Date separator
                        const msgDate = m.sent_at || m.created_at;
                        let dateLabel = null;
                        if (msgDate) {
                          try {
                            const _ms = String(msgDate).replace(' ','T');
                            const d = new Date(_ms.includes('+') || _ms.endsWith('Z') ? _ms : _ms + '-05:00');
                            const today = new Date(); today.setHours(0,0,0,0);
                            const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
                            d.setHours(0,0,0,0);
                            const ds = d.toDateString();
                            if (ds !== lastDateStr) {
                              lastDateStr = ds;
                              if (d.getTime() === today.getTime()) dateLabel = 'HOY';
                              else if (d.getTime() === yesterday.getTime()) dateLabel = 'AYER';
                              else dateLabel = d.toLocaleDateString('es-CO',{day:'numeric',month:'long'}).toUpperCase();
                            }
                          } catch(e) {}
                        }

                        const senderName = isOut ? (user?.name?.split(' ')[0] || 'Tú') : (shopifyContact?.nombre || conv.contact_name || fmtPhone(conv.contact_phone));

                        return (
                          <React.Fragment key={m.id}>
                            {dateLabel && (
                              <div className="date-sep">
                                <div className="date-sep-line"/>
                                <span className="date-sep-label">{dateLabel}</span>
                                <div className="date-sep-line"/>
                              </div>
                            )}
                            <div ref={el=>msgRefs.current[m.id]=el} className={`msg-row ${isOut?'out':'in'}`}>
                              {/* Meta row above bubble */}
                              <div className="msg-meta" style={{flexDirection: isOut ? 'row-reverse' : 'row'}}>
                                <div className="msg-av-sm" style={{background: isOut ? '#075e54' : avatarBg, color: isOut ? '#fff' : '#555'}}>
                                  {isOut ? (user?.name?.[0]||'T') : initials(shopifyContact?.nombre || conv.contact_name || conv.contact_phone)}
                                </div>
                                <span className="msg-sender">{senderName}</span>
                                <span className="msg-ts">{fmtTime(m.sent_at || m.created_at)}</span>
                                {isOut && <SourceBadge source={source} isOut={isOut}/>}
                              </div>
                              {/* Bubble */}
                              <div className={`msg-bubble ${isOut?'out':'in'}`}>
                                {m.quoted_content && (
                                  <div className="quoted-msg">
                                    <div className="quoted-bar"/>
                                    <div className="quoted-body">
                                      <div className="quoted-sender">{m.quoted_sender || 'Mensaje'}</div>
                                      <div className="quoted-text">{m.quoted_content.slice(0, 100)}</div>
                                    </div>
                                  </div>
                                )}
                                {isConfirmTemplate ? (
                                  <div style={{width:300}}>
                                    {proxyUrl && !hasError && (
                                      <img src={proxyUrl} alt="producto" style={{width:'100%',height:'auto',borderRadius:'6px 6px 0 0',marginBottom:10,cursor:'pointer',display:'block'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                    )}
                                    <div style={{whiteSpace:'pre-wrap',lineHeight:1.6,fontSize:13}}>
                                      {chatSearchTerm.length >= 2 ? highlightText(m.content, chatSearchTerm) : m.content}
                                    </div>
                                    <div style={{display:'flex',gap:8,marginTop:10,paddingTop:8,borderTop:'1px solid rgba(0,0,0,0.08)'}}>
                                      <div style={{flex:1,textAlign:'center',color:'#2563eb',fontSize:12,fontWeight:700,padding:'6px 4px',borderRadius:6,background:'rgba(37,99,235,0.08)'}}>Corregir datos</div>
                                      <div style={{flex:1,textAlign:'center',color:'#16a34a',fontSize:12,fontWeight:700,padding:'6px 4px',borderRadius:6,background:'rgba(22,163,74,0.08)'}}>Datos correctos</div>
                                    </div>
                                  </div>
                                ) : proxyUrl && !hasError ? (
                                  mediaType === 'audio' ?
                                    <AudioPlayer src={proxyUrl} isOut={isOut} time={fmtTime(m.sent_at || m.created_at)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} /> :
                                  mediaType === 'video' ?
                                    <video controls src={proxyUrl} style={{maxWidth:'100%',borderRadius:8,maxHeight:200,cursor:'pointer'}} onClick={()=>setImgModal({url:proxyUrl,type:'video'})} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} /> :
                                  mediaType === 'document' ?
                                    <a href={proxyUrl} target="_blank" rel="noreferrer" style={{color:'#3b82f6',fontSize:13,display:'flex',alignItems:'center',gap:6}}>📄 Ver documento</a> :
                                  mediaType === 'sticker' ?
                                    <div style={{position:'relative',display:'inline-block'}}>
                                      <img src={proxyUrl} alt="sticker" style={{maxWidth:120,borderRadius:8,cursor:'pointer'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                      <span style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.5)',color:'#fff',fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:6}}>Sticker</span>
                                    </div> :
                                    <img src={proxyUrl} alt="imagen" style={{maxWidth:'100%',borderRadius:8,maxHeight:300,cursor:'pointer'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                ) : hasError ? <MediaExpired type={mediaType} /> :
                                  m.content === '[Sticker]' ? '🎭 Sticker' :
                                  m.content === '' || !m.content ? '📎 Archivo' :
                                  chatSearchTerm.length >= 2 ? highlightText(m.content, chatSearchTerm) : m.content}
                              </div>
                              {/* Hover actions */}
                              {m.content && m.content !== '[Sticker]' && (
                                <div className="msg-actions">
                                  <button className="msg-action-btn" onClick={()=>setReplyTo({id:m.id,content:m.content,sender:senderName,wamid:m.twilio_sid||null})}>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                                    Responder
                                  </button>
                                </div>
                              )}
                              {/* Status under bubble for outbound */}
                              {isOut && (
                                <div className="msg-status-row">
                                  <StatusIcon status={status} isOut={isOut}/>
                                  {status === 'failed' && <span style={{fontSize:10,color:'#ef4444'}}>No entregado</span>}
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>

                  {/* Pending media preview */}
                  {pendingMedia && (
                    <div style={{padding:'10px 18px',borderTop:'1px solid #f0f0ee',background:'#fff',display:'flex',alignItems:'center',gap:12}}>
                      {pendingMedia.type==='image' && pendingMedia.previewUrl && <img src={pendingMedia.previewUrl} alt="preview" style={{height:56,width:56,objectFit:'cover',borderRadius:8,flexShrink:0}}/>}
                      {pendingMedia.type==='video' && pendingMedia.previewUrl && <video src={pendingMedia.previewUrl} style={{height:56,width:80,objectFit:'cover',borderRadius:8,flexShrink:0}} muted/>}
                      {pendingMedia.type==='audio' && <div style={{width:36,height:36,borderRadius:8,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg></div>}
                      {pendingMedia.type==='document' && <div style={{width:36,height:36,borderRadius:8,background:'#eff6ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>}
                      <div style={{flex:1,minWidth:0}}><div style={{fontSize:12,fontWeight:700,color:'#0a0a0a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pendingMedia.file.name}</div><div style={{fontSize:11,color:'#aaa'}}>{(pendingMedia.file.size/1024).toFixed(0)} KB</div></div>
                      <button onClick={()=>{ if(pendingMedia.previewUrl) URL.revokeObjectURL(pendingMedia.previewUrl); setPendingMedia(null); }} style={{padding:'5px 11px',borderRadius:8,border:'1px solid #e8e8e6',background:'#fff',fontSize:12,cursor:'pointer',fontFamily:'inherit',color:'#555'}}>Cancelar</button>
                      <button disabled={sending} onClick={async()=>{ const mm=pendingMedia; setPendingMedia(null); if(mm.previewUrl) URL.revokeObjectURL(mm.previewUrl); await sendMedia(mm.file); }} style={{padding:'5px 13px',borderRadius:8,border:'none',background:'#0a0a0a',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:5}}>
                        {sending?<div className="spinner" style={{width:11,height:11,borderTopColor:'#fff'}}/>:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                        Enviar
                      </button>
                    </div>
                  )}
                  {sendError && (
                    <div style={{padding:'5px 18px',background:'#fef2f2',display:'flex',alignItems:'center',gap:8}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#ef4444"/></svg>
                      <span style={{fontSize:11,color:'#ef4444',flex:1}}>{sendError}</span>
                      <button onClick={()=>setSendError('')} style={{fontSize:11,color:'#aaa',background:'none',border:'none',cursor:'pointer',padding:0}}>✕</button>
                    </div>
                  )}

                  {/* Quote preview when replying */}
                  {replyTo && (
                    <div className="reply-preview">
                      <div className="reply-bar"/>
                      <div className="reply-preview-body">
                        <div className="reply-preview-sender">{replyTo.sender}</div>
                        <div className="reply-preview-text">{replyTo.content.slice(0, 90)}</div>
                      </div>
                      <button className="reply-close" onClick={()=>setReplyTo(null)}>×</button>
                    </div>
                  )}

                  {/* ── Banner ventana 24h WhatsApp ── */}
                  {isWa24hExpired && (
                    <div style={{padding:'9px 16px',background:'#fffbeb',borderTop:'1px solid #fde68a',display:'flex',alignItems:'flex-start',gap:8,fontSize:12,color:'#92400e',lineHeight:1.55,flexShrink:0}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <span>
                        Has alcanzado tu ventana de respuesta abierta de 24 horas de Atención al Cliente en WhatsApp. Aún puedes mantenerte en contacto enviando un mensaje de plantilla de{' '}
                        <span
                          style={{fontWeight:700,cursor:'pointer',textDecoration:'underline',color:'#b45309',whiteSpace:'nowrap'}}
                          onClick={()=>{ setShowTemplates(true); setTemplateTab('meta'); loadMetaTemplates(); }}
                        >
                          WhatsApp aprobado
                        </span>.
                      </span>
                    </div>
                  )}

                  {/* ── New input area ── */}
                  <div className="chat-input-wrapper">
                    {recording ? (
                      <div style={{margin:'12px 18px 0',display:'flex',alignItems:'center',gap:10,background:'#fef2f2',border:'1.5px solid #fecaca',borderRadius:999,padding:'0 10px 0 18px',height:50}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:'#ef4444',animation:'pulse-red 1s infinite'}}/>
                        <span style={{fontSize:13,color:'#ef4444',fontWeight:500}}>{fmtRecording(recordingTime)}</span>
                        <span style={{fontSize:12,color:'#aaa',flex:1}}>Grabando nota de voz...</span>
                        <button onClick={stopRecording} style={{padding:'5px 13px',borderRadius:999,border:'none',background:'#ef4444',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Enviar</button>
                        <button onClick={()=>{recordingCancelledRef.current=true;mediaRecorderRef.current?.stop();setRecording(false);clearInterval(recordingTimerRef.current);setRecordingTime(0);}} style={{padding:'5px 11px',borderRadius:999,border:'1px solid #e8e8e6',background:'#fff',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>Cancelar</button>
                      </div>
                    ) : (
                      <ChatInput ref={chatInputRef} onSend={sendMessage} sending={sending} />
                    )}

                    {/* Emoji picker floating panel */}
                    {showEmojiPicker && (
                      <div style={{position:'absolute',bottom:'100%',left:16,zIndex:500}} onClick={e=>e.stopPropagation()}>
                        <EmojiPicker data={emojiData} set="native" theme="light" locale="es"
                          onEmojiSelect={e=>{chatInputRef.current?.append(e.native);setShowEmojiPicker(false);}}
                        />
                      </div>
                    )}

                    {/* Toolbar below input */}
                    <div className="chat-toolbar">
                      <button className="toolbar-btn" onClick={()=>{setShowTemplates(true);loadMetaTemplates();}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                        Template
                      </button>
                      <button className="toolbar-btn" onClick={e=>{e.stopPropagation();setShowEmojiPicker(v=>!v);}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                        Emoji
                      </button>
                      <button className="toolbar-btn" onClick={()=>fileInputRef.current?.click()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                        Archivo
                      </button>
                      <button className="toolbar-btn" onClick={recording ? stopRecording : startRecording} style={recording ? {color:'#ef4444'} : {}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={recording?'#ef4444':'currentColor'} strokeWidth="1.8" strokeLinecap="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                        Audio
                      </button>
                      <div className="toolbar-sep"/>
                      <button className="toolbar-btn" onClick={()=>setShowFlows(true)} style={{color:'#9333ea'}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="1.8" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                        Flujo
                      </button>
                      <button className="toolbar-btn shopify-btn" onClick={()=>{ if(shopifyOrders.length===0){alert('Este cliente no tiene pedidos en Shopify');return;} if(shopifyOrders.length===1){setSelectedOrder(shopifyOrders[0]);setShowOrderActions(true);}else{setShowOrderSelect(true);} }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                        Shopify
                      </button>
                      {activeOrder?.shopify_id && (
                        <button className="toolbar-btn" style={{color:'#f90370'}} onClick={()=>openNexusModal(activeOrder.shopify_id)}>
                          <img src="/nexus-logo.png" alt="Nexus" style={{width:14,height:14,objectFit:'contain'}} />
                          Nexus
                        </button>
                      )}
                      <div className="toolbar-sep"/>
                      <span style={{fontSize:10.5,color:'#bbb',fontWeight:500,flexShrink:0,fontVariantNumeric:'tabular-nums'}}>1000</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={`info-panel-overlay${showInfoPanel?' open':''}`} onClick={()=>setShowInfoPanel(false)}/>
            <div className={`info-panel${showInfoPanel?' open':''}`}>
              <div className="ip-tabs">
                {['cliente','notas'].map(t=>(
                  <div key={t} className={`ip-tab${activeTab===t?' active':''}`} onClick={()=>setActiveTab(t)}>
                    {t==='cliente'?'Cliente':'Notas'}
                  </div>
                ))}
              </div>
              <div className="ip-body">
                {activeTab==='cliente' ? (
                  <>
                    <div>
                      <div className="cl-header">
                        <div className="cl-avatar" style={{background: AVATAR_COLORS[(conv?.id || 0) % AVATAR_COLORS.length]}}>
                          {initials(shopifyContact?.nombre || conv?.contact_name || conv?.contact_phone)}
                        </div>
                        <div className="cl-name-row">
                          <div className="cl-name">{shopifyContact?.nombre || conv?.contact_name || '—'}</div>
                          {shopifyContact?.segmento && <span className={`seg-badge seg-${shopifyContact.segmento}`} style={{marginTop:2}}>{shopifyContact.segmento}</span>}
                        </div>
                      </div>
                      <div className="cl-detail">
                        <div className="cl-detail-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          <span className="cl-detail-val">{shopifyContact?.telefono || conv?.contact_phone || '—'}</span>
                        </div>
                        <div className="cl-detail-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          <span className="cl-detail-val">{shopifyContact?.ciudad || '—'}</span>
                        </div>
                        {(shopifyContact?.direccion && shopifyContact.direccion !== '—') && (
                          <div className="cl-detail-row">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span className="cl-detail-val" style={{whiteSpace:'normal'}}>{shopifyContact.direccion}</span>
                          </div>
                        )}
                      </div>
                      {shopifyContact && (
                        <div className="cl-stats">
                          <div className="cl-stat">
                            <div className="cl-stat-num">{shopifyContact.pedidos || 0}</div>
                            <div className="cl-stat-label">Pedidos</div>
                          </div>
                          <div className="cl-stat">
                            <div className="cl-stat-num" style={{color:'#16a34a'}}>{fmtCOP(shopifyContact.valor)}</div>
                            <div className="cl-stat-label">Valor total</div>
                          </div>
                        </div>
                      )}
                    </div>
                    {loadingOrders ? <div style={{display:'flex',justifyContent:'center',padding:16}}><div className="spinner"/></div>
                    : activeOrder ? (
                      <>
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>Pedido activo</div>
                        <div className="op-card">
                          <div className="op-head">
                            <div>
                              <span className="op-id">{activeOrder.id}</span>
                              {activeOrder.tienda && <div style={{fontSize:10,color:'#999',marginTop:1}}>{activeOrder.tienda}</div>}
                            </div>
                            <span className={`order-status ${activeOrder.estado==='entregado'?'status-entregado':activeOrder.estado==='despachado'?'status-despachado':'status-pendiente'}`}>{activeOrder.estado_label}</span>
                          </div>
                          {(activeOrder.items_detalle && activeOrder.items_detalle.length > 0 ? activeOrder.items_detalle : [{producto: activeOrder.product, variante: activeOrder.variant, cantidad: 1, variant_id: ''}]).map((item, idx) => {
                            const imgUrl = orderImages[idx];
                            return (
                              <div key={idx} className="op-item">
                                {imgUrl ? (
                                  <img className="op-img" src={imgUrl} alt="" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                                ) : null}
                                <div className="op-img-placeholder" style={imgUrl ? {display:'none'} : {}}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                                </div>
                                <div className="op-item-body">
                                  <div className="op-item-name">{item.producto || item.title || 'Producto'}</div>
                                  {(item.variante || item.variant_title) && (item.variante || item.variant_title) !== '—' && (
                                    <div className="op-item-variant">{item.variante || item.variant_title}</div>
                                  )}
                                  {(item.cantidad || item.quantity || 1) > 1 && (
                                    <div className="op-item-qty">Cant: {item.cantidad || item.quantity}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          <div className="op-footer">
                            <span className="op-price">{fmtCOP(activeOrder.total)}</span>
                            {(() => {
                              const pm = PAYMENT_MAP[(activeOrder.metodo||'').toLowerCase()] || { label: activeOrder.metodo || 'COD', img: null };
                              const isPaid = activeOrder.estado_pago === 'paid';
                              return (
                                <span className="op-payment" style={{ background: isPaid ? '#f0fdf4' : '#fef9c3', color: isPaid ? '#16a34a' : '#854d0e' }}>
                                  {pm.img && <img src={pm.img} alt="" style={{height:13,objectFit:'contain',marginRight:4,verticalAlign:'middle',borderRadius:2}}/>}
                                  {isPaid ? 'Pagado' : 'Pendiente'} · {pm.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* ── Trazabilidad ─────────────────────────────────────── */}
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>Trazabilidad</div>
                        <div className="op-card" style={{display:'flex',flexDirection:'column',gap:10}}>
                          {/* Envío */}
                          <div className="tr-section">
                            <div className="tr-section-label">Envío</div>
                            {(() => {
                              const cKey = (transportadora||'').toLowerCase();
                              const carrier = CARRIERS_MAP[cKey];
                              return (
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                                  {carrier?.img && <img src={carrier.img} alt="" style={{width:22,height:22,objectFit:'contain',borderRadius:'50%',border:'1.5px solid #f0efed'}}/>}
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{carrier?.label || transportadora || '—'}</span>
                                </div>
                              );
                            })()}
                            <div className="tr-pills">
                              {activeOrder.estado_envio && (
                                <span className="tr-pill" style={{background: activeOrder.estado_envio==='entregado' ? '#dcfce7' : activeOrder.estado_envio==='despachado' ? '#dbeafe' : '#f5f5f4', color: activeOrder.estado_envio==='entregado' ? '#16a34a' : activeOrder.estado_envio==='despachado' ? '#2563eb' : '#78716c'}}>
                                  {activeOrder.estado_envio}
                                </span>
                              )}
                              {carrierStatus && (
                                <span className="tr-pill" style={{background:'#eff6ff',color:'#3b82f6'}}>{carrierStatus}</span>
                              )}
                            </div>
                            {carrierLastUpdate && (
                              <div style={{fontSize:10,color:'#aaa',marginTop:2}}>{carrierLastUpdate}</div>
                            )}
                          </div>

                          {/* Pago */}
                          <div className="tr-section" style={{borderTop:'1px solid #f5f5f4',paddingTop:10}}>
                            <div className="tr-section-label">Pago</div>
                            {(() => {
                              const pm = PAYMENT_MAP[(activeOrder.metodo||'').toLowerCase()] || { label: activeOrder.metodo || 'COD', img: null };
                              const isPaid = activeOrder.estado_pago === 'paid';
                              return (
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  {pm.img && <img src={pm.img} alt="" style={{height:18,objectFit:'contain',borderRadius:3}}/>}
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{pm.label}</span>
                                  <span className="tr-pill" style={{marginLeft:'auto',background: isPaid ? '#dcfce7' : '#fef9c3', color: isPaid ? '#16a34a' : '#854d0e'}}>
                                    {isPaid ? 'Pagado' : 'Pendiente'}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Guías */}
                          <div className="tr-section" style={{borderTop:'1px solid #f5f5f4',paddingTop:10}}>
                            <div className="tr-section-label">Guía de envío</div>
                            {guiaNexus ? (
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <img src="/nexus-logo.png" alt="" style={{width:14,height:14,objectFit:'contain'}}/>
                                <a href={trackingUrl} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:700,color:'#f90370',textDecoration:'none'}}>
                                  {guiaNexus}
                                </a>
                              </div>
                            ) : nexusOrderInfo?.has_nexus ? (
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <img src="/nexus-logo.png" alt="" style={{width:14,height:14,objectFit:'contain'}}/>
                                <span style={{fontSize:11,color:'#aaa'}}>Sin guía aún</span>
                              </div>
                            ) : null}
                            {guiaShopify && (
                              <div style={{display:'flex',alignItems:'center',gap:6,marginTop: guiaNexus || nexusOrderInfo?.has_nexus ? 4 : 0}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#96bf48" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
                                {activeOrder.tracking_url ? (
                                  <a href={activeOrder.tracking_url} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:700,color:'#3b82f6',textDecoration:'none'}}>
                                    {guiaShopify}
                                  </a>
                                ) : (
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{guiaShopify}</span>
                                )}
                              </div>
                            )}
                            {!guiaNexus && !guiaShopify && !nexusOrderInfo?.has_nexus && (
                              <span style={{fontSize:11,color:'#bbb'}}>Sin guía de envío</span>
                            )}
                          </div>
                        </div>

                        {/* ── Timeline de tracking ────────────────────────────────── */}
                        {nexusOrderInfo?.shopify_order_id && (trackingTimeline?.events?.length > 0 || trackingTimelineLoading) && (
                          <div style={{marginBottom:14}}>
                            <div
                              onClick={()=>setTrackingTimelineOpen(!trackingTimelineOpen)}
                              style={{cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:trackingTimelineOpen?8:0,transition:'margin .2s'}}
                            >
                              <div className="ip-section-title" style={{marginBottom:0}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                                Seguimiento del envío
                              </div>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2.5" strokeLinecap="round" style={{transform:trackingTimelineOpen?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                            {trackingTimelineOpen && (
                              trackingTimelineLoading ? (
                                <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'18px 0'}}>
                                  <div className="spinner" style={{width:16,height:16,borderWidth:2}}/>
                                </div>
                              ) : (
                                <div style={{background:'#fff',border:'1px solid #e8e8e6',borderRadius:14,padding:'14px 16px',position:'relative'}}>
                                  {trackingTimeline.events.map((ev, idx) => {
                                    const isLast = idx === trackingTimeline.events.length - 1;
                                    const statusColor = (() => {
                                      if (ev.status === 'delivered') return '#22c55e';
                                      if (ev.status === 'novedad') return '#f59e0b';
                                      if (ev.status === 'novedad_resuelta') return '#22c55e';
                                      if (ev.status === 'returned' || ev.status === 'cancelled') return '#ef4444';
                                      if (ev.status === 'en_reparto') return '#8b5cf6';
                                      return '#3b82f6';
                                    })();
                                    const fmtDate = (d) => {
                                      if (!d) return '';
                                      try {
                                        const dt = new Date(d);
                                        return dt.toLocaleDateString('es-CO',{day:'numeric',month:'short'}) + ' · ' + dt.toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit',hour12:false});
                                      } catch { return d; }
                                    };
                                    const statusLabel = (() => {
                                      const m = {
                                        shipped:'Guía generada',in_transit:'En tránsito',terminal_origen:'Terminal origen',
                                        terminal_destino:'Terminal destino',en_reparto:'En reparto',delivered:'Entregado',
                                        returned:'Devolución',cancelled:'Cancelado',novedad:'Novedad',novedad_resuelta:'Novedad resuelta'
                                      };
                                      return m[ev.status] || ev.status;
                                    })();
                                    return (
                                      <div key={idx} style={{display:'flex',gap:12,position:'relative',paddingBottom:isLast?0:16,minHeight:isLast?'auto':36}}>
                                        <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,width:14}}>
                                          <div style={{width:10,height:10,borderRadius:'50%',background:isLast?statusColor:'#fff',border:`2px solid ${statusColor}`,flexShrink:0,position:'relative',zIndex:1,boxShadow:isLast?`0 0 0 3px ${statusColor}22`:undefined}}/>
                                          {!isLast && <div style={{width:1.5,flex:1,background:'#e8e8e6',marginTop:3}}/>}
                                        </div>
                                        <div style={{flex:1,minWidth:0,paddingTop:0}}>
                                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:1}}>
                                            <span style={{fontSize:11.5,fontWeight:700,color:ev.status==='novedad'?'#d97706':'#0a0a0a',lineHeight:1.2}}>{statusLabel}</span>
                                            {ev.status==='novedad' && <span style={{fontSize:8,fontWeight:800,background:'#fffbeb',color:'#d97706',padding:'1px 5px',borderRadius:999,border:'1px solid #fde68a',letterSpacing:'0.03em'}}>!</span>}
                                            {ev.status==='delivered' && <span style={{fontSize:8,fontWeight:800,background:'#f0fdf4',color:'#22c55e',padding:'1px 5px',borderRadius:999,border:'1px solid #bbf7d0'}}>&#10003;</span>}
                                          </div>
                                          {ev.description && ev.description !== statusLabel && (
                                            <div style={{fontSize:10.5,color:'#888',lineHeight:1.4,marginBottom:1}}>{ev.description}</div>
                                          )}
                                          <div style={{fontSize:9.5,color:'#bbb',fontVariantNumeric:'tabular-nums'}}>{fmtDate(ev.created_at)}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {/* ── Enviar guía manual ────────────────────────────────────── */}
                        <div style={{marginBottom:14}}>
                          <div className="ip-section-title" style={{color:'#aaa',fontWeight:400,fontSize:10,letterSpacing:0.3}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                            Envía guía manual (opcional)
                          </div>
                          <div style={{display:'flex',gap:6,marginTop:8}}>
                            {[
                              {val:'coordinadora',img:'/coordinadora-btn.webp'},
                              {val:'interrapidisimo',img:'/interrapidisimo-btn.jpg'},
                              {val:'servientrega',img:'/servientrega-btn.jpg'},
                              {val:'tcc',img:'/tcc-logo.jpg'},
                            ].map(c=>{
                              const sel = shipCarrier===c.val;
                              return (
                                <button key={c.val} onClick={()=>{setShipCarrier(c.val);setShipMsg('');}} style={{flex:1,height:38,borderRadius:10,border:sel?'2px solid #0a0a0a':'1.5px solid #e8e8e6',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,transition:'all 0.15s'}}>
                                  <img src={c.img} alt={c.val} style={{height:22,objectFit:'contain',borderRadius:4,opacity:sel?1:0.4,transition:'opacity 0.15s'}}/>
                                </button>
                              );
                            })}
                          </div>
                          <div style={{display:'flex',gap:6,marginTop:8}}>
                            <input
                              type="text"
                              placeholder="Número de guía"
                              value={shipGuia}
                              onChange={e=>{setShipGuia(e.target.value);setShipMsg('');}}
                              style={{flex:1,padding:'9px 11px',borderRadius:9,border:'1.5px solid #e8e8e6',fontSize:12,fontFamily:'inherit',outline:'none',color:'#0a0a0a',background:'#fafafa',transition:'border 0.15s'}}
                            />
                            <button
                              disabled={shipLoading || !shipGuia.trim()}
                              onClick={async()=>{
                                if (!shipGuia.trim() || !activeOrder?.shopify_id) return;
                                setShipLoading(true); setShipMsg('');
                                try {
                                  const res = await fetch(`${API}/orders/${activeOrder.shopify_id}/fulfill-manual`, {
                                    method:'POST',
                                    headers: authH({'Content-Type':'application/json'}),
                                    body: JSON.stringify({ tracking_number: shipGuia.trim(), carrier: shipCarrier }),
                                  });
                                  const d = await res.json();
                                  if (res.ok && d.ok) {
                                    setShipMsg('✅ Guía enviada');
                                    setShipGuia('');
                                    const c = conversations.find(x=>x.id===activeConv);
                                    if (c?.contact_phone) loadShopifyData(c.contact_phone);
                                  } else {
                                    setShipMsg(`❌ ${d.detail || 'Error'}`);
                                  }
                                } catch(e) { setShipMsg('❌ Error de conexión'); }
                                setShipLoading(false);
                              }}
                              style={{padding:'9px 16px',borderRadius:9,border:'none',background:shipLoading||!shipGuia.trim()?'#ddd':'#0a0a0a',color:'#fff',fontSize:11,fontWeight:700,cursor:shipLoading||!shipGuia.trim()?'not-allowed':'pointer',fontFamily:'inherit',transition:'all 0.15s',whiteSpace:'nowrap'}}
                            >
                              {shipLoading ? '...' : 'Enviar'}
                            </button>
                          </div>
                          {shipMsg && (
                            <div style={{marginTop:6,fontSize:11,color:shipMsg.includes('✅')?'#16a34a':'#ef4444',fontWeight:500,textAlign:'center'}}>{shipMsg}</div>
                          )}
                        </div>
                      </>
                    ) : shopifyContact ? <div style={{padding:'10px 12px',background:'#f8f8f6',borderRadius:10,fontSize:12,color:'#aaa',textAlign:'center',marginBottom:16}}>Sin pedidos activos</div> : null}
                    {pastOrders.length > 0 && (
                      <>
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>Historial reciente</div>
                        {pastOrders.map(o=>(
                          <div key={o.id} className="hist-item">
                            <div><div style={{fontSize:12,fontWeight:700,color:'#0a0a0a'}}>{o.id}</div><div style={{fontSize:11,color:'#aaa'}}>{o.product}</div></div>
                            <div style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:999,background: o.estado==='despachado'?'#eff6ff':o.estado==='entregado'?'#f0fdf4':'#f8f8f6', color: o.estado==='despachado'?'#3b82f6':o.estado==='entregado'?'#16a34a':'#888'}}>{o.estado_label}</div>
                          </div>
                        ))}
                      </>
                    )}
                    {!shopifyContact && conv && <div style={{padding:'10px 12px',background:'#f8f8f6',borderRadius:10,fontSize:12,color:'#aaa',textAlign:'center',marginTop:8}}>Cliente nuevo — sin historial en Shopify</div>}
                  </>
                ) : (
                  <div>
                    <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>Notas internas</div>
                    <div style={{display:'flex',flexDirection:'column',gap:9,marginBottom:14}}>
                      {notes.map(n=>(
                        <div key={n.id} style={{background:'#f6f6f4',borderRadius:10,padding:'11px 13px',borderLeft:'3px solid #bbf7d0'}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                            <span style={{fontSize:11,fontWeight:700,color:'#555'}}>{n.author}</span>
                            <span style={{fontSize:10,color:'#bbb'}}>{n.time}</span>
                          </div>
                          <div style={{fontSize:12,color:'#555',lineHeight:1.5}}>{n.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="note-input-wrap">
                      <textarea className="note-input" placeholder="Agregar nota interna..." value={noteInput} onChange={e=>setNoteInput(e.target.value)}/>
                      <button className="note-btn" onClick={()=>{if(!noteInput.trim())return;setNotes([...notes,{id:Date.now(),author:user?.name?.split(' ')[0]||'Daniel',text:noteInput,time:'ahora'}]);setNoteInput('');}}>Guardar nota</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTemplates && (
        <div className="modal-overlay" onClick={()=>setShowTemplates(false)}>
          <div className="modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-hdr"><div className="modal-title">Plantillas de mensaje</div><div className="modal-close" onClick={()=>setShowTemplates(false)}>×</div></div>
            <div style={{display:'flex',gap:4,marginBottom:14,flexShrink:0}}>
              {['meta','internas'].map(t=>(
                <button key={t} onClick={()=>setTemplateTab(t)} style={{padding:'6px 16px',borderRadius:8,border:'1px solid #e8e8e6',background:templateTab===t?'#0a0a0a':'#fff',color:templateTab===t?'#fff':'#555',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
                  {t==='meta'?'Meta (aprobadas)':'Internas Talku'}
                </button>
              ))}
            </div>
            <div className="modal-search">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="#bbb" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <input placeholder="Buscar plantilla..." value={templateSearch} onChange={e=>setTemplateSearch(e.target.value)}/>
            </div>
            <div className="modal-scroll">
              {templateTab === 'meta' ? (
                metaTemplates.length === 0 ? <div style={{textAlign:'center',padding:32,color:'#bbb',fontSize:13}}>Cargando plantillas...</div>
                : metaTemplates.filter(t=>t.name?.toLowerCase().includes(templateSearch.toLowerCase())).map((t,i)=>{
                  const approved = t.status === 'APPROVED';
                  return (
                    <div key={i} className="tpl-item" style={{opacity:approved?1:0.5,cursor:approved?'pointer':'default'}}
                      onClick={()=>{ if(!approved) return; handleMetaTplClick(t); }}>
                      <div className="tpl-badge" style={{background:approved?'#f0fdf4':'#fef9c3',color:approved?'#16a34a':'#854d0e'}}>{approved?'✓ Aprobada':'⏳ Pendiente'}</div>
                      <div className="tpl-name">{t.name}</div>
                      <div className="tpl-text">{t.components?.find(c=>c.type==='BODY')?.text?.substring(0,120)}{approved?'':' — Pendiente de aprobación'}</div>
                    </div>
                  );
                })
              ) : PLANTILLAS_INTERNAS.filter(p=>p.nombre.toLowerCase().includes(templateSearch.toLowerCase())).map(p=>(
                <div key={p.id} className="tpl-item" onClick={()=>{
                  const nombre = shopifyContact?.nombre || conv?.contact_name || 'cliente';
                  sendMessage(p.texto.replace('{nombre}',nombre).replace('{orden}',activeOrder?.id||'—').replace('{estado}',activeOrder?.status||'—'));
                  setShowTemplates(false);
                }}>
                  <div className="tpl-badge" style={{background:'#eff6ff',color:'#1d4ed8'}}>{p.categoria}</div>
                  <div className="tpl-name">{p.nombre}</div>
                  <div className="tpl-text">{p.texto.substring(0,100)}...</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de variables para plantilla Meta */}
      {metaTplToSend && (
        <TemplateModal
          tpl={metaTplToSend}
          sending={metaTplSending}
          err={metaTplErr}
          onSend={(tpl, vars, headerUrl) => {
            setMetaTplVars(vars);
            setMetaTplHeaderUrl(headerUrl);
            sendMetaTemplate(tpl, vars, headerUrl);
          }}
          onClose={()=>{ setMetaTplToSend(null); setMetaTplVars([]); setMetaTplErr(''); setMetaTplHeaderUrl(''); }}
        />
      )}

      {showFlows && (
        <div className="modal-overlay" onClick={()=>{setShowFlows(false);setFlowStep('list');setSelectedFlow(null);setFlowResult(null);setFlowSearch('');}}>
          <div className="modal" style={{width:520}} onClick={e=>e.stopPropagation()}>

            {/* ── Paso 1: lista ── */}
            {flowStep === 'list' && (() => {
              const nombre = shopifyContact?.nombre || conv?.contact_name || 'cliente';
              const visibles = FLOWS_ACTIVOS.filter(f => {
                const matchEstado = flowFilterEstado === 'todos' || f.estado === flowFilterEstado;
                const matchSearch = !flowSearch || f.nombre.toLowerCase().includes(flowSearch.toLowerCase()) || f.tienda.toLowerCase().includes(flowSearch.toLowerCase());
                return matchEstado && matchSearch;
              });
              return (<>
                <div className="modal-hdr">
                  <div className="modal-title">Ejecutar flujo</div>
                  <div className="modal-close" onClick={()=>{setShowFlows(false);setFlowStep('list');setSelectedFlow(null);setFlowSearch('');}}>×</div>
                </div>
                <div style={{display:'flex',gap:8,marginBottom:12,flexShrink:0}}>
                  <input value={flowSearch} onChange={e=>setFlowSearch(e.target.value)} placeholder="Buscar flujo..." style={{flex:1,padding:'7px 11px',border:'1.5px solid #e4e4e2',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit'}}/>
                  <select value={flowFilterEstado} onChange={e=>setFlowFilterEstado(e.target.value)} style={{padding:'7px 10px',border:'1.5px solid #e4e4e2',borderRadius:8,fontSize:12,outline:'none',fontFamily:'inherit',color:'#555',background:'#fff'}}>
                    <option value="activo">Solo activos</option>
                    <option value="todos">Todos</option>
                    <option value="inactivo">Inactivos</option>
                  </select>
                </div>
                <div className="modal-scroll">
                  {visibles.length === 0 && <div style={{textAlign:'center',padding:32,color:'#bbb',fontSize:13}}>No hay flujos que coincidan</div>}
                  {visibles.map(f => {
                    const msgPreview = f.mensaje.replace('{nombre}', nombre);
                    const tipoColor = FLOW_TIPO_COLOR[f.tipo] || '#888';
                    return (
                      <div key={f.id} className="flow-item" style={{opacity: f.estado==='inactivo'?0.55:1}} onClick={()=>{setSelectedFlow({...f, msgResuelto: msgPreview});setFlowStep('preview');setFlowResult(null);}}>
                        <div style={{width:36,height:36,borderRadius:10,background:`${tipoColor}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tipoColor} strokeWidth="1.8" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                            <span style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>{f.nombre}</span>
                            <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:999,background:`${tipoColor}18`,color:tipoColor,textTransform:'uppercase',letterSpacing:'0.04em'}}>{f.tipo}</span>
                            {f.estado==='inactivo' && <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:999,background:'#f4f4f2',color:'#999'}}>inactivo</span>}
                          </div>
                          <div style={{fontSize:11,color:'#aaa',marginBottom:3}}>{f.tienda} · {f.ejecuciones.toLocaleString()} ejecuciones</div>
                          <div style={{fontSize:11,color:'#888',fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>"{msgPreview.substring(0,65)}{msgPreview.length>65?'...':''}"</div>
                        </div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                    );
                  })}
                </div>
              </>);
            })()}

            {/* ── Paso 2: preview + confirmar ── */}
            {flowStep === 'preview' && selectedFlow && (() => {
              const tipoColor = FLOW_TIPO_COLOR[selectedFlow.tipo] || '#888';
              return (<>
                <div className="modal-hdr">
                  <button onClick={()=>{setFlowStep('list');setFlowResult(null);}} style={{background:'none',border:'none',cursor:'pointer',padding:'4px 8px 4px 0',color:'#555',fontSize:13,display:'flex',alignItems:'center',gap:4,fontFamily:'inherit'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                    Volver
                  </button>
                  <div className="modal-close" onClick={()=>{setShowFlows(false);setFlowStep('list');setSelectedFlow(null);setFlowResult(null);}}>×</div>
                </div>
                <div style={{marginBottom:16,flexShrink:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',background:'#f8f8f6',borderRadius:12,border:'1px solid #e4e4e2',marginBottom:14}}>
                    <div style={{width:38,height:38,borderRadius:10,background:`${tipoColor}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={tipoColor} strokeWidth="1.8" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:'#0a0a0a'}}>{selectedFlow.nombre}</div>
                      <div style={{fontSize:11,color:'#aaa',marginTop:1}}>{selectedFlow.tienda} · <span style={{color:tipoColor,fontWeight:700}}>{selectedFlow.tipo}</span></div>
                    </div>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:'#888',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Mensaje que se enviará</div>
                  <div style={{background:'#F1F3F4',borderRadius:12,padding:'14px 16px',fontSize:14,lineHeight:1.7,color:'#0a0a0a',whiteSpace:'pre-wrap'}}>{selectedFlow.msgResuelto}</div>
                  <div style={{fontSize:11,color:'#aaa',marginTop:8}}>→ Se enviará a <strong>{conv?.contact_name || conv?.contact_phone}</strong> ({conv?.contact_phone})</div>
                </div>
                {flowResult === 'ok' && (
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'#f0fdf4',borderRadius:10,border:'1px solid #bbf7d0',marginBottom:12}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{fontSize:13,color:'#16a34a',fontWeight:500}}>Flujo ejecutado correctamente</span>
                  </div>
                )}
                {flowResult === 'error' && (
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'#fef2f2',borderRadius:10,border:'1px solid #fecaca',marginBottom:12}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#ef4444"/></svg>
                    <span style={{fontSize:13,color:'#ef4444',fontWeight:500}}>Error al enviar el mensaje</span>
                  </div>
                )}
                <div style={{display:'flex',gap:10,flexShrink:0}}>
                  <button onClick={()=>{setShowFlows(false);setFlowStep('list');setSelectedFlow(null);setFlowResult(null);}} style={{flex:1,padding:'11px 0',borderRadius:10,border:'1.5px solid #e4e4e2',background:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit',color:'#555'}}>Cancelar</button>
                  <button disabled={flowSending || flowResult==='ok'} onClick={async()=>{
                    setFlowSending(true);
                    setFlowResult(null);
                    try {
                      await sendMessage(selectedFlow.msgResuelto);
                      setFlowResult('ok');
                      setTimeout(()=>{setShowFlows(false);setFlowStep('list');setSelectedFlow(null);setFlowResult(null);}, 1400);
                    } catch(e) { setFlowResult('error'); }
                    setFlowSending(false);
                  }} style={{flex:2,padding:'11px 0',borderRadius:10,border:'none',background:flowResult==='ok'?'#16a34a':flowSending?'#555':'#0a0a0a',color:'#fff',fontSize:13,fontWeight:700,cursor:flowSending||flowResult==='ok'?'not-allowed':'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:8,transition:'background 0.15s'}}>
                    {flowSending ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/> Enviando...</> : flowResult==='ok' ? '✓ Enviado' : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> Ejecutar flujo</>}
                  </button>
                </div>
              </>);
            })()}

          </div>
        </div>
      )}

      {showOrderSelect && (
        <div className="modal-overlay" onClick={()=>setShowOrderSelect(false)}>
          <div className="modal" style={{width:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-hdr"><div className="modal-title">Seleccionar pedido</div><div className="modal-close" onClick={()=>setShowOrderSelect(false)}>×</div></div>
            <div style={{fontSize:12,color:'#aaa',marginBottom:14}}>Este cliente tiene {shopifyOrders.length} pedidos. ¿Cuál deseas modificar?</div>
            <div className="modal-scroll">
              {shopifyOrders.map(o=>(
                <div key={o.shopify_id} onClick={()=>{setSelectedOrder(o);setShowOrderSelect(false);setShowOrderActions(true);setActionMsg('');}} style={{padding:'14px 16px',border:'1px solid #e8e8e6',borderRadius:12,marginBottom:8,cursor:'pointer',transition:'all 0.15s',opacity:isOrderEditable(o)?1:0.6}} onMouseEnter={e=>e.currentTarget.style.borderColor='#0a0a0a'} onMouseLeave={e=>e.currentTarget.style.borderColor='#e8e8e6'}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                    <span style={{fontSize:14,fontWeight:700,color:'#0a0a0a'}}>{o.id}</span>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      {!isOrderEditable(o) && <span style={{fontSize:10,color:'#aaa'}}>No modificable</span>}
                      <span className={`order-status ${o.status==='Entregado'?'status-entregado':'status-pendiente'}`}>{o.status}</span>
                    </div>
                  </div>
                  <div style={{fontSize:12,color:'#555',marginBottom:4}}>{o.product}{o.variant?` · ${o.variant}`:''}</div>
                  <div style={{display:'flex',justifyContent:'space-between'}}>
                    <span style={{fontSize:12,fontWeight:700,color:'#0a0a0a'}}>{fmtCOP(o.total)}</span>
                    <span style={{fontSize:11,color:'#bbb'}}>{o.date ? new Date(o.date).toLocaleDateString('es-CO',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showOrderActions && selectedOrder && (
        <div className="modal-overlay" onClick={()=>{setShowOrderActions(false);setActionMsg('');setShopifyNoteInput('');setShopifyTagInput('');}}>
          <div className="modal" style={{width:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-hdr">
              <div><div className="modal-title">Acciones — {selectedOrder.id}</div><div style={{fontSize:12,color:'#aaa',marginTop:3}}>{selectedOrder.product} · {fmtCOP(selectedOrder.total)}</div></div>
              <div className="modal-close" onClick={()=>{setShowOrderActions(false);setActionMsg('');setShopifyNoteInput('');setShopifyTagInput('');}}>×</div>
            </div>
            {!isOrderEditable(selectedOrder) && <div style={{background:'#fef9c3',border:'1px solid #fde68a',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400e'}}>⚠️ Este pedido ya fue preparado/entregado. Solo puedes agregar notas o etiquetas.</div>}
            {actionMsg && <div style={{background:actionMsg.includes('✅')?'#f0fdf4':'#fef2f2',border:`1px solid ${actionMsg.includes('✅')?'#bbf7d0':'#fee2e2'}`,borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:13,color:actionMsg.includes('✅')?'#16a34a':'#ef4444'}}>{actionMsg}</div>}
            <div className="modal-scroll">
              <button className={`action-btn${!isOrderEditable(selectedOrder)?' disabled':''}`} disabled={!isOrderEditable(selectedOrder)||actionLoading} onClick={()=>shopifyAction('fulfill',selectedOrder.shopify_id)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                <div><div style={{fontWeight:700,color:isOrderEditable(selectedOrder)?'#0a0a0a':'#aaa'}}>Preparar pedido</div><div style={{fontSize:11,color:'#aaa',marginTop:1}}>Marca como fulfillment completado en Shopify</div></div>
                {actionLoading && <div className="spinner" style={{marginLeft:'auto'}}/>}
              </button>
              <button className={`action-btn${selectedOrder.financial_status==='paid'?' disabled':''}`} disabled={selectedOrder.financial_status==='paid'||actionLoading} onClick={()=>shopifyAction('mark-paid',selectedOrder.shopify_id)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></svg>
                <div><div style={{fontWeight:700,color:selectedOrder.financial_status==='paid'?'#aaa':'#0a0a0a'}}>Marcar como pagado</div><div style={{fontSize:11,color:'#aaa',marginTop:1}}>{selectedOrder.financial_status==='paid'?'Ya está pagado':'Registra el pago en Shopify'}</div></div>
              </button>
              <div style={{border:'1px solid #e8e8e6',borderRadius:10,padding:'12px 14px',marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',marginBottom:8}}>Agregar nota</div>
                {selectedOrder.note && <div style={{fontSize:11,color:'#aaa',marginBottom:6,fontStyle:'italic'}}>Nota actual: "{selectedOrder.note}"</div>}
                <textarea value={shopifyNoteInput} onChange={e=>setShopifyNoteInput(e.target.value)} placeholder="Escribe la nota..." style={{width:'100%',padding:'8px 10px',border:'1px solid #e8e8e6',borderRadius:8,fontSize:12,fontFamily:'inherit',outline:'none',resize:'none',height:60}}/>
                <button disabled={!shopifyNoteInput.trim()||actionLoading} onClick={()=>shopifyAction('note',selectedOrder.shopify_id,{note:shopifyNoteInput}).then(()=>setShopifyNoteInput(''))} style={{marginTop:6,padding:'6px 14px',borderRadius:8,border:'none',background:shopifyNoteInput.trim()?'#0a0a0a':'#e8e8e6',color:shopifyNoteInput.trim()?'#fff':'#aaa',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Guardar</button>
              </div>
              <div style={{border:'1px solid #e8e8e6',borderRadius:10,padding:'12px 14px',marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',marginBottom:8}}>Agregar etiqueta</div>
                {selectedOrder.tags && <div style={{fontSize:11,color:'#aaa',marginBottom:6}}>Etiquetas: {selectedOrder.tags}</div>}
                <input value={shopifyTagInput} onChange={e=>setShopifyTagInput(e.target.value)} placeholder="ej: urgente, cambio-talla" style={{width:'100%',padding:'8px 10px',border:'1px solid #e8e8e6',borderRadius:8,fontSize:12,fontFamily:'inherit',outline:'none'}}/>
                <button disabled={!shopifyTagInput.trim()||actionLoading} onClick={()=>shopifyAction('tags',selectedOrder.shopify_id,{tags:shopifyTagInput}).then(()=>setShopifyTagInput(''))} style={{marginTop:6,padding:'6px 14px',borderRadius:8,border:'none',background:shopifyTagInput.trim()?'#0a0a0a':'#e8e8e6',color:shopifyTagInput.trim()?'#fff':'#aaa',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Agregar</button>
              </div>
              <button className={`action-btn danger${!isOrderEditable(selectedOrder)?' disabled':''}`} disabled={!isOrderEditable(selectedOrder)||actionLoading} onClick={()=>{if(window.confirm(`¿Cancelar ${selectedOrder.id}? Esta acción no se puede deshacer.`))shopifyAction('cancel',selectedOrder.shopify_id);}}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                <div><div style={{fontWeight:700}}>Cancelar pedido</div><div style={{fontSize:11,color:'#ef4444',marginTop:1,opacity:0.8}}>Acción irreversible</div></div>
              </button>
              <button className="action-btn" onClick={()=>storeDomain && window.open(`https://${storeDomain}/admin/orders/new`,'_blank')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <div><div style={{fontWeight:700}}>Crear nuevo pedido</div><div style={{fontSize:11,color:'#aaa',marginTop:1}}>Abre Shopify Admin</div></div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" style={{marginLeft:'auto'}}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssign && (
        <div className="modal-overlay" onClick={()=>setShowAssign(false)}>
          <div className="modal" style={{width:380}} onClick={e=>e.stopPropagation()}>
            <div className="modal-hdr"><div className="modal-title">Asignar agente</div><div className="modal-close" onClick={()=>setShowAssign(false)}>×</div></div>
            <div style={{fontSize:12,color:'#aaa',marginBottom:14}}>Asigna esta conversación a un agente del equipo</div>
            {AGENTES.map(a=>(
              <div key={a} onClick={()=>{setAssignedAgent(p=>({...p,[activeConv]:a}));setShowAssign(false);}} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:12,border:`1px solid ${assignedAgent[activeConv]===a?'#0a0a0a':'#e8e8e6'}`,marginBottom:8,cursor:'pointer',background:assignedAgent[activeConv]===a?'#f4f4f2':'#fff',transition:'all 0.15s'}}>
                <div style={{width:36,height:36,borderRadius:50,background:AVATAR_COLORS[AGENTES.indexOf(a)%AVATAR_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:'#555'}}>{initials(a)}</div>
                <span style={{fontSize:13,fontWeight:500,color:'#0a0a0a'}}>{a}</span>
                {assignedAgent[activeConv]===a && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" style={{marginLeft:'auto'}}><polyline points="20 6 9 17 4 12"/></svg>}
              </div>
            ))}
          </div>
        </div>
      )}

      {showCallIA && (
        <div className="modal-overlay" onClick={()=>setShowCallIA(false)}>
          <div className="modal" style={{width:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-hdr"><div className="modal-title">Llamar con IA · Zadarma</div><div className="modal-close" onClick={()=>setShowCallIA(false)}>×</div></div>
            <div style={{fontSize:12,color:'#aaa',marginBottom:16}}>Selecciona el tipo de llamada que la IA realizará</div>
            {[
              { tipo:'Confirmación de pedido', desc:'La IA confirma datos del pedido y dirección de entrega', icon:'📦' },
              { tipo:'Recuperación de carrito', desc:'La IA intenta recuperar al cliente que no completó su compra', icon:'🛒' },
              { tipo:'Novedad de entrega', desc:'La IA informa sobre una novedad en la entrega y solicita corrección', icon:'🚚' },
              { tipo:'Encuesta de satisfacción', desc:'La IA solicita feedback sobre el pedido recibido', icon:'⭐' },
              { tipo:'Cobranza COD', desc:'La IA gestiona el cobro de un pedido contra entrega pendiente', icon:'💰' },
            ].map((t,i)=>(
              <div key={i} onClick={()=>{alert(`Llamada IA "${t.tipo}" a ${conv?.contact_phone}.\n\nIntegración con Zadarma pendiente.`);setShowCallIA(false);}} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 14px',borderRadius:12,border:'1px solid #e8e8e6',marginBottom:8,cursor:'pointer',transition:'all 0.15s'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='#0a0a0a';e.currentTarget.style.background='#f8f8f6';}} onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8e8e6';e.currentTarget.style.background='#fff';}}>
                <span style={{fontSize:24,flexShrink:0}}>{t.icon}</span>
                <div><div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',marginBottom:3}}>{t.tipo}</div><div style={{fontSize:11,color:'#aaa',lineHeight:1.5}}>{t.desc}</div></div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" style={{marginLeft:'auto',flexShrink:0,marginTop:2}}><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            ))}
            <div style={{marginTop:8,padding:'10px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:10,fontSize:11,color:'#92400e'}}>
              ⚠️ La integración con Zadarma requiere configurar las credenciales API en el panel de configuración.
            </div>
          </div>
        </div>
      )}

      {imgModal && (
        <div onClick={()=>setImgModal(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',cursor:'zoom-out'}}>
          {imgModal?.type === 'video' ?
            <video controls autoPlay src={imgModal.url} style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12}} onClick={e=>e.stopPropagation()} /> :
            <img src={typeof imgModal === 'string' ? imgModal : imgModal?.url} alt="preview" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12,objectFit:'contain'}} onClick={e=>e.stopPropagation()} />
          }
          <div onClick={()=>setImgModal(null)} style={{position:'absolute',top:20,right:24,width:40,height:40,borderRadius:'50%',background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:22,color:'#fff',fontWeight:300}}>×</div>
        </div>
      )}

      {/* Toast notifications */}
      <style>{`
        @keyframes toastIn {
          from { transform: translateX(-110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes toastOut {
          from { transform: translateX(0);    opacity: 1; }
          to   { transform: translateX(-110%); opacity: 0; }
        }
      `}</style>
      <div style={{position:'fixed',bottom:24,left:24,zIndex:9999,display:'flex',flexDirection:'column',gap:10,pointerEvents:'none'}}>
        {notifications.slice(-4).map(n => {
          const isOut = exitingNotifs.has(n.id);
          const avatarColor = AVATAR_COLORS[(n.convId || 0) % AVATAR_COLORS.length];
          return (
            <div key={n.id} style={{
              width:340,background:'#fff',borderRadius:18,
              boxShadow:'0 12px 48px rgba(0,0,0,0.14),0 2px 8px rgba(0,0,0,0.06)',
              padding:'14px 16px 14px 14px',pointerEvents:'all',
              animation:`${isOut?'toastOut 0.35s cubic-bezier(0.4,0,1,1)':'toastIn 0.42s cubic-bezier(0.16,1,0.3,1)'} forwards`,
            }}>
              {/* Header */}
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:9}}>
                <div style={{position:'relative',flexShrink:0}}>
                  <div style={{width:40,height:40,borderRadius:'50%',background:avatarColor,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'#fff',letterSpacing:0.5}}>
                    {initials(n.contactName)}
                  </div>
                  <div style={{position:'absolute',bottom:1,right:1,width:10,height:10,borderRadius:'50%',background:'#22c55e',border:'2px solid #fff'}}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',lineHeight:1.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{n.contactName}</div>
                  <div style={{fontSize:11,color:'#aaa',marginTop:2}}>{fmtTime(n.timestamp)}</div>
                </div>
                <button onClick={()=>dismissNotification(n.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#c0c0c0',padding:'2px 4px',fontSize:19,lineHeight:1,flexShrink:0,borderRadius:6,transition:'color 0.15s'}}
                  onMouseEnter={e=>e.target.style.color='#888'} onMouseLeave={e=>e.target.style.color='#c0c0c0'}>×</button>
              </div>
              {/* Message */}
              <div style={{fontSize:13,color:'#555',lineHeight:1.55,marginBottom:12,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>
                {n.message}
              </div>
              {/* Buttons */}
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>dismissNotification(n.id)} style={{flex:1,padding:'8px 0',borderRadius:10,border:'1.5px solid #e4e4e4',background:'#fff',color:'#555',fontSize:13,fontWeight:500,cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.target.style.background='#f5f5f5'} onMouseLeave={e=>e.target.style.background='#fff'}>
                  Dismiss
                </button>
                <button onClick={()=>replyNotification(n)} style={{flex:1,padding:'8px 0',borderRadius:10,border:'none',background:'#4f46e5',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.target.style.background='#4338ca'} onMouseLeave={e=>e.target.style.background='#4f46e5'}>
                  Reply
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal: Nueva conversación ── */}
      {showNewConv && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e => { if (e.target === e.currentTarget) setShowNewConv(false); }}>
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.45)',backdropFilter:'blur(4px)'}}/>
          <div style={{position:'relative',width:520,maxHeight:'90vh',background:'#fff',borderRadius:20,boxShadow:'0 24px 80px rgba(0,0,0,0.18)',display:'flex',flexDirection:'column',overflow:'hidden'}}>

            {/* Header */}
            <div style={{padding:'22px 24px 16px',borderBottom:'1px solid #f0f0ee',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div>
                <div style={{fontSize:17,fontWeight:700,color:'#0a0a0a',letterSpacing:'-0.3px'}}>Nueva conversación</div>
                <div style={{fontSize:12,color:'#aaa',marginTop:2}}>Inicia con una plantilla aprobada por WhatsApp</div>
              </div>
              <button onClick={() => setShowNewConv(false)} style={{width:32,height:32,borderRadius:8,border:'1px solid #e8e8e6',background:'#f4f4f2',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#888',fontSize:18,lineHeight:1}}>×</button>
            </div>

            <div style={{overflowY:'auto',flex:1}}>
              {/* Tabs */}
              <div style={{padding:'16px 24px 0'}}>
                <div style={{display:'flex',gap:4,background:'#f4f4f2',borderRadius:10,padding:3,marginBottom:16}}>
                  {[['nuevo','Número nuevo'],['contacto','Contacto existente']].map(([id,label]) => (
                    <button key={id} onClick={() => { setNewConvTab(id); setNewConvSelectedContact(null); setNewConvPhone(''); setNewConvName(''); setNewConvContactSearch(''); }}
                      style={{flex:1,padding:'7px 0',borderRadius:8,border:'none',background:newConvTab===id?'#fff':'transparent',color:newConvTab===id?'#0a0a0a':'#888',fontWeight:newConvTab===id?600:400,fontSize:13,cursor:'pointer',boxShadow:newConvTab===id?'0 1px 3px rgba(0,0,0,0.08)':'none',transition:'all 0.15s',fontFamily:'inherit'}}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Recipient section */}
                {newConvTab === 'nuevo' ? (
                  <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:16}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:5,textTransform:'uppercase',letterSpacing:'0.06em'}}>Número de WhatsApp</div>
                      <input value={newConvPhone} onChange={e => setNewConvPhone(e.target.value)} placeholder="+57 300 123 4567"
                        style={{width:'100%',padding:'10px 14px',borderRadius:10,border:'1.5px solid #e4e4e2',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',color:'#0a0a0a'}}
                        onFocus={e => e.target.style.borderColor='#0a0a0a'} onBlur={e => e.target.style.borderColor='#e4e4e2'}/>
                    </div>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:5,textTransform:'uppercase',letterSpacing:'0.06em'}}>Nombre (opcional)</div>
                      <input value={newConvName} onChange={e => setNewConvName(e.target.value)} placeholder="Nombre del cliente"
                        style={{width:'100%',padding:'10px 14px',borderRadius:10,border:'1.5px solid #e4e4e2',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',color:'#0a0a0a'}}
                        onFocus={e => e.target.style.borderColor='#0a0a0a'} onBlur={e => e.target.style.borderColor='#e4e4e2'}/>
                    </div>
                  </div>
                ) : (
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:5,textTransform:'uppercase',letterSpacing:'0.06em'}}>Buscar contacto</div>
                    <div style={{position:'relative'}}>
                      <svg style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}} width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="#bbb" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      <input value={newConvContactSearch} onChange={e => setNewConvContactSearch(e.target.value)} placeholder="Nombre o teléfono..."
                        style={{width:'100%',padding:'10px 14px 10px 34px',borderRadius:10,border:'1.5px solid #e4e4e2',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',color:'#0a0a0a'}}
                        onFocus={e => e.target.style.borderColor='#0a0a0a'} onBlur={e => e.target.style.borderColor='#e4e4e2'}/>
                    </div>
                    {/* Contact results from existing conversations */}
                    {newConvContactSearch.length >= 1 && (
                      <div style={{marginTop:6,maxHeight:140,overflowY:'auto',border:'1px solid #f0f0ee',borderRadius:10,background:'#fff'}}>
                        {conversations.filter(c => {
                          const q = newConvContactSearch.toLowerCase();
                          return (c.contact_name||'').toLowerCase().includes(q) || (c.contact_phone||'').includes(newConvContactSearch);
                        }).slice(0,6).map(c => (
                          <div key={c.id} onClick={() => { setNewConvSelectedContact({phone: c.contact_phone, name: c.contact_name}); setNewConvContactSearch(c.contact_name || c.contact_phone); }}
                            style={{padding:'10px 14px',cursor:'pointer',display:'flex',alignItems:'center',gap:10,borderBottom:'1px solid #f8f8f8',transition:'background 0.1s'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#f8f8f8'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                            <div style={{width:30,height:30,borderRadius:'50%',background:'#e8e8e6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'#555',flexShrink:0}}>{initials(c.contact_name)}</div>
                            <div>
                              <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>{c.contact_name || c.contact_phone}</div>
                              <div style={{fontSize:11,color:'#aaa'}}>{c.contact_phone}</div>
                            </div>
                          </div>
                        ))}
                        {conversations.filter(c => { const q=newConvContactSearch.toLowerCase(); return (c.contact_name||'').toLowerCase().includes(q)||(c.contact_phone||'').includes(newConvContactSearch); }).length === 0 && (
                          <div style={{padding:'12px 14px',fontSize:12,color:'#bbb',textAlign:'center'}}>Sin resultados</div>
                        )}
                      </div>
                    )}
                    {newConvSelectedContact && (
                      <div style={{marginTop:8,padding:'10px 14px',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,display:'flex',alignItems:'center',gap:10}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                        <div style={{fontSize:13,fontWeight:500,color:'#15803d'}}>{newConvSelectedContact.name || newConvSelectedContact.phone} — {newConvSelectedContact.phone}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div style={{height:1,background:'#f0f0ee',margin:'4px 0 16px'}}/>

                {/* Template selector */}
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em',display:'flex',alignItems:'center',gap:6}}>
                    Plantilla aprobada
                    <span style={{background:'#fffbeb',color:'#d97706',fontSize:9,padding:'2px 6px',borderRadius:999,fontWeight:700}}>REQUERIDA</span>
                  </div>

                  {newConvTemplates.length === 0 ? (
                    <div style={{padding:'12px 0',fontSize:12,color:'#bbb'}}>Cargando plantillas...</div>
                  ) : (
                    <>
                      <div style={{position:'relative',marginBottom:8}}>
                        <svg style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}} width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="#bbb" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        <input value={newConvTemplateSearch} onChange={e => setNewConvTemplateSearch(e.target.value)} placeholder="Filtrar plantillas..."
                          style={{width:'100%',padding:'8px 12px 8px 32px',borderRadius:10,border:'1.5px solid #e4e4e2',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}
                          onFocus={e=>e.target.style.borderColor='#0a0a0a'} onBlur={e=>e.target.style.borderColor='#e4e4e2'}/>
                      </div>
                      <div style={{maxHeight:180,overflowY:'auto',display:'flex',flexDirection:'column',gap:6}}>
                        {newConvTemplates
                          .filter(t => t.name.toLowerCase().includes(newConvTemplateSearch.toLowerCase()))
                          .map(tpl => {
                            const bodyComp = (tpl.components||[]).find(c=>c.type==='BODY');
                            const isSelected = newConvSelectedTemplate?.name === tpl.name;
                            return (
                              <div key={tpl.name} onClick={() => selectNewConvTemplate(tpl)}
                                style={{padding:'11px 14px',borderRadius:12,border:`1.5px solid ${isSelected?'#0a0a0a':'#e8e8e6'}`,background:isSelected?'#0a0a0a':'#fff',cursor:'pointer',transition:'all 0.15s'}}
                                onMouseEnter={e=>{ if(!isSelected) e.currentTarget.style.borderColor='#aaa'; }} onMouseLeave={e=>{ if(!isSelected) e.currentTarget.style.borderColor='#e8e8e6'; }}>
                                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                                  <span style={{fontSize:12,fontWeight:700,color:isSelected?'#fff':'#0a0a0a'}}>{tpl.name}</span>
                                  <span style={{fontSize:10,color:isSelected?'#6ee7b7':'#888',background:isSelected?'rgba(110,231,183,0.15)':'#f4f4f2',padding:'2px 7px',borderRadius:999}}>{tpl.language}</span>
                                </div>
                                <div style={{fontSize:11,color:isSelected?'#aaa':'#888',lineHeight:1.4,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>
                                  {bodyComp?.text || '(sin texto de cuerpo)'}
                                </div>
                              </div>
                            );
                          })
                        }
                      </div>
                    </>
                  )}
                </div>

                {/* Variables */}
                {newConvVariables.length > 0 && (
                  <div style={{marginTop:14}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Variables de la plantilla</div>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {newConvVariables.map((v, i) => (
                        <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                          <span style={{fontSize:12,color:'#888',minWidth:32,flexShrink:0}}>{`{{${i+1}}}`}</span>
                          <input value={v} onChange={e => setNewConvVariables(prev => { const n=[...prev]; n[i]=e.target.value; return n; })}
                            placeholder={`Valor para {{${i+1}}}`}
                            style={{flex:1,padding:'8px 12px',borderRadius:9,border:'1.5px solid #e4e4e2',fontSize:13,fontFamily:'inherit',outline:'none'}}
                            onFocus={e=>e.target.style.borderColor='#0a0a0a'} onBlur={e=>e.target.style.borderColor='#e4e4e2'}/>
                        </div>
                      ))}
                    </div>
                    {/* Preview */}
                    <div style={{marginTop:10,padding:'10px 14px',background:'#f8f8f8',borderRadius:10,fontSize:12,color:'#555',lineHeight:1.5}}>
                      <span style={{fontWeight:700,color:'#888',marginRight:6}}>Preview:</span>
                      {(() => {
                        const bodyComp = (newConvSelectedTemplate?.components||[]).find(c=>c.type==='BODY');
                        let text = bodyComp?.text || '';
                        newConvVariables.forEach((val, i) => { text = text.replace(`{{${i+1}}}`, val || `{{${i+1}}}`); });
                        return text;
                      })()}
                    </div>
                  </div>
                )}

                {/* 24h rule note */}
                <div style={{marginTop:14,padding:'10px 14px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:10,display:'flex',gap:10,alignItems:'flex-start'}}>
                  <svg style={{flexShrink:0,marginTop:1}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
                  <div style={{fontSize:11,color:'#92400e',lineHeight:1.5}}>
                    <strong>Regla de 24h de WhatsApp:</strong> Para iniciar una conversación siempre se requiere una plantilla aprobada. Una vez que el cliente responda, podrás enviar mensajes libres durante 24 horas.
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{padding:'16px 24px',borderTop:'1px solid #f0f0ee',flexShrink:0}}>
              {newConvError && <div style={{marginBottom:10,padding:'8px 12px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,fontSize:12,color:'#dc2626'}}>{newConvError}</div>}
              <div style={{display:'flex',gap:10}}>
                <button onClick={() => setShowNewConv(false)}
                  style={{flex:1,padding:'12px 0',borderRadius:12,border:'1.5px solid #e4e4e2',background:'#fff',color:'#555',fontSize:14,fontWeight:500,cursor:'pointer',fontFamily:'inherit',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.currentTarget.style.background='#f8f8f8'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  Cancelar
                </button>
                <button onClick={sendNewConversation} disabled={newConvSending}
                  style={{flex:2,padding:'12px 0',borderRadius:12,border:'none',background:newConvSending?'#555':'#0a0a0a',color:'#fff',fontSize:14,fontWeight:700,cursor:newConvSending?'not-allowed':'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:8,transition:'background 0.15s'}}
                  onMouseEnter={e=>{ if(!newConvSending) e.currentTarget.style.background='#222'; }} onMouseLeave={e=>{ if(!newConvSending) e.currentTarget.style.background='#0a0a0a'; }}>
                  {newConvSending ? (
                    <><div style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.3)',borderTop:'2px solid #fff',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/> Enviando...</>
                  ) : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Enviar plantilla</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Nexus — Generar Guía (3 columnas) ─────────────────────── */}
      {showNexusModal && (
        <div className="modal-overlay" onClick={e=>e.stopPropagation()} style={{background:'rgba(15,15,20,0.55)',backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}>
          <style>{`
            @keyframes nxSlideUp { from { opacity:0; transform:translateY(20px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
            @keyframes nxFadeCol { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
            @keyframes nxShine { from { left:-100%; } to { left:200%; } }
            .nx-modal { animation: nxSlideUp .4s cubic-bezier(.4,0,.2,1) both; }
            .nx-col-1 { animation: nxFadeCol .4s cubic-bezier(.4,0,.2,1) .08s both; }
            .nx-col-2 { animation: nxFadeCol .4s cubic-bezier(.4,0,.2,1) .14s both; }
            .nx-col-3 { animation: nxFadeCol .4s cubic-bezier(.4,0,.2,1) .2s both; }
            .nx-footer { animation: nxFadeCol .35s cubic-bezier(.4,0,.2,1) .26s both; }
            .nx-input { width:100%;padding:9px 11px;border:1px solid rgba(0,0,0,0.08);border-radius:9px;font-size:13px;font-family:inherit;outline:none;box-sizing:border-box;background:rgba(255,255,255,0.65);color:#0a0a0a;transition:border-color .18s,box-shadow .18s,background .18s; }
            .nx-input:focus { border-color:#f90370;background:#fff;box-shadow:0 0 0 3px rgba(249,3,112,0.1); }
            .nx-input::placeholder { color:#c4c4c8; }
            .nx-label { font-size:9.5px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.07em;display:block;margin-bottom:4px; }
            .nx-sec-title { font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:12px;display:flex;align-items:center;gap:7px; }
            .nx-sec-title svg { color:#f90370;opacity:.9; }
            .nx-tile { position:relative;border:1px solid rgba(0,0,0,0.07);border-radius:12px;padding:12px;background:rgba(252,252,253,0.7);cursor:pointer;transition:border-color .2s,background .2s,box-shadow .2s,transform .15s;display:flex;align-items:center;gap:10px; }
            .nx-tile:hover:not(.nx-tile-off) { transform:translateY(-1px);border-color:rgba(0,0,0,0.16); }
            .nx-tile-on { border-color:#f90370;background:rgba(249,3,112,0.05);box-shadow:0 0 0 3px rgba(249,3,112,0.09); }
            .nx-tile-off { opacity:.4;cursor:not-allowed; }
            .nx-tiles { display:grid;grid-template-columns:1fr 1fr;gap:9px; }
            .nx-grid { display:grid;grid-template-columns:3fr 4fr 3fr;gap:22px;align-items:start; }
            .nx-step { display:flex;align-items:center;gap:7px;font-size:11px;font-weight:600;color:#b0b0b6;letter-spacing:.01em; }
            .nx-step-dot { width:16px;height:16px;border-radius:50%;border:1.5px solid #d4d4d8;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#fff; }
            .nx-step-on { color:#f90370; }
            .nx-step-on .nx-step-dot, .nx-step-done .nx-step-dot { border-color:#f90370;background:#f90370; }
            .nx-step-line { width:28px;height:1.5px;background:#e4e4e8; }
            .nx-gen-btn { position:relative;overflow:hidden;flex:2.4;padding:13px 0;border-radius:12px;border:none;background:#f90370;color:#fff;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:background .2s,transform .12s,box-shadow .2s;box-shadow:0 6px 20px rgba(249,3,112,0.28); }
            .nx-gen-btn:hover { background:#e00265;transform:translateY(-1px);box-shadow:0 8px 26px rgba(249,3,112,0.34); }
            .nx-gen-btn:active { transform:translateY(0); }
            .nx-gen-btn::after { content:'';position:absolute;top:0;width:40%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);animation:nxShine 2.6s ease-in-out 1s 2; }
            .nx-gen-btn:disabled { background:#c9c9cd;box-shadow:none;cursor:not-allowed; }
            .nx-gen-btn:disabled::after { display:none; }
            .nx-ghost-btn { flex:1;padding:13px 0;border-radius:12px;border:1px solid rgba(0,0,0,0.1);background:#fff;color:#555;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;transition:background .15s; }
            .nx-ghost-btn:hover { background:#f5f5f3; }
            @media (max-width:900px) { .nx-grid { grid-template-columns:1fr; } .nx-col-3 { position:static !important; } }
          `}</style>
          <div className="nx-modal" style={{background:'#fbfbfc',borderRadius:22,padding:0,boxShadow:'0 30px 90px rgba(0,0,0,0.22),0 6px 24px rgba(0,0,0,0.08)',border:'1px solid rgba(255,255,255,0.6)',maxHeight:'92vh',display:'flex',flexDirection:'column',width:'min(1180px,96vw)'}} onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div style={{padding:'18px 26px 14px',borderBottom:'1px solid rgba(0,0,0,0.06)',flexShrink:0,background:'rgba(255,255,255,0.7)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:40,height:40,borderRadius:12,background:'linear-gradient(135deg,#ffe0ed,#fff0f5)',display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid #fecdd3'}}>
                    <img src="/nexus-logo.png" alt="Nexus" style={{width:22,height:22,objectFit:'contain'}} onError={e=>{e.target.style.display='none';e.target.parentElement.innerHTML='<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f90370" strokeWidth="2" stroke-linecap="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';}}/>
                  </div>
                  <div>
                    <div style={{fontSize:18,fontWeight:800,color:'#0a0a0a',letterSpacing:'-0.02em',lineHeight:1.1}}>Generar guía</div>
                    <div style={{fontSize:12,color:'#9ca3af',marginTop:2}}>
                      {nexusOrderInfo?.tracking_number ? `Guía actual: ${nexusOrderInfo.tracking_number}` : nexusOrderInfo?.has_nexus ? 'Nexus · Sin guía generada' : 'Pedido no vinculado a Nexus'}
                    </div>
                  </div>
                </div>
                <button onClick={()=>{ setShowNexusModal(false); setNexusMsg(''); }} style={{width:32,height:32,borderRadius:'50%',border:'1px solid rgba(0,0,0,0.08)',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#999',flexShrink:0,transition:'all .15s'}} onMouseEnter={e=>{e.currentTarget.style.background='#f5f5f3';e.currentTarget.style.color='#333';}} onMouseLeave={e=>{e.currentTarget.style.background='#fff';e.currentTarget.style.color='#999';}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              {/* Stepper decorativo (solo visual) */}
              <div style={{display:'flex',alignItems:'center',gap:9,marginTop:14,paddingLeft:52}}>
                <div className="nx-step nx-step-done">
                  <span className="nx-step-dot"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
                  Orden
                </div>
                <div className="nx-step-line"/>
                <div className="nx-step nx-step-on">
                  <span className="nx-step-dot"><span style={{width:5,height:5,borderRadius:'50%',background:'#fff'}}/></span>
                  Transportadora
                </div>
                <div className="nx-step-line"/>
                <div className="nx-step">
                  <span className="nx-step-dot" style={{background:'#fff'}}/>
                  Confirmación
                </div>
              </div>
            </div>

            {/* Contenido scrollable */}
            <div style={{overflow:'auto',flex:1}}>

            {nexusMsg && (
              <div style={{margin:'16px 26px 0',background:nexusMsg.includes('✅')?'#f0fdf4':nexusMsg.includes('⚠️')?'#fffbeb':'#fef2f2',border:`1px solid ${nexusMsg.includes('✅')?'#bbf7d0':nexusMsg.includes('⚠️')?'#fde68a':'#fee2e2'}`,borderRadius:12,padding:'11px 14px',fontSize:13,color:nexusMsg.includes('✅')?'#16a34a':nexusMsg.includes('⚠️')?'#92400e':'#ef4444',flexShrink:0,animation:'nxFadeCol .3s ease both'}}>{nexusMsg}</div>
            )}

            {nexusLoading && !nexusMsg && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'56px 0',flex:1,gap:12}}>
                <div className="spinner" style={{width:24,height:24,borderWidth:3}}/>
                <span style={{fontSize:13,color:'#aaa'}}>Cargando datos del pedido...</span>
              </div>
            )}

            {!nexusOrderInfo?.has_nexus && !nexusLoading && (
              <div style={{margin:'16px 26px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:12,padding:'14px 16px',fontSize:13,color:'#92400e',display:'flex',alignItems:'center',gap:10,animation:'nxFadeCol .3s ease both'}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Este pedido aún no está vinculado a Nexus. Espera a que llegue el webhook de <strong>nueva_orden</strong>.
              </div>
            )}

            {nexusOrderInfo?.has_nexus && !nexusLoading && (() => {
              const ord = shopifyOrders.find(o=>o.fulfillment_status!=='fulfilled') || shopifyOrders[0];
              const isPaid = ord?.financial_status === 'paid' || (ord?.metodo && ord.metodo !== 'COD' && ord.metodo !== 'cash_on_delivery' && ord.metodo !== '—');
              const imgSrc = nexusProductImg || null;
              const productName = nexusProductTitle || ord?.product || 'Producto';
              // Todos los productos de TODOS los pedidos a despachar (no cumplidos).
              const _guideOrders = (() => {
                const pend = shopifyOrders.filter(o => o.fulfillment_status !== 'fulfilled');
                return pend.length ? pend : (shopifyOrders.length ? [shopifyOrders[0]] : []);
              })();
              const _guideName = (s) => { const t = (s == null ? '' : String(s)); const tt = t.trim(); return (tt !== '' && tt !== '—' && tt !== '–') ? t : ''; };
              const guideItems = _guideOrders.flatMap(o =>
                (o.items_detalle && o.items_detalle.length > 0
                  ? o.items_detalle
                  : [{ producto: o.product, variante: o.variant, cantidad: 1 }]
                ).map(it => ({
                  producto: _guideName(it.producto) || _guideName(it.title) || _guideName(o.product) || 'Producto',
                  variante: it.variante || it.variant_title || o.variant || '',
                  cantidad: it.cantidad || it.quantity || 1,
                }))
              );
              const nf = (key) => nexusForm[key] || '';
              const setNf = (key,val) => setNexusForm(p=>({...p,[key]:val}));
              const CARRIERS = ['coordinadora','servientrega','interrapidisimo','tcc'].map(id=>({id,...CARRIERS_MAP[id]}));
              const sel = shippingQuotes.find(q => q.carrier === selectedCarrier);
              return (
                <>
                  {/* ORDER HERO */}
                  <div style={{margin:'18px 26px 6px',borderRadius:16,padding:'16px 20px',background:'rgba(255,255,255,0.6)',border:'1px solid rgba(0,0,0,0.05)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:16,flexWrap:'wrap'}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:800,color:'#f90370',letterSpacing:'0.06em'}}>PEDIDO #{ord?.order_number || nexusOrderInfo?.shopify_order_id || '—'}</div>
                        <div style={{fontSize:16,fontWeight:700,color:'#0a0a0a',marginTop:3,letterSpacing:'-0.01em'}}>{nf('customer_name') || '—'}</div>
                        <div style={{display:'flex',alignItems:'center',gap:5,marginTop:3,fontSize:12.5,color:'#6b7280'}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          {(nf('city') || cityQuery || '—')}{nf('state') ? `, ${nf('state')}` : ''}
                        </div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0}}>
                        <div style={{fontSize:30,fontWeight:800,color:'#0a0a0a',letterSpacing:'-0.03em',lineHeight:1}}>${Number(nexusForm.total_amount||ord?.total||0).toLocaleString('es-CO')}</div>
                        <div style={{fontSize:10,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.08em',marginTop:5}}>Total a recaudar</div>
                        <span style={{display:'inline-block',marginTop:7,fontSize:10.5,fontWeight:700,padding:'3px 10px',borderRadius:999,background:isPaid?'#f0fdf4':'#fffbeb',color:isPaid?'#16a34a':'#d97706',border:`1px solid ${isPaid?'#bbf7d0':'#fde68a'}`}}>{`${ord?.metodo && ord.metodo!=='—' ? ord.metodo+' · ' : ''}${isPaid?'Pagado':'Pendiente'}`}</span>
                      </div>
                    </div>
                    {nexusOrderInfo?.tracking_number && (
                      <div style={{marginTop:14,paddingTop:14,borderTop:'1px solid rgba(0,0,0,0.06)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                        <div style={{display:'flex',alignItems:'center',gap:16}}>
                          <div>
                            <div style={{fontSize:9.5,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2}}>Guía actual</div>
                            <a href={`https://www.coordinadora.com/portafolio-de-servicios/servicios-en-linea/rastrear-guias/?guia=${nexusOrderInfo.tracking_number}`} target="_blank" rel="noreferrer" style={{fontSize:15,fontWeight:800,color:'#f90370',textDecoration:'none',letterSpacing:0.5}}>{nexusOrderInfo.tracking_number}</a>
                          </div>
                          {(nexusOrderInfo?.carrier || nexusOrderInfo?.carrier_status) && (
                            <div style={{fontSize:11.5,color:'#6b7280'}}>
                              {CARRIERS_MAP[nexusOrderInfo?.carrier]?.label || nexusOrderInfo?.carrier || ''}{nexusOrderInfo?.carrier_status ? ` · ${nexusOrderInfo.carrier_status}` : ''}
                            </div>
                          )}
                        </div>
                        <button onClick={async()=>{
                          try {
                            const r = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/label`,{headers:authH()});
                            const d = await r.json();
                            if (d.pdf_b64) { const w=window.open(''); w.document.write(`<iframe src="data:application/pdf;base64,${d.pdf_b64}" style="width:100%;height:100vh;border:none;"></iframe>`); }
                            else alert('PDF no disponible aún');
                          } catch { alert('Error al obtener el PDF'); }
                        }} style={{padding:'8px 15px',borderRadius:10,border:'none',background:'#0a0a0a',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                          PDF
                        </button>
                      </div>
                    )}
                  </div>

                  {/* GRID 3 columnas 30 / 40 / 30 */}
                  <div className="nx-grid" style={{padding:'12px 26px 24px'}}>

                    {/* Col 1 — Cliente & envío */}
                    <div className="nx-col-1">
                      <div className="nx-sec-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                        Cliente &amp; envío
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:9}}>
                        <div><label className="nx-label">Nombre completo</label><input className="nx-input" value={nf('customer_name')} onChange={e=>setNf('customer_name',e.target.value)}/></div>
                        <div><label className="nx-label">Teléfono</label><input className="nx-input" value={nf('customer_phone')} onChange={e=>setNf('customer_phone',e.target.value)}/></div>
                        <div style={{position:'relative'}}>
                          <label className="nx-label">Ciudad</label>
                          <input className="nx-input"
                            value={cityQuery}
                            onChange={e => {
                              const v = e.target.value;
                              setCityQuery(v);
                              setNf('city',''); setNf('city_code',''); setNf('state','');
                              if (cityTimeoutRef.current) clearTimeout(cityTimeoutRef.current);
                              if (v.length >= 2) {
                                setCitySearching(true);
                                cityTimeoutRef.current = setTimeout(async () => {
                                  try {
                                    const r = await fetch(`${API}/nexus/cities?q=${encodeURIComponent(v)}`, { headers: authH() });
                                    if (r.ok) { const d = await r.json(); setCitySuggestions(Array.isArray(d) ? d : []); }
                                  } catch(_) {}
                                  setCitySearching(false);
                                }, 350);
                              } else { setCitySuggestions([]); setCitySearching(false); }
                            }}
                            placeholder="Escribe para buscar ciudad..."
                            style={{borderColor: nexusForm.city_code ? '#6ee7b7' : undefined, background: nexusForm.city_code ? '#f0fdf4' : undefined}}
                            onFocus={e=>{ if (!nexusForm.city_code) e.target.style.borderColor='#6ee7b7'; }}
                            onBlur={e=>{ setTimeout(()=>setCitySuggestions([]),200); if (!nexusForm.city_code) e.target.style.borderColor=''; }}
                          />
                          {nexusForm.city_code && <div style={{fontSize:10,color:'#16a34a',marginTop:3,fontWeight:600}}>DANE: {nexusForm.city_code}</div>}
                          {citySearching && <div style={{fontSize:11,color:'#999',marginTop:3}}>Buscando...</div>}
                          {citySuggestions.length > 0 && (
                            <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1.5px solid #e8e8e6',borderRadius:10,marginTop:4,maxHeight:180,overflowY:'auto',zIndex:100,boxShadow:'0 8px 24px rgba(0,0,0,0.1)'}}>
                              {citySuggestions.map((c, i) => (
                                <div key={i}
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    const nombre = c.nombre || c.city || '';
                                    const depto = c.departamento || c.department || '';
                                    const code = c.codigo_dane || c.city_code || '';
                                    setNf('city', nombre); setNf('state', depto); setNf('city_code', code);
                                    setCityQuery(`${nombre}, ${depto}`);
                                    setCitySuggestions([]);
                                    if (code) {
                                      const total = parseInt(nexusForm.total_amount) || 0;
                                      if (total > 0) {
                                        setQuotesLoading(true); setShippingQuotes([]);
                                        fetch(`${API}/nexus/quote-by-city`, { method:'POST', headers:authH({'Content-Type':'application/json'}), body:JSON.stringify({city_code:code,total}) })
                                          .then(r=>r.ok?r.json():null).then(d=>{ if(d?.quotes) setShippingQuotes(d.quotes); }).catch(()=>{}).finally(()=>setQuotesLoading(false));
                                      }
                                    }
                                  }}
                                  style={{padding:'9px 14px',fontSize:12,cursor:'pointer',borderBottom:i<citySuggestions.length-1?'1px solid #f0f0ee':'none',transition:'background 0.15s'}}
                                  onMouseEnter={e=>e.target.style.background='#f8f8f6'}
                                  onMouseLeave={e=>e.target.style.background='#fff'}
                                >
                                  <span style={{fontWeight:600,color:'#0a0a0a'}}>{c.nombre || c.city}</span>
                                  <span style={{color:'#999',marginLeft:6,fontSize:11}}>{c.departamento || c.department}</span>
                                  <span style={{color:'#bbb',marginLeft:4,fontSize:10}}>({c.codigo_dane || c.city_code})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="nx-label">Departamento</label>
                          <input className="nx-input" value={nf('state')} readOnly placeholder="Se llena al seleccionar ciudad" style={{background:'rgba(245,245,245,0.6)',color:'#666'}}/>
                        </div>
                        <div><label className="nx-label">Dirección y complementos</label><input className="nx-input" value={nf('shipping_address')} onChange={e=>setNf('shipping_address',e.target.value)}/></div>
                        <div><label className="nx-label">Correo electrónico</label><input className="nx-input" value={nf('customer_email')} onChange={e=>setNf('customer_email',e.target.value)} placeholder="opcional@email.com"/></div>
                      </div>
                    </div>

                    {/* Col 2 — Foco: Productos, Variante, Transportadoras, Cotización */}
                    <div className="nx-col-2">
                      <div className="nx-sec-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                        Productos
                        <span style={{color:'#c4c4c8',fontWeight:600,marginLeft:2,textTransform:'none',letterSpacing:0}}>· {guideItems.length} {guideItems.length===1?'producto':'productos'} en esta orden</span>
                      </div>
                      <div style={{border:'1px solid rgba(0,0,0,0.05)',borderRadius:12,background:'rgba(250,250,252,0.6)',overflow:'hidden'}}>
                        {guideItems.map((it, idx) => (
                          <div key={idx} style={{display:'flex',gap:11,alignItems:'center',padding:'10px 12px',borderTop:idx>0?'1px solid rgba(0,0,0,0.05)':'none'}}>
                            {idx===0 && imgSrc
                              ? <img src={imgSrc} alt="" style={{width:46,height:46,borderRadius:10,objectFit:'cover',flexShrink:0,border:'1px solid rgba(0,0,0,0.06)'}} onError={e=>e.target.style.display='none'}/>
                              : <div style={{width:46,height:46,borderRadius:10,background:'#eeeeef',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>
                            }
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',lineHeight:1.35}}>{it.producto}</div>
                              {it.variante && it.variante !== '—' && <div style={{fontSize:11.5,color:'#9ca3af',marginTop:2}}>{it.variante}</div>}
                            </div>
                            <div style={{fontSize:11.5,fontWeight:600,color:'#6b7280',flexShrink:0}}>Cant. {it.cantidad}</div>
                          </div>
                        ))}
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 12px',borderTop:'1px solid rgba(0,0,0,0.08)',background:'rgba(255,255,255,0.5)'}}>
                          <span style={{fontSize:10,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.06em'}}>Total de productos</span>
                          <span style={{fontSize:15,fontWeight:800,color:'#0a0a0a',letterSpacing:'-0.01em'}}>${Number(nexusForm.total_amount||ord?.total||0).toLocaleString('es-CO')}</span>
                        </div>
                      </div>

                      {/* Variante */}
                      <div style={{marginTop:18}}>
                        <div className="nx-sec-title" style={{marginBottom:8}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                          Variante del producto
                        </div>
                        <input className="nx-input" value={nf('content_description')} onChange={e=>setNf('content_description',e.target.value)} placeholder="Ej: MARRON / 43" style={{fontWeight:600}}/>
                      </div>

                      {/* Transportadoras */}
                      <div style={{marginTop:22}}>
                        <div style={{fontSize:15,fontWeight:800,color:'#0a0a0a',letterSpacing:'-0.01em'}}>Elige tu transportadora</div>
                        <div style={{fontSize:12,color:'#9ca3af',marginTop:2,marginBottom:12}}>Selecciona la mejor opción para este pedido</div>
                        <div className="nx-tiles">
                          {CARRIERS.map(c => {
                            const active = selectedCarrier === c.id;
                            const hasQuote = shippingQuotes.some(q => q.carrier === c.id);
                            return (
                              <div key={c.id} onClick={() => hasQuote && setSelectedCarrier(c.id)}
                                className={`nx-tile ${active?'nx-tile-on':''} ${hasQuote?'':'nx-tile-off'}`}>
                                <div style={{width:38,height:38,borderRadius:9,background:'#fff',border:'1px solid rgba(0,0,0,0.06)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
                                  {c.img ? <img src={c.img} alt={c.label} style={{width:30,height:30,objectFit:'contain'}}/> : <span style={{fontSize:10,fontWeight:900,color:c.color}}>{c.label?.slice(0,3)}</span>}
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:12.5,fontWeight:700,color:'#0a0a0a',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.label}</div>
                                  <div style={{fontSize:10,fontWeight:600,color:active?'#f90370':(hasQuote?'#9ca3af':'#c4c4c8'),marginTop:1}}>{active?'Seleccionada':(hasQuote?'Disponible':'Sin cotización')}</div>
                                </div>
                                {active && <span style={{width:18,height:18,borderRadius:'50%',background:'#f90370',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>}
                              </div>
                            );
                          })}
                        </div>
                        {quotesLoading && (
                          <div style={{display:'flex',alignItems:'center',gap:8,padding:'12px 2px 2px',fontSize:12,color:'#9ca3af'}}>
                            <span style={{width:14,height:14,border:'2px solid #e8e8e6',borderTop:'2px solid #f90370',borderRadius:'50%',animation:'spin .8s linear infinite'}}/> Cotizando transportadoras...
                          </div>
                        )}

                        {/* Cotización seleccionada */}
                        {sel ? (
                          <div style={{marginTop:12,borderRadius:14,border:'1px solid rgba(249,3,112,0.18)',background:'linear-gradient(180deg,rgba(249,3,112,0.045),rgba(255,255,255,0))',padding:'14px 16px'}}>
                            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:13}}>
                              <div style={{display:'flex',alignItems:'center',gap:9}}>
                                <div style={{width:30,height:30,borderRadius:8,background:'#fff',border:'1px solid rgba(0,0,0,0.06)',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
                                  {CARRIERS_MAP[selectedCarrier]?.img ? <img src={CARRIERS_MAP[selectedCarrier].img} alt="" style={{width:24,height:24,objectFit:'contain'}}/> : null}
                                </div>
                                <span style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>{CARRIERS_MAP[selectedCarrier]?.label}</span>
                              </div>
                              <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10,fontWeight:700,color:'#f90370',textTransform:'uppercase',letterSpacing:'0.06em'}}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f90370" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                Seleccionada
                              </span>
                            </div>
                            <div style={{display:'grid',gridTemplateColumns:'1.3fr 1fr 1.3fr',gap:10,alignItems:'flex-end'}}>
                              <div>
                                <div style={{fontSize:9.5,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Flete</div>
                                <div style={{fontSize:22,fontWeight:800,color:'#0a0a0a',letterSpacing:'-0.02em',lineHeight:1}}>${Number(sel.total||0).toLocaleString('es-CO')}</div>
                              </div>
                              <div>
                                <div style={{fontSize:9.5,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Tiempo</div>
                                <div style={{fontSize:14,fontWeight:700,color:'#0a0a0a'}}>{sel.delivery_time} día{sel.delivery_time!=='1'?'s':''}</div>
                              </div>
                              <div style={{textAlign:'right'}}>
                                <div style={{fontSize:9.5,fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Se deposita</div>
                                <div style={{fontSize:15,fontWeight:800,color:'#16a34a'}}>${Number(sel.value_deposited||0).toLocaleString('es-CO')}</div>
                              </div>
                            </div>
                          </div>
                        ) : (!quotesLoading && shippingQuotes.length === 0 && (
                          <div style={{marginTop:12,fontSize:12,color:'#b0b0b6',fontStyle:'italic',padding:'2px'}}>Cotización no disponible</div>
                        ))}
                      </div>
                    </div>

                    {/* Col 3 — Resumen financiero (sticky) */}
                    <div className="nx-col-3" style={{position:'sticky',top:0}}>
                      <div className="nx-sec-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        Resumen de la orden
                      </div>
                      <div style={{borderRadius:16,padding:'16px 18px',background:'linear-gradient(180deg,rgba(249,3,112,0.06),rgba(250,250,252,0.35))',border:'1px solid rgba(249,3,112,0.14)',marginBottom:12}}>
                        <label className="nx-label" style={{letterSpacing:'0.08em'}}>Total a recaudar (COD)</label>
                        <input className="nx-input" value={nf('total_amount')} onChange={e=>setNf('total_amount',e.target.value.replace(/\D/g,''))}
                          style={{fontSize:26,fontWeight:800,letterSpacing:'-0.02em',padding:'8px 11px',border:'1px solid rgba(249,3,112,0.2)',background:'rgba(255,255,255,0.75)',color:'#0a0a0a'}}/>
                        <div style={{marginTop:11,display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontSize:11,color:'#9ca3af',fontWeight:600}}>Estado de pago</span>
                          <span style={{fontSize:11,fontWeight:700,padding:'3px 11px',borderRadius:999,background:isPaid?'#f0fdf4':'#fffbeb',color:isPaid?'#16a34a':'#d97706',border:`1px solid ${isPaid?'#bbf7d0':'#fde68a'}`}}>{isPaid?'Pagado':'Pendiente'}</span>
                        </div>
                      </div>
                      <div style={{display:'flex',flexDirection:'column'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 4px',borderBottom:'1px solid rgba(0,0,0,0.05)'}}>
                          <span style={{fontSize:12.5,color:'#6b7280'}}>Costo de envío</span>
                          <span style={{fontSize:13,fontWeight:700,color:'#0a0a0a'}}>{sel?`$${Number(sel.total||0).toLocaleString('es-CO')}`:'—'}</span>
                        </div>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 4px'}}>
                          <span style={{fontSize:12.5,color:'#6b7280'}}>Se deposita</span>
                          <span style={{fontSize:14,fontWeight:800,color:'#16a34a'}}>{sel?`$${Number(sel.value_deposited||0).toLocaleString('es-CO')}`:'—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

            </div>{/* end contenido scrollable */}

            {/* Footer buttons */}
            {nexusOrderInfo?.has_nexus && !nexusLoading && (
              <div className="nx-footer" style={{display:'flex',gap:12,padding:'14px 26px 18px',flexShrink:0,borderTop:'1px solid rgba(0,0,0,0.06)',background:'rgba(255,255,255,0.7)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)'}}>
                <button onClick={()=>{ setShowNexusModal(false); setNexusMsg(''); }} className="nx-ghost-btn">
                  Cancelar
                </button>
                <button disabled={nexusLoading} onClick={nexusGenerateGuide} className="nx-gen-btn">
                  {nexusLoading
                    ? <><div style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.3)',borderTop:'2px solid #fff',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/> Generando...</>
                    : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> {nexusOrderInfo?.tracking_number ? 'Sincronizar guía' : 'Generar guía'}</>
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
